var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);

// CODEX CUSTOMIZATION START
// Early motion bootstrap so paused-state content can respect Consentmo before
// motion-heavy components initialize.
(() => {
  const MOTION_STORAGE_KEY = 'codexMotionPaused';
  const root = document.documentElement;
  const originalMatchMedia = window.matchMedia ? window.matchMedia.bind(window) : null;

  const readStoredMotionPaused = () => {
    try {
      return window.localStorage.getItem(MOTION_STORAGE_KEY) === 'true';
    } catch (error) {
      return false;
    }
  };

  const createReducedMotionMatchMedia = (query) => {
    const reduceMatches = query.includes('prefers-reduced-motion: reduce');

    return {
      matches: reduceMatches,
      media: query,
      onchange: null,
      addEventListener() {},
      removeEventListener() {},
      addListener() {},
      removeListener() {},
      dispatchEvent() {
        return false;
      },
    };
  };

  const applyMotionPaused = (paused) => {
    const pausedState = Boolean(paused);

    window.__OOLMotionPaused = pausedState;
    root.toggleAttribute('data-codex-motion-paused', pausedState);
    root.classList.toggle('codex-motion-paused', pausedState);
  };

  window.__OOLSetMotionPaused = (paused) => {
    try {
      window.localStorage.setItem(MOTION_STORAGE_KEY, paused ? 'true' : 'false');
    } catch (error) {
      /* Ignore storage failures. */
    }

    applyMotionPaused(paused);
  };

  applyMotionPaused(readStoredMotionPaused());

  if (originalMatchMedia) {
    window.matchMedia = (query) => {
      const mediaQuery = String(query || '');
      const originalQuery = originalMatchMedia(mediaQuery);

      if (!window.__OOLMotionPaused || !mediaQuery.includes('prefers-reduced-motion')) {
        return originalQuery;
      }

      if (mediaQuery.includes('prefers-reduced-motion: reduce') || mediaQuery.includes('prefers-reduced-motion: no-preference')) {
        return createReducedMotionMatchMedia(mediaQuery);
      }

      return originalQuery;
    };
  }
})();
// CODEX CUSTOMIZATION END

// js/common/utilities/country-selector.js
var CountrySelector = class extends HTMLElement {
  constructor() {
    super();
    this._onCountryChangedListener = this._onCountryChanged.bind(this);
  }
  connectedCallback() {
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement.addEventListener("change", this._onCountryChangedListener);
    if (this.hasAttribute("country") && this.getAttribute("country") !== "") {
      this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute("country")));
      this.countryElement.dispatchEvent(new Event("change"));
    } else {
      this._onCountryChanged();
    }
  }
  disconnectedCallback() {
    this.countryElement.removeEventListener("change", this._onCountryChangedListener);
  }
  _onCountryChanged() {
    const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute("data-provinces"));
    this.provinceElement.parentElement.hidden = provinces.length === 0;
    if (provinces.length === 0) {
      return;
    }
    this.provinceElement.innerHTML = "";
    provinces.forEach((data) => {
      const selected = data[1] === this.getAttribute("province");
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
};
if (!window.customElements.get("country-selector")) {
  window.customElements.define("country-selector", CountrySelector);
}

// js/common/utilities/cached-fetch.js
var cachedMap = /* @__PURE__ */ new Map();
function cachedFetch(url, options) {
  const cacheKey = url;
  if (cachedMap.has(cacheKey)) {
    return Promise.resolve(new Response(new Blob([cachedMap.get(cacheKey)])));
  }
  return fetch(url, options).then((response) => {
    if (response.status === 200) {
      const contentType = response.headers.get("Content-Type");
      if (contentType && (contentType.match(/application\/json/i) || contentType.match(/text\//i))) {
        response.clone().text().then((content) => {
          cachedMap.set(cacheKey, content);
        });
      }
    }
    return response;
  });
}

// js/common/utilities/extract-section-id.js
function extractSectionId(element) {
  element = element.classList.contains("shopify-section") ? element : element.closest(".shopify-section");
  return element.id.replace("shopify-section-", "");
}

// js/common/utilities/dom.js
function deepQuerySelector(root, selector) {
  let element = root.querySelector(selector);
  if (element) {
    return element;
  }
  for (const template2 of root.querySelectorAll("template")) {
    element = deepQuerySelector(template2.content, selector);
    if (element) {
      return element;
    }
  }
  return null;
}
function throttle(callback) {
  let requestId = null, lastArgs;
  const later = (context) => () => {
    requestId = null;
    callback.apply(context, lastArgs);
  };
  const throttled = (...args) => {
    lastArgs = args;
    if (requestId === null) {
      requestId = requestAnimationFrame(later(this));
    }
  };
  throttled.cancel = () => {
    cancelAnimationFrame(requestId);
    requestId = null;
  };
  return throttled;
}
function debounce(fn, delay) {
  let timer = null;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}
function waitForEvent(element, eventName) {
  return new Promise((resolve) => {
    const done = (event) => {
      if (event.target === element) {
        element.removeEventListener(eventName, done);
        resolve(event);
      }
    };
    element.addEventListener(eventName, done);
  });
}
function isCartAddRequest(resource, options = {}) {
  const method = (options?.method || resource?.method || "GET").toString().toUpperCase();
  if (method !== "POST") {
    return false;
  }
  const requestUrl = typeof resource === "string" ? resource : resource?.url;
  if (!requestUrl) {
    return false;
  }
  try {
    return /\/cart\/add(\.js)?\/?$/.test(new URL(requestUrl, window.location.origin).pathname);
  } catch (error) {
    return /\/cart\/add(\.js)?\/?(?:[?#]|$)/.test(requestUrl);
  }
}
function dispatchExternalCartAddRefresh(cartAddResponse = null) {
  if (window.themeVariables?.settings?.cartType !== "drawer" || window.themeVariables?.settings?.pageType === "cart") {
    return;
  }
  clearTimeout(window.themeExternalCartAddRefreshTimer);
  window.themeExternalCartAddRefreshTimer = setTimeout(() => {
    document.dispatchEvent(new CustomEvent("cart:refresh", {
      detail: {
        openCartDrawerOnAdd: true,
        cartAddResponse
      }
    }));
  }, 0);
}
function installExternalCartAddMonitor() {
  if (window.themeExternalCartAddMonitorInstalled) {
    return;
  }
  window.themeExternalCartAddMonitorInstalled = true;
  const originalFetch = window.fetch;
  if (typeof originalFetch === "function") {
    window.fetch = async function(resource, options = {}) {
      const isThemeProductFormRequest = options?._themeProductFormRequest === true;
      const shouldRefreshAfterAdd = !isThemeProductFormRequest && isCartAddRequest(resource, options);
      const response = await originalFetch.apply(this, arguments);
      if (shouldRefreshAfterAdd && response.ok) {
        response.clone().json().then(dispatchExternalCartAddRefresh).catch(() => dispatchExternalCartAddRefresh());
      }
      return response;
    };
  }
  const originalXhrOpen = window.XMLHttpRequest?.prototype?.open;
  const originalXhrSend = window.XMLHttpRequest?.prototype?.send;
  if (originalXhrOpen && originalXhrSend) {
    window.XMLHttpRequest.prototype.open = function(method, url) {
      this.themeExternalCartAddRequest = isCartAddRequest(url, { method });
      return originalXhrOpen.apply(this, arguments);
    };
    window.XMLHttpRequest.prototype.send = function() {
      if (this.themeExternalCartAddRequest) {
        this.addEventListener("load", function() {
          if (this.status < 200 || this.status >= 300) {
            return;
          }
          try {
            dispatchExternalCartAddRefresh(JSON.parse(this.responseText));
          } catch (error) {
            dispatchExternalCartAddRefresh();
          }
        });
      }
      return originalXhrSend.apply(this, arguments);
    };
  }
}
installExternalCartAddMonitor();

// js/common/utilities/player.js
var _callback, _duration, _remainingTime, _startTime, _timer, _state, _onVisibilityChangeListener, _Player_instances, onVisibilityChange_fn;
var Player = class extends EventTarget {
  constructor(durationInSec, stopOnVisibility = true) {
    super();
    __privateAdd(this, _Player_instances);
    __privateAdd(this, _callback);
    __privateAdd(this, _duration);
    __privateAdd(this, _remainingTime);
    __privateAdd(this, _startTime);
    __privateAdd(this, _timer);
    __privateAdd(this, _state, "paused");
    __privateAdd(this, _onVisibilityChangeListener, __privateMethod(this, _Player_instances, onVisibilityChange_fn).bind(this));
    __privateSet(this, _callback, () => this.dispatchEvent(new CustomEvent("player:end")));
    this.setDuration(durationInSec);
    if (stopOnVisibility) {
      document.addEventListener("visibilitychange", __privateGet(this, _onVisibilityChangeListener));
    }
  }
  getDuration() {
    return __privateGet(this, _duration) / 1e3;
  }
  setDuration(durationInSec) {
    __privateSet(this, _duration, __privateSet(this, _remainingTime, durationInSec * 1e3));
  }
  pause() {
    if (__privateGet(this, _state) !== "started") {
      return;
    }
    clearTimeout(__privateGet(this, _timer));
    __privateSet(this, _state, "paused");
    __privateSet(this, _remainingTime, __privateGet(this, _remainingTime) - ((/* @__PURE__ */ new Date()).getTime() - __privateGet(this, _startTime)));
    this.dispatchEvent(new CustomEvent("player:pause", { detail: { duration: __privateGet(this, _duration) / 1e3, remainingTime: __privateGet(this, _remainingTime) / 1e3 } }));
  }
  resume(restartTimer = false) {
    if (__privateGet(this, _state) !== "stopped") {
      if (restartTimer) {
        this.start();
      } else {
        clearTimeout(__privateGet(this, _timer));
        __privateSet(this, _startTime, (/* @__PURE__ */ new Date()).getTime());
        __privateSet(this, _state, "started");
        __privateSet(this, _timer, setTimeout(__privateGet(this, _callback), __privateGet(this, _remainingTime)));
        this.dispatchEvent(new CustomEvent("player:resume", { detail: { duration: __privateGet(this, _duration) / 1e3, remainingTime: __privateGet(this, _remainingTime) / 1e3 } }));
      }
    }
  }
  start() {
    clearTimeout(__privateGet(this, _timer));
    __privateSet(this, _startTime, (/* @__PURE__ */ new Date()).getTime());
    __privateSet(this, _state, "started");
    __privateSet(this, _remainingTime, __privateGet(this, _duration));
    __privateSet(this, _timer, setTimeout(__privateGet(this, _callback), __privateGet(this, _remainingTime)));
    this.dispatchEvent(new CustomEvent("player:start", { detail: { duration: __privateGet(this, _duration) / 1e3, remainingTime: __privateGet(this, _remainingTime) / 1e3 } }));
  }
  stop() {
    clearTimeout(__privateGet(this, _timer));
    __privateSet(this, _state, "stopped");
    this.dispatchEvent(new CustomEvent("player:stop"));
  }
};
_callback = new WeakMap();
_duration = new WeakMap();
_remainingTime = new WeakMap();
_startTime = new WeakMap();
_timer = new WeakMap();
_state = new WeakMap();
_onVisibilityChangeListener = new WeakMap();
_Player_instances = new WeakSet();
onVisibilityChange_fn = function() {
  if (document.visibilityState === "hidden") {
    this.pause();
  } else if (document.visibilityState === "visible") {
    this.resume();
  }
};

// js/common/utilities/accessibility.js
function announceText(holderId, message) {
  const holder = document.getElementById(holderId);
  if (!(holder instanceof HTMLElement)) {
    return;
  }
  holder.textContent = "";
  setTimeout(() => {
    if (holder.isConnected) {
      holder.textContent = message;
    }
  }, 100);
}
function announceStatus(message) {
  announceText("status-announcement", message);
}
const FACET_FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
const FACET_REMOVAL_SELECTOR = 'a[is="facet-link"][data-codex-facet-removal], a[is="facet-link"][data-codex-facet-clear-all]';
function isFacetFocusable(element) {
  return element instanceof HTMLElement && element.getClientRects().length > 0 && !element.closest("[hidden], [aria-hidden=\"true\"], [inert]") && !element.hasAttribute("disabled") && element.getAttribute("tabindex") !== "-1";
}
function getFacetFocusableElements(root) {
  return Array.from(root?.querySelectorAll(FACET_FOCUSABLE_SELECTOR) || []).filter(isFacetFocusable);
}
function getFacetFocusDescriptor(element) {
  if (!(element instanceof HTMLElement)) {
    return null;
  }
  return {
    id: element.id,
    name: element.getAttribute("name") || "",
    value: element.getAttribute("value") || "",
    controls: element.getAttribute("aria-controls") || "",
    href: element instanceof HTMLAnchorElement ? element.getAttribute("href") || "" : "",
    label: element.getAttribute("aria-label") || "",
    facetKey: element.getAttribute("data-codex-facet-key") || "",
    clearAll: element.hasAttribute("data-codex-facet-clear-all"),
    text: element.textContent?.replace(/\s+/g, " ").trim() || ""
  };
}
function getNextFacetFocusableElement(element) {
  const root = element?.closest(".shopify-section") || document;
  const focusableElements = getFacetFocusableElements(root);
  const currentIndex = focusableElements.indexOf(element);
  return currentIndex >= 0 ? focusableElements[currentIndex + 1] || null : null;
}
function getNextFacetRemovalElement(element) {
  const activeFacets = element?.closest(".active-facets");
  if (!(activeFacets instanceof HTMLElement) || element?.hasAttribute("data-codex-facet-clear-all")) {
    return null;
  }
  const removalElements = Array.from(activeFacets.querySelectorAll('a[is="facet-link"][data-codex-facet-removal]')).filter(isFacetFocusable);
  const currentIndex = removalElements.indexOf(element);
  return currentIndex >= 0 ? removalElements[currentIndex + 1] || null : null;
}
function getFacetSortByFocusTarget(root) {
  return Array.from(root?.querySelectorAll("facet-sort-by button[aria-controls]") || []).find(isFacetFocusable) || null;
}
function facetFocusDescriptorMatches(element, descriptor) {
  if (!descriptor || !isFacetFocusable(element)) {
    return false;
  }
  if (descriptor.facetKey) {
    return element.getAttribute("data-codex-facet-key") === descriptor.facetKey;
  }
  if (descriptor.clearAll) {
    return element.hasAttribute("data-codex-facet-clear-all");
  }
  if (descriptor.id && element.id === descriptor.id) {
    return true;
  }
  if (descriptor.name && element.getAttribute("name") === descriptor.name && element.getAttribute("value") === descriptor.value) {
    return true;
  }
  if (descriptor.controls && element.getAttribute("aria-controls") === descriptor.controls) {
    return true;
  }
  if (descriptor.href && element instanceof HTMLAnchorElement && element.getAttribute("href") === descriptor.href && (!descriptor.label || element.getAttribute("aria-label") === descriptor.label)) {
    return true;
  }
  return Boolean(descriptor.text && element.textContent?.replace(/\s+/g, " ").trim() === descriptor.text);
}
function findFacetFocusTarget(root, descriptor) {
  return getFacetFocusableElements(root).find((element) => facetFocusDescriptorMatches(element, descriptor)) || null;
}
function getFacetFocusContext(element) {
  const activeFacets = element?.closest(".active-facets");
  if (!(activeFacets instanceof HTMLElement)) {
    return null;
  }
  const topBar = activeFacets.closest(".collection__top-bar");
  if (topBar) {
    return { scopeSelector: ".collection__top-bar", mode: "after", index: Array.from(activeFacets.querySelectorAll(FACET_REMOVAL_SELECTOR)).indexOf(element) };
  }
  const sidebarActiveFacets = activeFacets.closest(".collection__active-facets");
  if (sidebarActiveFacets) {
    return { scopeSelector: ".collection__active-facets", fallbackSelector: ".collection__facets", mode: "after", index: Array.from(activeFacets.querySelectorAll(FACET_REMOVAL_SELECTOR)).indexOf(element) };
  }
  const results = activeFacets.closest(".collection__results");
  if (results) {
    return { scopeSelector: ".collection__results", mode: "inside", index: Array.from(activeFacets.querySelectorAll(FACET_REMOVAL_SELECTOR)).indexOf(element) };
  }
  return null;
}
function getFacetFallbackFocusTarget(root, context) {
  if (!context) {
    return null;
  }
  const scope = root.querySelector(context.scopeSelector);
  const fallbackScope = root.querySelector(context.fallbackSelector || context.scopeSelector);
  if (!(scope instanceof HTMLElement) && !(fallbackScope instanceof HTMLElement)) {
    return null;
  }
  const activeFacets = scope instanceof HTMLElement ? Array.from(scope.querySelectorAll(".active-facets")).find((element) => isFacetFocusable(element) || element.getClientRects().length > 0) : null;
  if (activeFacets && context.index >= 0) {
    const replacementLinks = Array.from(activeFacets.querySelectorAll(FACET_REMOVAL_SELECTOR)).filter(isFacetFocusable);
    if (replacementLinks[context.index]) {
      return replacementLinks[context.index];
    }
  }
  const focusableElements = getFacetFocusableElements(root);
  if (context.mode === "inside") {
    return focusableElements.find((element) => scope?.contains(element)) || null;
  }
  return focusableElements.find((element) => Boolean(fallbackScope.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING)) || null;
}
function focusFacetRemovalTarget(root, descriptor, context, openFacetDrawer, focusGuard) {
  if (focusGuard && !focusGuard.canRestore()) {
    focusGuard.cancel();
    return false;
  }
  let target = findFacetFocusTarget(root, descriptor);
  if (!target) {
    target = getFacetFallbackFocusTarget(root, context);
  }
  if (!(target instanceof HTMLElement) || openFacetDrawer?.open && !openFacetDrawer.contains(target)) {
    focusGuard?.cancel();
    return false;
  }
  target.focus({ preventScroll: true });
  focusGuard?.cancel();
  return document.activeElement === target;
}
function getFacetRemovalAnnouncement(element) {
  if (!(element instanceof HTMLElement)) {
    return "";
  }
  if (element.hasAttribute("data-codex-facet-clear-all")) {
    return "Removed.";
  }
  const label = element.getAttribute("data-codex-facet-label")?.trim();
  return label ? `Filter ${label} was removed.` : "Removed.";
}
// A DOM replacement may restore lost focus, but must yield to a newer user,
// screen-reader or dialog focus owner. Listeners exist only during the handoff.
function createFocusRestorationGuard() {
  const originalFocus = document.activeElement;
  const controller = new AbortController();
  let cancelled = false;
  const cancel = () => {
    cancelled = true;
    controller.abort();
  };
  document.addEventListener("focusin", (event) => {
    if (event.target !== originalFocus) {
      cancel();
    }
  }, { capture: true, signal: controller.signal });
  document.addEventListener("pointerdown", cancel, { capture: true, signal: controller.signal });
  document.addEventListener("keydown", cancel, { capture: true, signal: controller.signal });
  return {
    cancel,
    canRestore: () => !cancelled && (!originalFocus?.isConnected || originalFocus === document.body) && document.activeElement === document.body
  };
}
function announceCartDrawer(message) {
  const holder = document.getElementById("cart-drawer-announcement");
  if (!holder) {
    return;
  }
  holder.textContent = "";
  holder.textContent = message;
}
function announceError(message) {
  announceText("error-announcement", message);
}

// js/common/actions/confirm-button.js
var ConfirmButton = class extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      if (!window.confirm(this.getAttribute("data-message") ?? "Are you sure you wish to do this?")) {
        event.preventDefault();
      }
    });
  }
};
if (!window.customElements.get("confirm-button")) {
  window.customElements.define("confirm-button", ConfirmButton, { extends: "button" });
}

// js/common/actions/controls.js
var PageDots = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this._isProductGalleryControls = this.hasAttribute("data-product-gallery-controls");
    this._isProductGalleryThumbnailControls = this.hasAttribute("data-product-gallery-thumbnail-controls");
    this.items = Array.from(this.children);
    this.items.forEach((button, index) => button.addEventListener("click", () => this.select(index), { signal: this._abortController.signal }));
    this.addEventListener("control:filter", this._filterItems, { signal: this._abortController.signal });
    if (this._isProductGalleryControls) {
      this.addEventListener("keydown", this._onKeyboardNavigation, { signal: this._abortController.signal });
      this._syncTabStops();
    }
    if (this.controlledElement) {
      this.controlledElement.addEventListener("carousel:change", (event) => this.select(event.detail.index, false), { signal: this._abortController.signal });
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.select(this.items.indexOf(event.target)));
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get controlledElement() {
    return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
  }
  get selectedIndex() {
    const currentAttribute = this.hasAttribute("data-mobile-gallery-active") ? "data-codex-current" : "aria-current";
    return this.items.findIndex((button) => button.getAttribute(currentAttribute) === "true");
  }
  get visibleItems() {
    return this.items.filter((button) => !button.hidden);
  }
  _syncTabStops() {
    if (!this._isProductGalleryControls) {
      return;
    }
    if (this.hasAttribute("data-mobile-gallery-active")) {
      const mobilePicker = this.hasAttribute("data-mobile-gallery-picker");
      this.items.forEach((button) => button.tabIndex = mobilePicker && !button.hidden ? 0 : -1);
      return;
    }
    if (this._isProductGalleryThumbnailControls) {
      this.items.forEach((button) => button.tabIndex = -1);
      return;
    }
    const visibleItems = this.visibleItems;
    const selectedItem = visibleItems.find((button) => button.getAttribute("aria-current") === "true") || visibleItems[0];
    this.items.forEach((button) => button.tabIndex = button === selectedItem ? 0 : -1);
  }
  select(selectedIndex, dispatchEvent = true) {
    if (!(this.items[selectedIndex] instanceof HTMLElement)) {
      return;
    }
    if (this.hasAttribute("align-selected")) {
      const scrollElement = this.getAttribute("align-selected") !== "" ? this.closest(this.getAttribute("align-selected")) : this;
      scrollElement.scrollTo({
        left: this.items[selectedIndex].offsetLeft - scrollElement.clientWidth / 2 + this.items[selectedIndex].clientWidth / 2,
        top: this.items[selectedIndex].offsetTop - scrollElement.clientHeight / 2 - this.items[selectedIndex].clientHeight / 2,
        behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches ? "smooth" : "auto"
      });
    }
    if (this.selectedIndex === selectedIndex) {
      return;
    }
    this.items.forEach((button, index) => {
      if (this.hasAttribute("data-mobile-gallery-active")) {
        button.setAttribute("data-codex-current", String(selectedIndex === index));
        if (this.hasAttribute("data-mobile-gallery-picker")) button.setAttribute("aria-disabled", String(selectedIndex === index));
        else button.removeAttribute("aria-disabled");
        button.removeAttribute("aria-current");
      } else {
        button.setAttribute("aria-current", String(selectedIndex === index));
      }
    });
    this._syncTabStops();
    if (dispatchEvent) {
      this._dispatchEvent(selectedIndex);
    }
  }
  _filterItems(event) {
    this.items.forEach((item, index) => item.hidden = event.detail.filteredIndexes.includes(index));
    this._syncTabStops();
  }
  _onKeyboardNavigation(event) {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    const button = event.target instanceof Element ? event.target.closest("button") : null;
    if (!(button instanceof HTMLButtonElement) || !this.items.includes(button)) {
      return;
    }
    if (this._isProductGalleryControls && ["Home", "End"].includes(event.key)) {
      return;
    }
    const visibleItems = this.visibleItems;
    const currentIndex = visibleItems.indexOf(button);
    if (currentIndex < 0 || !visibleItems.length || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      return;
    }
    const targetIndex = event.key === "Home" ? 0 : event.key === "End" ? visibleItems.length - 1 : Math.max(0, Math.min(visibleItems.length - 1, currentIndex + (event.key === "ArrowLeft" ? -1 : 1)));
    const nextButton = visibleItems[targetIndex];
    event.preventDefault();
    this.select(this.items.indexOf(nextButton));
    nextButton.focus({ preventScroll: true });
  }
  _dispatchEvent(index) {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:select", { bubbles: true, cancelable: true, detail: { index } }));
  }
};
var PrevButton = class extends HTMLButtonElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.addEventListener("click", () => (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:prev", { bubbles: true, cancelable: true })), { signal: this._abortController.signal });
    if (this.controlledElement) {
      this.controlledElement.addEventListener("scroll:edge-nearing", (event) => this.disabled = event.detail.position === "start", { signal: this._abortController.signal });
      this.controlledElement.addEventListener("scroll:edge-leaving", (event) => this.disabled = event.detail.position === "start" ? false : this.disabled, { signal: this._abortController.signal });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get controlledElement() {
    return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
  }
};
var NextButton = class extends HTMLButtonElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.addEventListener("click", () => (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:next", { bubbles: true, cancelable: true })), { signal: this._abortController.signal });
    if (this.controlledElement) {
      this.controlledElement.addEventListener("scroll:edge-nearing", (event) => this.disabled = event.detail.position === "end", { signal: this._abortController.signal });
      this.controlledElement.addEventListener("scroll:edge-leaving", (event) => this.disabled = event.detail.position === "end" ? false : this.disabled, { signal: this._abortController.signal });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get controlledElement() {
    return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
  }
};
if (!window.customElements.get("page-dots")) {
  window.customElements.define("page-dots", PageDots);
}
if (!window.customElements.get("prev-button")) {
  window.customElements.define("prev-button", PrevButton, { extends: "button" });
}
if (!window.customElements.get("next-button")) {
  window.customElements.define("next-button", NextButton, { extends: "button" });
}

// js/common/actions/copy-button.js
import { timeline } from "vendor";
var CopyButton = class extends HTMLButtonElement {
  constructor() {
    super();
    this.append(this.contentElement, this.animationElement);
    this.addEventListener("click", this._copyToClipboard.bind(this));
  }
  get contentElement() {
    if (this._contentElement) {
      return this._contentElement;
    }
    this._contentElement = document.createElement("div");
    this._contentElement.append(...this.childNodes);
    return this._contentElement = this._contentElement || document.createElement("div").append();
  }
  get animationElement() {
    return this._animationElement = this._animationElement || document.createRange().createContextualFragment(`
      <span class="button__feedback">  
        <svg role="presentation" focusable="false" fill="none" width="18px" height="18px" viewBox="0 0 20 20">
          <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/>
          <path d="m6 9.8 2.63 2.8L14 7" stroke="currentColor" stroke-width="2"/>
        </svg>
      </span>
    `).firstElementChild;
  }
  async _copyToClipboard() {
    if (!navigator.clipboard) {
      return;
    }
    await navigator.clipboard.writeText(this.getAttribute("data-text") || "");
    timeline([
      [this.contentElement, { y: ["translateY(0)", "translateY(-10px)"], opacity: 0 }, { duration: 0.15 }],
      [this.animationElement, { transform: ["translateY(10px)", "translateY(0)"], opacity: [0, 1] }, { duration: 0.15 }],
      [this.animationElement, { transform: ["translateY(0)", "translateY(-10px)"], opacity: 0 }, { duration: 0.15, at: "+0.5" }],
      [this.contentElement, { transform: ["translateY(10px)", "translateY(0)"], opacity: 1 }, { duration: 0.15 }]
    ]);
  }
};
if (!window.customElements.get("copy-button")) {
  window.customElements.define("copy-button", CopyButton, { extends: "button" });
}

// js/common/actions/custom-button.js
import { animate, timeline as timeline2, stagger } from "vendor";
var CustomButton = class extends HTMLButtonElement {
  static get observedAttributes() {
    return ["aria-busy"];
  }
  constructor() {
    super();
    if (this.type === "submit" && this.form) {
      this.form.addEventListener("submit", () => this.setAttribute("aria-busy", "true"));
    }
    this.append(this.contentElement, this.animationElement);
    window.addEventListener("pageshow", () => this.removeAttribute("aria-busy"));
  }
  get contentElement() {
    if (this._contentElement) {
      return this._contentElement;
    }
    this._contentElement = document.createElement("div");
    this._contentElement.append(...this.childNodes);
    return this._contentElement = this._contentElement || document.createElement("div").append();
  }
  get animationElement() {
    return this._animationElement = this._animationElement || document.createRange().createContextualFragment(`
      <span class="button__loader">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `).firstElementChild;
  }
  async attributeChangedCallback(name, oldValue, newValue) {
    if (newValue === "true") {
      timeline2([
        [this.contentElement, { transform: ["translateY(0)", "translateY(-10px)"], opacity: 0 }, { duration: 0.15 }],
        [this.animationElement, { transform: ["translateY(10px)", "translateY(0)"], opacity: 1 }, { duration: 0.15 }]
      ]);
      animate(this.animationElement.children, { opacity: [1, 0.1] }, { duration: 0.35, delay: stagger(0.35 / 3), direction: "alternate", repeat: Infinity });
    } else {
      timeline2([
        [this.animationElement, { transform: ["translateY(0)", "translateY(-10px)"], opacity: 0 }, { duration: 0.15 }],
        [this.contentElement, { transform: ["translateY(10px)", "translateY(0)"], opacity: 1 }, { duration: 0.15 }]
      ]);
    }
  }
};
if (!window.customElements.get("custom-button")) {
  window.customElements.define("custom-button", CustomButton, { extends: "button" });
}

// js/common/actions/share-button.js
var ShareButton = class extends HTMLButtonElement {
  constructor() {
    super();
    if (navigator.share) {
      this.addEventListener("click", this._showSystemShare);
    }
  }
  _showSystemShare() {
    navigator.share({
      title: this.hasAttribute("share-title") ? this.getAttribute("share-title") : document.title,
      url: this.hasAttribute("share-url") ? this.getAttribute("share-url") : window.location.href
    });
  }
};
if (!window.customElements.get("share-button")) {
  window.customElements.define("share-button", ShareButton, { extends: "button" });
}

// js/common/animation/heading.js
import { stagger as stagger2 } from "vendor";
function getHeadingKeyframe(element, options = {}) {
  if (!element) {
    return [];
  }
  const splitLines = element.querySelector("split-lines")?.lines;
  if (window.themeVariables.settings.headingApparition === "fade" || !splitLines) {
    return [element, { opacity: [0, 1] }, { duration: 0.2, ...options }];
  } else {
    element.style.opacity = "1";
    switch (window.themeVariables.settings.headingApparition) {
      case "split_fade":
        return [splitLines, { transform: ["translateY(0.5em)", "translateY(0)"], opacity: [0, 1] }, { duration: 0.3, delay: stagger2(0.1), ...options }];
      case "split_clip":
        return [splitLines, { clipPath: ["inset(0 0 100% 0)", "inset(0 0 -0.3em 0)"], transform: ["translateY(100%)", "translateY(0)"], opacity: [0, 1] }, { duration: 0.7, delay: stagger2(0.15), easing: [0.22, 1, 0.36, 1], ...options }];
      case "split_rotation":
        const rotatedSpans = splitLines.map((line) => line.querySelector("span"));
        rotatedSpans.forEach((span) => span.style.transformOrigin = "top left");
        splitLines.forEach((line) => line.style.clipPath = "inset(0 0 -0.3em 0)");
        return [rotatedSpans, { transform: ["translateY(0.5em) rotateZ(5deg)", "translateY(0) rotateZ(0)"], opacity: [0, 1] }, { duration: 0.4, delay: stagger2(0.1), ...options }];
    }
  }
}

// js/common/animation/reveal-items.js
import { animate as animate2, stagger as stagger3, inView } from "vendor";
var _RevealItems_instances, reveal_fn;
var RevealItems = class extends HTMLElement {
  constructor() {
    super();
    this._codexMotionStateListener = null;
    this._focusInListener = this._handleFocusIn.bind(this);
    this._codexFocusability = new Map();
    __privateAdd(this, _RevealItems_instances);
  }
  connectedCallback() {
    this._codexMotionStateListener = this._handleCodexMotionStateChange.bind(this);
    document.addEventListener("codex:motion-state-change", this._codexMotionStateListener);
    this.addEventListener("focusin", this._focusInListener);
    if (this._motionPaused()) {
      this._renderStaticState();
      return;
    }
    if (this.hasAttribute("disable-on-mobile") && window.matchMedia("(max-width: 699px)").matches) {
      this.style.opacity = "1";
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches && window.themeVariables.settings.staggerProductsApparition) {
      this._setRevealItemsFocusable(false);
      inView(this, __privateMethod(this, _RevealItems_instances, reveal_fn).bind(this), { margin: this.getAttribute("margin") ?? "-50px 0px" });
    }
  }
  disconnectedCallback() {
    this._restoreRevealItemsFocusability();
    this.removeEventListener("focusin", this._focusInListener);
    if (this._codexMotionStateListener) {
      document.removeEventListener("codex:motion-state-change", this._codexMotionStateListener);
      this._codexMotionStateListener = null;
    }
  }
  _getRevealItems() {
    return this.hasAttribute("selector") ? Array.from(this.querySelectorAll(this.getAttribute("selector"))) : Array.from(this.children);
  }
  _getFocusableDescendants(item) {
    const selector = 'a[href], button, input, select, textarea, summary, [tabindex]';
    return [item, ...item.querySelectorAll(selector)].filter((element) => element instanceof HTMLElement);
  }
  _setRevealItemsFocusable(focusable) {
    this._getRevealItems().forEach((item) => {
      this._getFocusableDescendants(item).forEach((element) => {
        if (!focusable) {
          if (element.tabIndex < 0) {
            return;
          }
          if (!this._codexFocusability.has(element)) {
            this._codexFocusability.set(element, element.getAttribute("tabindex"));
          }
          element.setAttribute("tabindex", "-1");
          return;
        }

        if (!this._codexFocusability.has(element)) {
          return;
        }
        const originalTabindex = this._codexFocusability.get(element);
        if (originalTabindex === null) {
          element.removeAttribute("tabindex");
        } else {
          element.setAttribute("tabindex", originalTabindex);
        }
        this._codexFocusability.delete(element);
      });
    });
  }
  _restoreRevealItemFocusability(item) {
    this._getFocusableDescendants(item).forEach((element) => {
      if (!this._codexFocusability.has(element)) {
        return;
      }
      const originalTabindex = this._codexFocusability.get(element);
      if (originalTabindex === null) {
        element.removeAttribute("tabindex");
      } else {
        element.setAttribute("tabindex", originalTabindex);
      }
      this._codexFocusability.delete(element);
    });
  }
  _restoreRevealItemsFocusability() {
    this._codexFocusability.forEach((originalTabindex, element) => {
      if (!(element instanceof HTMLElement) || !element.isConnected) {
        return;
      }
      if (originalTabindex === null) {
        element.removeAttribute("tabindex");
      } else {
        element.setAttribute("tabindex", originalTabindex);
      }
    });
    this._codexFocusability.clear();
  }
  _handleFocusIn(event) {
    const revealItems = this._getRevealItems();
    const focusedItem = revealItems.find((item) => item.contains(event.target));
    if (!focusedItem) {
      return;
    }
    this._restoreRevealItemFocusability(focusedItem);
    this.style.opacity = "1";
    this.style.visibility = "visible";
    this.style.transform = "none";
    focusedItem.getAnimations().filter((animation) => animation.effect?.target === focusedItem).forEach((animation) => animation.finish());
    focusedItem.style.opacity = "1";
    focusedItem.style.visibility = "visible";
    focusedItem.style.transform = "none";
  }
  _motionPaused() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches || document.documentElement.getAttribute("data-codex-motion-paused") === "true" || window.__OOLMotionPaused === true;
  }
  _handleCodexMotionStateChange() {
    if (this._motionPaused()) {
      this._renderStaticState();
    }
  }
  _renderStaticState() {
    const revealItems = this._getRevealItems();
    this._restoreRevealItemsFocusability();
    this.style.opacity = "1";
    this.style.visibility = "visible";
    this.style.transform = "none";
    revealItems.forEach((item) => {
      item.style.opacity = "1";
      item.style.visibility = "visible";
      item.style.transform = "none";
      Array.from(item.querySelectorAll("img, svg, .collection-card__content-wrapper, .collection-card__content, .product-card__info")).forEach((element) => {
        element.style.opacity = "1";
        element.style.visibility = "visible";
        element.style.transform = "none";
      });
    });
  }
};
_RevealItems_instances = new WeakSet();
reveal_fn = function() {
  const revealItems = this.hasAttribute("selector") ? Array.from(this.querySelectorAll(this.getAttribute("selector"))) : Array.from(this.children);
  this.style.opacity = "1";
  animate2(
    revealItems,
    { opacity: [0, 1], transform: ["translateY(15px)", "translateY(0)"] },
    { duration: 0.35, delay: stagger3(0.05, { easing: "ease-out" }), easing: "ease" }
  ).finished.then(() => this._restoreRevealItemsFocusability(), () => this._restoreRevealItemsFocusability());
};
if (!window.customElements.get("reveal-items")) {
  window.customElements.define("reveal-items", RevealItems);
}

// js/common/behavior/custom-cursor.js
var _abortController, _CustomCursor_instances, onPointerLeave_fn, onPointerMove_fn;
var CustomCursor = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CustomCursor_instances);
    __privateAdd(this, _abortController);
  }
  connectedCallback() {
    __privateSet(this, _abortController, new AbortController());
    this.parentElement.addEventListener("pointermove", __privateMethod(this, _CustomCursor_instances, onPointerMove_fn).bind(this), { passive: true, signal: __privateGet(this, _abortController).signal });
    this.parentElement.addEventListener("pointerleave", __privateMethod(this, _CustomCursor_instances, onPointerLeave_fn).bind(this), { signal: __privateGet(this, _abortController).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController).abort();
  }
};
_abortController = new WeakMap();
_CustomCursor_instances = new WeakSet();
onPointerLeave_fn = function() {
  this.classList.remove("is-visible", "is-half-start", "is-half-end");
};
onPointerMove_fn = function(event) {
  if (event.target.matches("button, a[href], button :scope, a[href] :scope")) {
    return this.classList.remove("is-visible");
  }
  const parentBoundingRect = this.parentElement.getBoundingClientRect(), parentXCenter = (parentBoundingRect.left + parentBoundingRect.right) / 2, isOnStartHalfPart = event.pageX < parentXCenter;
  this.classList.toggle("is-half-start", isOnStartHalfPart);
  this.classList.toggle("is-half-end", !isOnStartHalfPart);
  this.classList.add("is-visible");
  const mouseY = event.clientY - parentBoundingRect.y - this.clientHeight / 2, mouseX = event.clientX - parentBoundingRect.x - this.clientWidth / 2;
  this.style.translate = `${mouseX.toFixed(3)}px ${mouseY.toFixed(3)}px`;
  this.style.transform = `${mouseX.toFixed(3)}px ${mouseY.toFixed(3)}px`;
};
if (!window.customElements.get("custom-cursor")) {
  window.customElements.define("custom-cursor", CustomCursor);
}

// js/common/behavior/gesture-area.js
var _domElement, _thresholdDistance, _thresholdTime, _signal, _firstClientX, _tracking, _start, _GestureArea_instances, touchStart_fn, preventTouch_fn, gestureStart_fn, gestureMove_fn, gestureEnd_fn;
var GestureArea = class {
  constructor(domElement, { thresholdDistance = 80, thresholdTime = 500, signal = null } = {}) {
    __privateAdd(this, _GestureArea_instances);
    __privateAdd(this, _domElement);
    __privateAdd(this, _thresholdDistance);
    __privateAdd(this, _thresholdTime);
    __privateAdd(this, _signal);
    __privateAdd(this, _firstClientX);
    __privateAdd(this, _tracking, false);
    __privateAdd(this, _start, {});
    __privateSet(this, _domElement, domElement);
    __privateSet(this, _thresholdDistance, thresholdDistance);
    __privateSet(this, _thresholdTime, thresholdTime);
    __privateSet(this, _signal, signal);
    __privateGet(this, _domElement).addEventListener("touchstart", __privateMethod(this, _GestureArea_instances, touchStart_fn).bind(this), { passive: true, signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("touchmove", __privateMethod(this, _GestureArea_instances, preventTouch_fn).bind(this), { passive: false, signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointerdown", __privateMethod(this, _GestureArea_instances, gestureStart_fn).bind(this), { signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointermove", __privateMethod(this, _GestureArea_instances, gestureMove_fn).bind(this), { passive: false, signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointerup", __privateMethod(this, _GestureArea_instances, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointerleave", __privateMethod(this, _GestureArea_instances, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointercancel", __privateMethod(this, _GestureArea_instances, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
  }
};
_domElement = new WeakMap();
_thresholdDistance = new WeakMap();
_thresholdTime = new WeakMap();
_signal = new WeakMap();
_firstClientX = new WeakMap();
_tracking = new WeakMap();
_start = new WeakMap();
_GestureArea_instances = new WeakSet();
touchStart_fn = function(event) {
  __privateSet(this, _firstClientX, event.touches[0].clientX);
};
preventTouch_fn = function(event) {
  if (Math.abs(event.touches[0].clientX - __privateGet(this, _firstClientX)) > 10) {
    event.preventDefault();
  }
};
gestureStart_fn = function(event) {
  __privateSet(this, _tracking, true);
  __privateSet(this, _start, {
    time: (/* @__PURE__ */ new Date()).getTime(),
    x: event.clientX,
    y: event.clientY
  });
};
gestureMove_fn = function(event) {
  if (__privateGet(this, _tracking)) {
    event.preventDefault();
  }
};
gestureEnd_fn = function(event) {
  if (!__privateGet(this, _tracking)) {
    return;
  }
  __privateSet(this, _tracking, false);
  const now = (/* @__PURE__ */ new Date()).getTime(), deltaTime = now - __privateGet(this, _start).time, deltaX = event.clientX - __privateGet(this, _start).x, deltaY = event.clientY - __privateGet(this, _start).y;
  if (deltaTime > __privateGet(this, _thresholdTime)) {
    return;
  }
  let matchedEvent;
  if (deltaX === 0 && deltaY === 0 && !event.target.matches("a, button, a :scope, button :scope")) {
    matchedEvent = "tap";
  } else if (deltaX > __privateGet(this, _thresholdDistance) && Math.abs(deltaY) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swiperight";
  } else if (-deltaX > __privateGet(this, _thresholdDistance) && Math.abs(deltaY) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swipeleft";
  } else if (deltaY > __privateGet(this, _thresholdDistance) && Math.abs(deltaX) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swipedown";
  } else if (-deltaY > __privateGet(this, _thresholdDistance) && Math.abs(deltaX) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swipeup";
  }
  if (matchedEvent) {
    __privateGet(this, _domElement).dispatchEvent(new CustomEvent(matchedEvent, { bubbles: true, composed: true, detail: { originalEvent: event } }));
  }
};

// js/common/behavior/height-observer.js
var HeightObserver = class extends HTMLElement {
  constructor() {
    super();
    if (window.ResizeObserver) {
      new ResizeObserver(this._updateCustomProperties.bind(this)).observe(this);
    }
  }
  connectedCallback() {
    if (!window.ResizeObserver) {
      document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${this.clientHeight.toFixed(1)}px`);
    }
  }
  _updateCustomProperties(entries) {
    requestAnimationFrame(() => {
      entries.forEach((entry) => {
        if (entry.target === this) {
          const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;
          document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${Math.round(height)}px`);
        }
      });
    });
  }
};
if (!window.customElements.get("height-observer")) {
  window.customElements.define("height-observer", HeightObserver);
}

// js/common/behavior/safe-sticky.js
import { inView as inView2 } from "vendor";
var _resizeObserver, _checkPositionListener, _initialTop, _lastKnownY, _currentTop, _position, _SafeSticky_instances, recalculateStyles_fn, checkPosition_fn;
var SafeSticky = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _SafeSticky_instances);
    __privateAdd(this, _resizeObserver, new ResizeObserver(__privateMethod(this, _SafeSticky_instances, recalculateStyles_fn).bind(this)));
    __privateAdd(this, _checkPositionListener, throttle(__privateMethod(this, _SafeSticky_instances, checkPosition_fn).bind(this)));
    __privateAdd(this, _initialTop, 0);
    __privateAdd(this, _lastKnownY, 0);
    /* we could initialize it to window.scrollY but this avoids a costly reflow */
    __privateAdd(this, _currentTop, 0);
    __privateAdd(this, _position, "relative");
  }
  connectedCallback() {
    inView2(this, () => {
      __privateGet(this, _resizeObserver).observe(this);
      window.addEventListener("scroll", __privateGet(this, _checkPositionListener));
      return () => {
        __privateGet(this, _resizeObserver).unobserve(this);
        window.removeEventListener("scroll", __privateGet(this, _checkPositionListener));
      };
    }, { margin: "500px" });
  }
  disconnectedCallback() {
    window.removeEventListener("scroll", __privateGet(this, _checkPositionListener));
  }
};
_resizeObserver = new WeakMap();
_checkPositionListener = new WeakMap();
_initialTop = new WeakMap();
_lastKnownY = new WeakMap();
_currentTop = new WeakMap();
_position = new WeakMap();
_SafeSticky_instances = new WeakSet();
recalculateStyles_fn = function() {
  this.style.removeProperty("top");
  const computedStyles = getComputedStyle(this);
  __privateSet(this, _initialTop, parseInt(computedStyles.top));
  __privateSet(this, _position, computedStyles.position);
  __privateMethod(this, _SafeSticky_instances, checkPosition_fn).call(this);
};
checkPosition_fn = function() {
  if (__privateGet(this, _position) !== "sticky") {
    return this.style.removeProperty("top");
  }
  let bounds = this.getBoundingClientRect(), maxTop = bounds.top + window.scrollY - this.offsetTop + __privateGet(this, _initialTop), minTop = this.clientHeight - window.innerHeight + 20;
  if (window.scrollY < __privateGet(this, _lastKnownY)) {
    __privateSet(this, _currentTop, __privateGet(this, _currentTop) - (window.scrollY - __privateGet(this, _lastKnownY)));
  } else {
    __privateSet(this, _currentTop, __privateGet(this, _currentTop) + (__privateGet(this, _lastKnownY) - window.scrollY));
  }
  __privateSet(this, _currentTop, Math.min(Math.max(__privateGet(this, _currentTop), -minTop), maxTop, __privateGet(this, _initialTop)));
  __privateSet(this, _lastKnownY, window.scrollY);
  this.style.top = `${Math.round(__privateGet(this, _currentTop))}px`;
};
if (!window.customElements.get("safe-sticky")) {
  window.customElements.define("safe-sticky", SafeSticky);
}

// js/common/behavior/scroll-area.js
var ScrollArea = class {
  constructor(element, abortController2 = null) {
    this._element = element;
    this._allowTriggerNearingStartEvent = false;
    this._allowTriggerLeavingStartEvent = true;
    this._allowTriggerNearingEndEvent = true;
    this._allowTriggerLeavingEndEvent = false;
    new ResizeObserver(this._checkIfScrollable.bind(this)).observe(element);
    this._element.addEventListener("scroll", throttle(this._onScroll.bind(this)), { signal: abortController2?.signal });
  }
  get scrollNearingThreshold() {
    return 125;
  }
  get scrollDirection() {
    if (this._element.scrollWidth > this._element.clientWidth) {
      return "inline";
    } else if (this._element.scrollHeight - this._element.clientHeight > 1) {
      return "block";
    } else {
      return "none";
    }
  }
  _checkIfScrollable() {
    this._element.classList.toggle("is-scrollable", this.scrollDirection !== "none");
  }
  _onScroll() {
    clearTimeout(this._scrollTimeout);
    this._lastScrollPosition = this._lastScrollPosition ?? (this.scrollDirection === "inline" ? Math.abs(this._element.scrollLeft) : Math.abs(this._element.scrollTop));
    let direction;
    if (this.scrollDirection === "inline") {
      direction = this._lastScrollPosition > Math.abs(this._element.scrollLeft) ? "start" : "end";
      this._lastScrollPosition = Math.abs(this._element.scrollLeft);
    } else {
      direction = this._lastScrollPosition > Math.abs(this._element.scrollTop) ? "start" : "end";
      this._lastScrollPosition = Math.abs(this._element.scrollTop);
    }
    const scrollPosition = Math.round(Math.abs(this.scrollDirection === "inline" ? this._element.scrollLeft : this._element.scrollTop)), scrollMinusSize = Math.round(this.scrollDirection === "inline" ? this._element.scrollWidth - this._element.clientWidth : this._element.scrollHeight - this._element.clientHeight);
    if (direction === "start" && this._allowTriggerNearingStartEvent && scrollPosition <= this.scrollNearingThreshold) {
      this._allowTriggerNearingStartEvent = false;
      this._allowTriggerLeavingStartEvent = true;
      this._element.dispatchEvent(new CustomEvent("scroll:edge-nearing", { bubbles: true, detail: { position: "start" } }));
    } else if (direction === "end" && scrollPosition > this.scrollNearingThreshold) {
      this._allowTriggerNearingStartEvent = true;
      if (this._allowTriggerLeavingStartEvent) {
        this._allowTriggerLeavingStartEvent = false;
        this._element.dispatchEvent(new CustomEvent("scroll:edge-leaving", { bubbles: true, detail: { position: "start" } }));
      }
    }
    if (direction === "end" && this._allowTriggerNearingEndEvent && scrollMinusSize <= scrollPosition + this.scrollNearingThreshold) {
      this._allowTriggerNearingEndEvent = false;
      this._allowTriggerLeavingEndEvent = true;
      this._element.dispatchEvent(new CustomEvent("scroll:edge-nearing", { bubbles: true, detail: { position: "end" } }));
    } else if (direction === "start" && scrollMinusSize > scrollPosition + this.scrollNearingThreshold) {
      this._allowTriggerNearingEndEvent = true;
      if (this._allowTriggerLeavingEndEvent) {
        this._allowTriggerLeavingEndEvent = false;
        this._element.dispatchEvent(new CustomEvent("scroll:edge-leaving", { bubbles: true, detail: { position: "end" } }));
      }
    }
    if (window.onscrollend === void 0) {
      this._scrollTimeout = setTimeout(() => {
        this._element.dispatchEvent(new CustomEvent("scrollend", { bubbles: true, composed: true }));
      }, 75);
    }
  }
};

// js/common/behavior/scroll-progress.js
var ScrollProgress = class extends HTMLElement {
  connectedCallback() {
    this.scrolledElement.addEventListener("scroll", throttle(this._updateScrollProgress.bind(this)));
    if (window.ResizeObserver) {
      new ResizeObserver(this._updateScrollProgress.bind(this)).observe(this.scrolledElement);
    }
  }
  get scrolledElement() {
    return this._scrolledElement = this._scrolledElement || document.getElementById(this.getAttribute("observes"));
  }
  _updateScrollProgress() {
    const scrollLeft = document.dir === "ltr" ? this.scrolledElement.scrollLeft : Math.abs(this.scrolledElement.scrollLeft), advancement = (scrollLeft + this.scrolledElement.clientWidth) / this.scrolledElement.scrollWidth;
    this.style.setProperty("--scroll-progress", Math.max(0, Math.min(advancement, 1)).toFixed(6));
  }
};
if (!window.customElements.get("scroll-progress")) {
  window.customElements.define("scroll-progress", ScrollProgress);
}

// js/common/behavior/scroll-shadow.js
var template = `
  <style>
    :host {
      display: inline-block;
      contain: layout;
      position: relative;
    }
    
    :host([hidden]) {
      display: none;
    }
    
    s {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      pointer-events: none;
      background-image:
        var(--scroll-shadow-top, linear-gradient(to bottom, rgb(var(--background)), rgb(var(--background) / 0))),
        var(--scroll-shadow-bottom, linear-gradient(to top, rgb(var(--background)), rgb(var(--background) / 0))),
        var(--scroll-shadow-left, linear-gradient(to right, rgb(var(--background)), rgb(var(--background) / 0))),
        var(--scroll-shadow-right, linear-gradient(to left, rgb(var(--background)), rgb(var(--background) / 0)));
      background-position: top, bottom, left, right;
      background-repeat: no-repeat;
      background-size: 100% var(--top, 0), 100% var(--bottom, 0), var(--left, 0) 100%, var(--right, 0) 100%;
    }
  </style>
  <slot></slot>
  <s></s>
`;
var Updater = class {
  constructor(targetElement) {
    this.scheduleUpdate = throttle(() => this.update(targetElement, getComputedStyle(targetElement)));
    this.resizeObserver = new ResizeObserver(this.scheduleUpdate.bind(this));
  }
  start(element) {
    if (this.element) {
      this.stop();
    }
    if (element) {
      element.addEventListener("scroll", this.scheduleUpdate);
      this.resizeObserver.observe(element);
      this.element = element;
    }
  }
  stop() {
    if (!this.element) {
      return;
    }
    this.element.removeEventListener("scroll", this.scheduleUpdate);
    this.resizeObserver.unobserve(this.element);
    this.element = null;
  }
  update(targetElement, style) {
    if (!this.element) {
      return;
    }
    const maxSize = style.getPropertyValue("--scroll-shadow-size") ? parseInt(style.getPropertyValue("--scroll-shadow-size")) : 0;
    const scroll5 = {
      top: Math.max(this.element.scrollTop, 0),
      bottom: Math.max(this.element.scrollHeight - this.element.offsetHeight - this.element.scrollTop, 0),
      left: Math.max(this.element.scrollLeft, 0),
      right: Math.max(this.element.scrollWidth - this.element.offsetWidth - this.element.scrollLeft, 0)
    };
    requestAnimationFrame(() => {
      for (const position of ["top", "bottom", "left", "right"]) {
        targetElement.style.setProperty(
          `--${position}`,
          `${scroll5[position] > maxSize ? maxSize : scroll5[position]}px`
        );
      }
    });
  }
};
var ScrollShadow = class extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).innerHTML = template;
    this.updater = new Updater(this.shadowRoot.lastElementChild);
  }
  connectedCallback() {
    this.shadowRoot.querySelector("slot").addEventListener("slotchange", this.start);
    this.start();
  }
  disconnectedCallback() {
    this.updater.stop();
  }
  start() {
    if (this.firstElementChild) {
      this.updater.start(this.firstElementChild);
    }
  }
};
if ("ResizeObserver" in window && !window.customElements.get("scroll-shadow")) {
  window.customElements.define("scroll-shadow", ScrollShadow);
}

// js/common/behavior/split-lines.js
var _requireSplit, _lastScreenWidth, _SplitLines_instances, split_fn, onWindowResized_fn;
var SplitLines = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _SplitLines_instances);
    __privateAdd(this, _requireSplit, true);
    __privateAdd(this, _lastScreenWidth, window.innerWidth);
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(document.createRange().createContextualFragment("<slot></slot>"));
    window.addEventListener("resize", throttle(__privateMethod(this, _SplitLines_instances, onWindowResized_fn).bind(this)));
    new MutationObserver(__privateMethod(this, _SplitLines_instances, split_fn).bind(this, true)).observe(this, { characterData: true, attributes: false, childList: false, subtree: true });
  }
  connectedCallback() {
    __privateMethod(this, _SplitLines_instances, split_fn).call(this);
  }
  get lines() {
    return Array.from(this.shadowRoot.children);
  }
};
_requireSplit = new WeakMap();
_lastScreenWidth = new WeakMap();
_SplitLines_instances = new WeakSet();
split_fn = function(force = false) {
  if (!__privateGet(this, _requireSplit) && !force) {
    return;
  }
  this.shadowRoot.innerHTML = this.textContent.replace(/./g, "<span>$&</span>").replace(/\s/g, " ");
  const bounds = /* @__PURE__ */ new Map();
  Array.from(this.shadowRoot.children).forEach((letter) => {
    const key = Math.round(letter.getBoundingClientRect().top);
    bounds.set(key, (bounds.get(key) || "").concat(letter.textContent));
  });
  this.shadowRoot.replaceChildren(...Array.from(bounds.values(), (line) => document.createRange().createContextualFragment(`
      <span style="display: inline-block;">
        <span style="display: block">${line}</span>
      </span>
    `)));
  __privateSet(this, _requireSplit, false);
};
onWindowResized_fn = function() {
  if (__privateGet(this, _lastScreenWidth) === window.innerWidth) {
    return;
  }
  __privateMethod(this, _SplitLines_instances, split_fn).call(this, true);
  __privateSet(this, _lastScreenWidth, window.innerWidth);
};
if (!window.customElements.get("split-lines")) {
  window.customElements.define("split-lines", SplitLines);
}

// js/common/carousel/effect-carousel.js
import { timeline as timeline3, inView as inView3 } from "vendor";

// js/common/carousel/base-carousel.js
var BaseCarousel = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this._reloaded = false;
    if (Shopify.designMode) {
      this.closest(".shopify-section")?.addEventListener("shopify:section:select", (event) => this._reloaded = event.detail.load);
    }
    if (this.items.length > 1) {
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.select(this.items.indexOf(event.target), { animate: !event.detail.load }));
      }
      if (this.hasAttribute("adaptive-height")) {
        this.addEventListener("carousel:settle", this._adjustHeight);
        this._adjustHeight();
      }
      this.addEventListener("carousel:select", this._preloadImages, { signal: this._abortController.signal });
      this.addEventListener("carousel:filter", this._filterItems, { signal: this._abortController.signal });
      this.addEventListener("control:prev", this.previous, { signal: this._abortController.signal });
      this.addEventListener("control:next", this.next, { signal: this._abortController.signal });
      this.addEventListener("control:select", (event) => this.select(event.detail.index), { signal: this._abortController.signal });
    }
    if (this.selectedIndex === 0) {
      this.selectedSlide.classList.add("is-selected");
      this._dispatchEvent("carousel:select", 0);
    } else {
      this.select(this.selectedIndex, { animate: false, force: true });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get items() {
    if (this.hasAttribute("reversed")) {
      return this._items = this._items || Array.from(this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children).reverse();
    } else {
      return this._items = this._items || Array.from(this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children);
    }
  }
  get visibleItems() {
    return this.items.filter((item) => item.offsetParent !== null);
  }
  get selectedIndex() {
    return this._selectedIndex = this._selectedIndex ?? parseInt(this.getAttribute("initial-index") || 0);
  }
  get selectedIndexAmongVisible() {
    return this.visibleItems.indexOf(this.selectedSlide);
  }
  get loop() {
    return false;
  }
  get selectedSlide() {
    return this.items[this.selectedIndex];
  }
  get previousSlide() {
    return this.visibleItems[this.loop ? (this.selectedIndexAmongVisible - 1 + this.visibleItems.length) % this.visibleItems.length : Math.max(this.selectedIndexAmongVisible - 1, 0)];
  }
  get nextSlide() {
    return this.visibleItems[this.loop ? (this.selectedIndexAmongVisible + 1 + this.visibleItems.length) % this.visibleItems.length : Math.min(this.selectedIndexAmongVisible + 1, this.visibleItems.length - 1)];
  }
  previous(animate19 = true) {
    this.select(this.items.indexOf(this.previousSlide), { direction: "previous", animate: animate19 });
  }
  next(animate19 = true) {
    this.select(this.items.indexOf(this.nextSlide), { direction: "next", animate: animate19 });
  }
  _transitionTo(fromSlide, toSlide, options = {}) {
  }
  _adjustHeight() {
    if (this.hasAttribute("adaptive-height") && this.selectedSlide.clientHeight !== this.clientHeight) {
      this.style.maxHeight = `${this.selectedSlide.clientHeight}px`;
    }
  }
  _filterItems(event) {
    this.items.forEach((item, index) => item.hidden = event.detail.filteredIndexes.includes(index));
  }
  _preloadImages() {
    requestAnimationFrame(() => {
      [this.previousSlide, this.nextSlide].forEach((item) => {
        if (item) {
          Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.removeAttribute("loading"));
        }
      });
    });
  }
  _dispatchEvent(eventName, index) {
    this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: { slide: this.items[index], index } }));
  }
};

// js/common/carousel/effect-carousel.js
var EffectCarousel = class extends BaseCarousel {
  connectedCallback() {
    if (this.items.length > 1 && this.hasAttribute("autoplay")) {
      this._player = new Player(this.getAttribute("autoplay"));
      this._player.addEventListener("player:end", this.next.bind(this));
      inView3(this, () => this._player.resume(true));
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", () => this._player.stop());
        this.addEventListener("shopify:block:deselect", () => this._player.start());
      }
    }
    super.connectedCallback();
    if (this.items.length > 1) {
      if (this.swipeable) {
        new GestureArea(this, { signal: this._abortController.signal });
        this.addEventListener("swipeleft", this.next, { signal: this._abortController.signal });
        this.addEventListener("swiperight", this.previous, { signal: this._abortController.signal });
      }
    }
  }
  get player() {
    return this._player;
  }
  get loop() {
    return true;
  }
  get swipeable() {
    return this.getAttribute("swipeable") !== "false";
  }
  async select(index, { direction, animate: animate19 = true } = {}) {
    const indexBeforeChange = this.selectedIndex;
    this._selectedIndex = index;
    this._dispatchEvent("carousel:select", index);
    if (!direction) {
      direction = index > indexBeforeChange ? "next" : "previous";
    }
    if (index !== indexBeforeChange) {
      const [fromSlide, toSlide] = [this.items[indexBeforeChange], this.items[index]];
      this._dispatchEvent("carousel:change", index);
      this.player?.pause();
      await this._transitionTo(fromSlide, toSlide, { direction, animate: animate19 });
      this.player?.resume(true);
      this._dispatchEvent("carousel:settle", index);
    }
  }
  /**
   * Perform a simple fade animation. For more complex animations, you should implement your own custom elements
   * that extends the EffectCarousel, and implement your own transition. You should make sure to return a promise
   * that resolves when the animation is finished
   */
  _transitionTo(fromSlide, toSlide, { direction = "next", animate: animate19 = true } = {}) {
    fromSlide.classList.remove("is-selected");
    toSlide.classList.add("is-selected");
    const timelineControls = timeline3([
      [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"], zIndex: 0 }, { zIndex: { easing: "step-end" } }],
      [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"], zIndex: 1 }, { at: "<", zIndex: { easing: "step-end" } }]
    ], { duration: animate19 ? 0.3 : 0 });
    return timelineControls.finished;
  }
};
if (!window.customElements.get("effect-carousel")) {
  window.customElements.define("effect-carousel", EffectCarousel);
}

// js/common/carousel/scroll-carousel.js
var ScrollCarousel = class extends BaseCarousel {
  constructor() {
    super();
    if (window.ResizeObserver) {
      new ResizeObserver(throttle(this._adjustHeight.bind(this))).observe(this);
    }
  }
  connectedCallback() {
    this._hasPendingProgrammaticScroll = false;
    this._scrollArea = new ScrollArea(this, this._abortController);
    super.connectedCallback();
    this.addEventListener("scroll", throttle(this._onCarouselScroll.bind(this)), { signal: this._abortController.signal });
    this.addEventListener("scrollend", this._onScrollSettled, { signal: this._abortController.signal });
  }
  get itemOffset() {
    return this.visibleItems.length < 2 ? 0 : this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
  }
  get slidesPerPage() {
    return this.visibleItems.length < 2 ? 1 : Math.floor((this.clientWidth - this.visibleItems[0].offsetLeft) / (Math.abs(this.itemOffset) - (parseInt(getComputedStyle(this).gap) || 0)));
  }
  get totalPages() {
    return this.visibleItems.length < 2 ? 1 : this.visibleItems.length - this.slidesPerPage + 1;
  }
  select(index, { animate: animate19 = true, force = false, exact = false } = {}) {
    const indexBeforeChange = this.selectedIndex;
    if (!this.offsetParent || this._scrollArea.scrollDirection === "none") {
      return this._selectedIndex = index;
    }
    const indexAmongVisible = this.visibleItems.indexOf(this.items[index]);
    index = this.items.indexOf(this.visibleItems[exact ? indexAmongVisible : Math.min(this.totalPages, indexAmongVisible)]);
    this._selectedIndex = index;
    this._dispatchEvent("carousel:select", index);
    if (index !== indexBeforeChange || force) {
      const [fromSlide, toSlide] = [this.items[indexBeforeChange], this.items[index]];
      this._dispatchEvent("carousel:change", index);
      this._transitionTo(fromSlide, toSlide, { animate: animate19 });
    }
  }
  /**
   * Transition using the scrollTo method. To prevent the intersection observer to caught up the change, we set up
   * a "hasPendingProgrammaticScroll" variable to true, that is set back to false once the scroll has settled
   */
  _transitionTo(fromSlide, toSlide, { animate: animate19 = true } = {}) {
    fromSlide.classList.remove("is-selected");
    toSlide.classList.add("is-selected");
    let slideAlign = this._extractSlideAlign(toSlide), scrollAmount = 0;
    switch (slideAlign) {
      case "start":
        scrollAmount = this.itemOffset * this.visibleItems.indexOf(toSlide);
        break;
      case "center":
        scrollAmount = toSlide.offsetLeft - (this.clientWidth / 2 - (parseInt(getComputedStyle(this).scrollPaddingInline) || 0)) + toSlide.clientWidth / 2;
        break;
    }
    this._hasPendingProgrammaticScroll = animate19;
    this.scrollTo({ left: scrollAmount, behavior: animate19 ? "smooth" : "auto" });
  }
  /**
   * Update the index when manually scrolling (which allows to update the controls)
   */
  _onCarouselScroll() {
    if (this._hasPendingProgrammaticScroll || this._scrollArea.scrollDirection === "none") {
      return;
    }
    const newIndex = this.items.indexOf(this.visibleItems[Math.round(this.scrollLeft / this.itemOffset)]);
    if (newIndex !== this.selectedIndex) {
      this._selectedIndex = newIndex;
      this._dispatchEvent("carousel:select", this.selectedIndex);
      this._dispatchEvent("carousel:change", this.selectedIndex);
    }
  }
  /**
   * On the scroll has settled we dispatch the event (which covers both programmatic scroll and swipe)
   */
  _onScrollSettled() {
    this.items.forEach((item) => item.classList.remove("is-selected"));
    this.selectedSlide.classList.add("is-selected");
    this._hasPendingProgrammaticScroll = false;
    this._dispatchEvent("carousel:settle", this.selectedIndex);
  }
  _adjustHeight() {
    this.style.maxHeight = null;
    if (this._scrollArea?.scrollDirection !== "none") {
      super._adjustHeight();
    }
  }
  _extractSlideAlign(slide) {
    return getComputedStyle(slide).scrollSnapAlign === "center" ? "center" : "start";
  }
};
if (!window.customElements.get("scroll-carousel")) {
  window.customElements.define("scroll-carousel", ScrollCarousel);
}

// js/common/cart/cart-count.js
import { animate as animate3 } from "vendor";

// js/common/cart/fetch-cart.js
var createCartPromise = () => {
  return new Promise(async (resolve) => {
    resolve(await (await fetch(`${Shopify.routes.root}cart.js`)).json());
  });
};
var fetchCart = createCartPromise();
document.addEventListener("cart:refresh", () => {
  fetchCart = createCartPromise();
});
document.addEventListener("cart:change", (event) => {
  fetchCart = event.detail["cart"];
});
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    fetchCart = createCartPromise();
  }
});

// js/common/cart/cart-count.js
var CartCount = class extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(document.createRange().createContextualFragment("<span><slot></slot></span>"));
  }
  connectedCallback() {
    this._abortController = new AbortController();
    document.addEventListener("cart:change", (event) => this.itemCount = event.detail["cart"]["item_count"], { signal: this._abortController.signal });
    document.addEventListener("cart:refresh", this._updateFromServer.bind(this), { signal: this._abortController.signal });
    window.addEventListener("pageshow", this._updateFromServer.bind(this));
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  async _updateFromServer() {
    this.itemCount = (await fetchCart)["item_count"];
  }
  get itemCount() {
    return parseInt(this.textContent);
  }
  set itemCount(count) {
    if (this.itemCount === count) {
      return;
    }
    if (count === 0) {
      animate3(this, { opacity: 0 }, { duration: 0.1 });
      this.textContent = count;
    } else if (this.itemCount === 0) {
      animate3(this, { opacity: 1 }, { duration: 0.1 });
      this.textContent = count;
    } else {
      (async () => {
        await animate3(this.shadowRoot.firstElementChild, { transform: ["translateY(-50%)"], opacity: 0 }, { duration: 0.25, easing: [1, 0, 0, 1] }).finished;
        this.textContent = count;
        animate3(this.shadowRoot.firstElementChild, { transform: ["translateY(50%)", "translateY(0)"], opacity: 1 }, { duration: 0.25, easing: [1, 0, 0, 1] });
      })();
    }
  }
};
if (!window.customElements.get("cart-count")) {
  window.customElements.define("cart-count", CartCount);
}

// js/common/cart/cart-discount.js
var AbstractCartDiscount = class extends HTMLElement {
  async getDiscountCodes() {
    return (await fetchCart)["discount_codes"].filter((discount) => discount.applicable).map((discount) => discount.code.toLowerCase());
  }
  async toggleDiscount(event) {
    let target = event.currentTarget;
    target.setAttribute("aria-busy", "true");
    let discountCodes = await this.getDiscountCodes();
    if (target.hasAttribute("discount-code")) {
      discountCodes = discountCodes.filter((discount2) => discount2 !== target.getAttribute("discount-code").toLowerCase());
    }
    let discount = (discountCodes.length > 0 ? discountCodes.join(",") + "," : "") + (event.target.value || "");
    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({ discount })
    });
    target.setAttribute("aria-busy", "false");
    if (!response.ok) {
      this.dispatchEvent(new CustomEvent("cart:discount:error", { bubbles: true }));
      return;
    }
    const responseJson = await response.json();
    if (responseJson.discount_codes.some((obj) => obj.applicable === false)) {
      this.dispatchEvent(new CustomEvent("cart:discount:error", { bubbles: true }));
      return;
    } else {
      this.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }
    if (window.themeVariables.settings.pageType === "cart") {
      window.location.reload();
    }
  }
};
var _abortController2, _CartDiscountBanner_instances, onCartDiscountError_fn;
var CartDiscountBanner = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CartDiscountBanner_instances);
    __privateAdd(this, _abortController2);
  }
  connectedCallback() {
    __privateSet(this, _abortController2, new AbortController());
    document.addEventListener("cart:discount:error", __privateMethod(this, _CartDiscountBanner_instances, onCartDiscountError_fn).bind(this), { signal: __privateGet(this, _abortController2).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController2).abort();
  }
};
_abortController2 = new WeakMap();
_CartDiscountBanner_instances = new WeakSet();
onCartDiscountError_fn = function() {
  // Toggle the live region so repeated asynchronous failures are announced.
  this.hidden = true;
  requestAnimationFrame(() => {
    this.hidden = false;
  });
};
var _hiddenDiscountInputOriginalValue, _CartDiscountField_instances, hiddenDiscountInput_get, updateHiddenInput_fn;
var CartDiscountField = class extends AbstractCartDiscount {
  constructor() {
    super();
    __privateAdd(this, _CartDiscountField_instances);
    __privateAdd(this, _hiddenDiscountInputOriginalValue);
    __privateSet(this, _hiddenDiscountInputOriginalValue, __privateGet(this, _CartDiscountField_instances, hiddenDiscountInput_get).value);
    this.addEventListener("change", this.toggleDiscount.bind(this));
    this.addEventListener("input", __privateMethod(this, _CartDiscountField_instances, updateHiddenInput_fn));
  }
};
_hiddenDiscountInputOriginalValue = new WeakMap();
_CartDiscountField_instances = new WeakSet();
hiddenDiscountInput_get = function() {
  return this.querySelector('[name="discount"]');
};
updateHiddenInput_fn = function(event) {
  __privateGet(this, _CartDiscountField_instances, hiddenDiscountInput_get).value = [__privateGet(this, _hiddenDiscountInputOriginalValue), event.target.value].filter((val) => val && val.trim() !== "").join(",");
};
var CartDiscountRemoveButton = class extends AbstractCartDiscount {
  constructor() {
    super();
    this.addEventListener("click", this.toggleDiscount.bind(this));
  }
};
if (!window.customElements.get("cart-discount-field")) {
  window.customElements.define("cart-discount-field", CartDiscountField);
}
if (!window.customElements.get("cart-discount-remove-button")) {
  window.customElements.define("cart-discount-remove-button", CartDiscountRemoveButton);
}
if (!window.customElements.get("cart-discount-banner")) {
  window.customElements.define("cart-discount-banner", CartDiscountBanner);
}

// js/common/cart/cart-drawer.js
import { animate as animate4 } from "vendor";

// js/common/overlay/dialog-element.js
import { FocusTrap, Delegate } from "vendor";
var DialogElement = class _DialogElement extends HTMLElement {
  static get observedAttributes() {
    return ["id", "open"];
  }
  static #lockLayerCount = 0;
  #isLocked = false;
  constructor() {
    super();
    this.addEventListener("dialog:force-close", (event) => {
      this.hide();
      event.stopPropagation();
    });
  }
  connectedCallback() {
    if (this.id) {
      this.delegate.off().on("click", `[aria-controls="${this.id}"]`, this._onToggleClicked.bind(this));
    }
    this._abortController = new AbortController();
    this.setAttribute("role", "dialog");
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.show(!event.detail.load), { signal: this._abortController.signal });
      this.addEventListener("shopify:block:deselect", this.hide, { signal: this._abortController.signal });
      this._shopifySection = this._shopifySection || this.closest(".shopify-section");
      if (this._shopifySection) {
        if (this.hasAttribute("handle-section-events")) {
          this._shopifySection.addEventListener("shopify:section:select", (event) => this.show(!event.detail.load), { signal: this._abortController.signal });
          this._shopifySection.addEventListener("shopify:section:deselect", this.hide.bind(this), { signal: this._abortController.signal });
        }
        this._shopifySection.addEventListener("shopify:section:unload", () => this.remove(), { signal: this._abortController.signal });
      }
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
    this.delegate.off();
    this.focusTrap?.deactivate({ onDeactivate: () => {
    } });
    if (this.#isLocked) {
      this.#isLocked = false;
      document.documentElement.classList.toggle("lock", --_DialogElement.#lockLayerCount > 0);
    }
  }
  show(animate19 = true) {
    if (this.open) {
      return;
    }
    this.setAttribute("open", animate19 ? "" : "immediate");
    return waitForEvent(this, "dialog:after-show");
  }
  hide() {
    if (!this.open) {
      return;
    }
    this.removeAttribute("open");
    return waitForEvent(this, "dialog:after-hide");
  }
  get delegate() {
    return this._delegate = this._delegate || new Delegate(document.body);
  }
  get controls() {
    return Array.from(this.getRootNode().querySelectorAll(`[aria-controls="${this.id}"]`));
  }
  get open() {
    return this.hasAttribute("open");
  }
  get shouldTrapFocus() {
    return true;
  }
  get shouldLock() {
    return false;
  }
  get shouldHideOnFocusTrapDeactivate() {
    return true;
  }
  /**
   * Sometimes (especially for drawer) we need to ensure that an element is on top of everything else. To do that,
   * we need to move the element to the body. We are doing that on open, and then restore the initial position on
   * close
   */
  get shouldAppendToBody() {
    return false;
  }
  get initialFocus() {
    return this.hasAttribute("initial-focus") ? this.getAttribute("initial-focus") : this.hasAttribute("tabindex") ? this : this.querySelector('input:not([type="hidden"])') || false;
  }
  get preventScrollWhenTrapped() {
    return true;
  }
  get focusTrap() {
    return this._focusTrap = this._focusTrap || new FocusTrap.createFocusTrap(this, {
      onDeactivate: () => {
        if (!this._ignoreFocusTrapDeactivate && this.shouldHideOnFocusTrapDeactivate) {
          this.hide();
        }
      },
      allowOutsideClick: this._allowOutsideClick.bind(this),
      initialFocus: window.matchMedia("screen and (pointer: fine)").matches ? this.initialFocus : void 0,
      fallbackFocus: this,
      tabbableOptions: {
        getShadowRoot: true
      },
      preventScroll: this.preventScrollWhenTrapped
    });
  }
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "id":
        if (newValue) {
          this.delegate.off().on("click", `[aria-controls="${this.id}"]`, this._onToggleClicked.bind(this));
        }
        break;
      case "open":
        this.controls.forEach((toggle) => toggle.setAttribute("aria-expanded", newValue === null ? "false" : "true"));
        if (oldValue === null && (newValue === "" || newValue === "immediate")) {
          this.removeAttribute("inert");
          this._originalParentBeforeAppend = null;
          if (this.shouldAppendToBody && this.parentElement !== document.body) {
            this._originalParentBeforeAppend = this.parentElement;
            document.body.append(this);
          }
          const showTransitionPromise = this._showTransition(newValue !== "immediate") || Promise.resolve();
          showTransitionPromise.then(() => {
            this.dispatchEvent(new CustomEvent("dialog:after-show", { bubbles: true }));
          });
          if (this.shouldTrapFocus) {
            this.focusTrap.activate({
              checkCanFocusTrap: () => showTransitionPromise
            });
          }
          if (this.shouldLock) {
            _DialogElement.#lockLayerCount += 1;
            this.#isLocked = true;
            document.documentElement.classList.add("lock");
          }
        } else if (oldValue !== null && newValue === null) {
          this.setAttribute("inert", "");
          const hideTransitionPromise = this._hideTransition() || Promise.resolve();
          hideTransitionPromise.then(() => {
            if (this.parentElement === document.body && this._originalParentBeforeAppend) {
              this._originalParentBeforeAppend.appendChild(this);
              this._originalParentBeforeAppend = null;
            }
            this.dispatchEvent(new CustomEvent("dialog:after-hide", { bubbles: true }));
          });
          this.focusTrap?.deactivate({
            checkCanReturnFocus: () => hideTransitionPromise
          });
          if (this.shouldLock) {
            this.#isLocked = false;
            document.documentElement.classList.toggle("lock", --_DialogElement.#lockLayerCount > 0);
          }
        }
        this.dispatchEvent(new CustomEvent("toggle", { bubbles: true }));
        break;
    }
  }
  /* Those methods are used to perform custom show/hide transition, and must return a promise */
  _showTransition(animate19 = true) {
  }
  _hideTransition() {
  }
  /* By default, a focus element is deactivated when you click outside it */
  _allowOutsideClick(event) {
    if ("TouchEvent" in window && event instanceof TouchEvent) {
      return this._allowOutsideClickTouch(event);
    } else {
      return this._allowOutsideClickMouse(event);
    }
  }
  _allowOutsideClickTouch(event) {
    event.target.addEventListener("touchend", (subEvent) => {
      const endTarget = document.elementFromPoint(subEvent.changedTouches.item(0).clientX, subEvent.changedTouches.item(0).clientY);
      if (!this.contains(endTarget)) {
        this.hide();
      }
    }, { once: true });
    return false;
  }
  _allowOutsideClickMouse(event) {
    if (event.type !== "click") {
      return false;
    }
    if (!this.contains(event.target)) {
      this.hide();
    }
    let target = event.target, closestControl = event.target.closest("[aria-controls]");
    if (closestControl && closestControl.getAttribute("aria-controls") === this.id) {
      target = closestControl;
    }
    return this.id !== target.getAttribute("aria-controls");
  }
  _onToggleClicked(event) {
    event?.preventDefault();
    this.open ? this.hide() : this.show();
  }
};
var CloseButton = class extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("dialog:force-close", { bubbles: true, cancelable: true, composed: true })));
  }
};
if (!window.customElements.get("dialog-element")) {
  window.customElements.define("dialog-element", DialogElement);
}
if (!window.customElements.get("close-button")) {
  window.customElements.define("close-button", CloseButton, { extends: "button" });
}

// js/common/overlay/drawer.js
import { animate as motionAnimate, timeline as motionTimeline } from "vendor";
var reduceDrawerAnimation = window.matchMedia("(prefers-reduced-motion: reduce)").matches || window.themeVariables.settings.reduceDrawerAnimation;
var Drawer = class extends DialogElement {
  constructor() {
    super();
    const template2 = document.getElementById(this.template).content.cloneNode(true);
    this.attachShadow({ mode: "open" }).appendChild(template2);
    this.shadowRoot.addEventListener("slotchange", (event) => this._updateSlotVisibility(event.target));
  }
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("aria-modal", "true");
    this.shadowRoot.querySelector('[part="overlay"]')?.addEventListener("click", this.hide.bind(this), { signal: this._abortController.signal });
    Array.from(this.shadowRoot.querySelectorAll("slot")).forEach((slot) => this._updateSlotVisibility(slot));
  }
  get template() {
    return this.getAttribute("template") || "drawer-default-template";
  }
  get shouldLock() {
    return true;
  }
  get shouldAppendToBody() {
    return true;
  }
  get openFrom() {
    return window.matchMedia(`${window.themeVariables.breakpoints["sm-max"]}`).matches ? "bottom" : this.getAttribute("open-from") || "right";
  }
  _getClipPathProperties() {
    switch (this.openFrom) {
      case "left":
        return document.dir === "ltr" ? ["inset(0 100% 0 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"] : ["inset(0 0 0 100% round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
      case "right":
        return document.dir === "ltr" ? ["inset(0 0 0 100% round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm)"] : ["inset(0 100% 0 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
      case "bottom":
        return ["inset(100% 0 0 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
      case "top":
        return ["inset(0 0 100% 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
    }
  }
  _setInitialPosition() {
    this.style.left = document.dir === "ltr" && this.openFrom === "left" || document.dir === "rtl" && this.openFrom === "right" ? "0px" : "auto";
    this.style.right = this.style.left === "auto" ? "0px" : "auto";
    this.style.bottom = this.openFrom === "bottom" ? "0px" : null;
    this.style.top = this.style.bottom === "" ? "0px" : null;
  }
  _showTransition(animate19 = true) {
    let animationControls;
    this._setInitialPosition();
    if (reduceDrawerAnimation) {
      animationControls = motionAnimate(this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.2 });
    } else {
      let content = this.shadowRoot.querySelector('[part="content"]'), closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');
      animationControls = motionTimeline([
        [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
        [content, { clipPath: this._getClipPathProperties() }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
        [content.children, { opacity: [0, 1] }, { duration: 0.15 }],
        [closeButton, { opacity: [0, 1] }, { at: "<", duration: 0.15 }]
      ]);
    }
    animate19 ? animationControls.play() : animationControls.finish();
    return animationControls.finished.then(() => this.classList.add("show-close-cursor"));
  }
  _hideTransition() {
    let animationControls;
    if (reduceDrawerAnimation) {
      animationControls = motionAnimate(this, { opacity: [1, 0], visibility: ["visibility", "hidden"] }, { duration: 0.2 });
    } else {
      let content = this.shadowRoot.querySelector('[part="content"]'), closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');
      animationControls = motionTimeline([
        [closeButton, { opacity: [null, 0] }, { duration: 0.15 }],
        [content.children, { opacity: [null, 0] }, { at: "<", duration: 0.15 }],
        [content, { clipPath: this._getClipPathProperties().reverse() }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
        [this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 }]
      ]);
    }
    return animationControls.finished.then(() => this.classList.remove("show-close-cursor"));
  }
  _updateSlotVisibility(slot) {
    if (!["header", "footer", "body"].includes(slot.name)) {
      return;
    }
    slot.parentElement.hidden = slot.assignedElements({ flatten: true }).length === 0;
  }
};
if (!window.customElements.get("x-drawer")) {
  window.customElements.define("x-drawer", Drawer);
}

// js/common/overlay/popover.js
import { animate as motionAnimate2, timeline as motionTimeline2 } from "vendor";
var Popover = class extends DialogElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(document.getElementById(this.template).content.cloneNode(true));
  }
  connectedCallback() {
    super.connectedCallback();
    this.shadowRoot.querySelector('[part="overlay"]')?.addEventListener("click", this.hide.bind(this), { signal: this._abortController.signal });
    this.controls.forEach((control) => control.setAttribute("aria-haspopup", "dialog"));
    if (this.hasAttribute("close-on-listbox-select")) {
      if (this.querySelector("x-listbox")) {
        this.addEventListener("listbox:select", this.hide, { signal: this._abortController.signal });
      } else {
        this.addEventListener("change", this.hide, { signal: this._abortController.signal });
      }
    }
  }
  get template() {
    return this.getAttribute("template") || "popover-default-template";
  }
  get shouldLock() {
    return window.matchMedia("screen and (max-width: 999px)").matches;
  }
  get shouldAppendToBody() {
    return window.matchMedia("screen and (max-width: 999px)").matches;
  }
  get anchor() {
    return {
      vertical: this.getAttribute("anchor-vertical") || "start",
      horizontal: this.getAttribute("anchor-horizontal") || "end"
    };
  }
  _showTransition(animate19 = true) {
    let animationControls, content = this.shadowRoot.querySelector('[part="content"]'), closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');
    this.style.display = "block";
    if (window.matchMedia("screen and (max-width: 999px)").matches) {
      this.style.insetInlineStart = "0px";
      this.style.insetInlineEnd = null;
      this.style.insetBlockEnd = "0px";
      this.style.insetBlockStart = null;
      animationControls = motionTimeline2([
        [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
        [content, { clipPath: ["inset(100% 0 0 0 round 8px)", "inset(0 0 0 0 round 8px"] }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
        [content.children, { opacity: [0, 1] }, { duration: 0.15 }],
        [closeButton, { opacity: [0, 1] }, { at: "<", duration: 0.15 }]
      ]);
    } else {
      let spacingBlockValue = "var(--popover-anchor-block-spacing)", spacingInlineValue = "var(--popover-anchor-inline-spacing)";
      this.style.insetInlineStart = this.anchor.horizontal === "start" ? spacingInlineValue : null;
      this.style.insetInlineEnd = this.anchor.horizontal === "end" ? spacingInlineValue : null;
      if (this.anchor.vertical === "center") {
        this.style.insetBlockStart = `calc(50% - ${parseInt(this.clientHeight / 2)}px)`;
        this.style.insetBlockEnd = null;
      } else {
        this.style.insetBlockStart = this.anchor.vertical === "end" ? `calc(100% + ${spacingBlockValue})` : null;
        this.style.insetBlockEnd = this.anchor.vertical === "start" ? `calc(100% + ${spacingBlockValue})` : null;
      }
      animationControls = motionTimeline2([
        [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
        [content, { clipPath: "none" }, { at: "<", duration: 0 }],
        [content.children, { opacity: 1 }, { at: "<", duration: 0 }]
      ]);
    }
    animate19 ? animationControls.play() : animationControls.finish();
    return animationControls.finished;
  }
  _hideTransition() {
    let animationControls;
    if (window.matchMedia("screen and (max-width: 999px)").matches) {
      let content = this.shadowRoot.querySelector('[part="content"]'), closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');
      animationControls = motionTimeline2([
        [closeButton, { opacity: [null, 0] }, { duration: 0.15 }],
        [content.children, { opacity: [null, 0] }, { at: "<", duration: 0.15 }],
        [content, { clipPath: [null, "inset(100% 0 0 0 round 8px)"] }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
        [this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 }]
      ]);
    } else {
      animationControls = motionAnimate2(this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 });
    }
    return animationControls.finished.then(() => this.style.display = "none");
  }
};
if (!window.customElements.get("x-popover")) {
  window.customElements.define("x-popover", Popover);
}

// js/common/overlay/privacy-bar.js
import { Delegate as Delegate2 } from "vendor";
var PrivacyBar = class extends HTMLElement {
  constructor() {
    super();
    this._delegate = new Delegate2(this);
    window.Shopify.loadFeatures([{
      name: "consent-tracking-api",
      version: "0.1",
      onLoad: this._onConsentLibraryLoaded.bind(this)
    }]);
    if (Shopify.designMode) {
      const section = this.closest(".shopify-section");
      section.addEventListener("shopify:section:select", this.show.bind(this));
      section.addEventListener("shopify:section:deselect", this.hide.bind(this));
    }
  }
  connectedCallback() {
    this._delegate.on("click", '[data-action="accept"]', this._acceptPolicy.bind(this));
    this._delegate.on("click", '[data-action="decline"]', this._declinePolicy.bind(this));
    this._delegate.on("click", '[data-action="close"]', this.hide.bind(this));
  }
  disconnectedCallback() {
    this._delegate.off();
  }
  show() {
    this.hidden = false;
  }
  hide() {
    this.hidden = true;
  }
  _onConsentLibraryLoaded() {
    if (window.Shopify.customerPrivacy?.shouldShowBanner()) {
      this.show();
    }
  }
  _acceptPolicy() {
    window.Shopify.customerPrivacy?.setTrackingConsent(true, this.hide.bind(this));
  }
  _declinePolicy() {
    window.Shopify.customerPrivacy?.setTrackingConsent(false, this.hide.bind(this));
  }
};
if (!window.customElements.get("privacy-bar")) {
  window.customElements.define("privacy-bar", PrivacyBar);
}

// js/common/cart/cart-drawer.js
var CartDrawer = class extends Drawer {
  constructor() {
    super();
    this._onPrepareBundledSectionsListener = this._onPrepareBundledSections.bind(this);
    this._onCartChangedListener = this._onCartChanged.bind(this);
    this._onCartRefreshListener = this._onCartRefresh.bind(this);
    this._onVariantAddedListener = this._onVariantAdded.bind(this);
    this._onCartAddStartListener = this._onCartAddStart.bind(this);
    this._onProductFormTriggerClickedListener = this._onProductFormTriggerClicked.bind(this);
    this._onProductFormSubmittedListener = this._onProductFormSubmitted.bind(this);
    window.addEventListener("pageshow", this._onPageShow.bind(this));
  }
  connectedCallback() {
    super.connectedCallback();
    this._syncCheckoutAccessibleName({
      item_count: Number(this.getAttribute("data-codex-cart-item-count")),
      total_price: Number(this.getAttribute("data-codex-cart-total-price")),
      currency: window.Shopify?.currency?.active
    });
    document.addEventListener("cart:prepare-bundled-sections", this._onPrepareBundledSectionsListener);
    document.addEventListener("cart:change", this._onCartChangedListener);
    document.addEventListener("cart:refresh", this._onCartRefreshListener);
    document.addEventListener("variant:add", this._onVariantAddedListener);
    document.addEventListener("cart:add:start", this._onCartAddStartListener);
    document.addEventListener("click", this._onProductFormTriggerClickedListener, true);
    document.addEventListener("submit", this._onProductFormSubmittedListener, true);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this._cartRemovalNoticeTimer);
    this._cartRemovalNoticeTimer = null;
    document.removeEventListener("cart:prepare-bundled-sections", this._onPrepareBundledSectionsListener);
    document.removeEventListener("cart:change", this._onCartChangedListener);
    document.removeEventListener("cart:refresh", this._onCartRefreshListener);
    document.removeEventListener("variant:add", this._onVariantAddedListener);
    document.removeEventListener("cart:add:start", this._onCartAddStartListener);
    document.removeEventListener("click", this._onProductFormTriggerClickedListener, true);
    document.removeEventListener("submit", this._onProductFormSubmittedListener, true);
  }
  get shouldAppendToBody() {
    return false;
  }
  get openFrom() {
    return "right";
  }
  get focusTrap() {
    return this._focusTrap = this._focusTrap || new FocusTrap.createFocusTrap(this, {
      returnFocusOnDeactivate: false,
      onDeactivate: () => {
        if (!this._ignoreFocusTrapDeactivate && this.shouldHideOnFocusTrapDeactivate) {
          this.hide();
        }
      },
      allowOutsideClick: this._allowOutsideClick.bind(this),
      initialFocus: this.initialFocus,
      onPostUnpause: () => this._onCartFocusTrapUnpaused(),
      fallbackFocus: this,
      tabbableOptions: {
        getShadowRoot: true
      },
      preventScroll: this.preventScrollWhenTrapped
    });
  }
  _rememberReturnFocusTarget(target) {
    if (target instanceof HTMLElement && target.isConnected && !target.matches("[disabled]")) {
      this._cartReturnFocusTarget = target;
    }
  }
  _isExternalCartAddTarget(target, form = target?.form) {
    return window.themeVariables.settings.cartType === "drawer" && target instanceof HTMLElement && form instanceof HTMLFormElement && target.form === form && form.matches('form[action*="/cart/add"], form.shopify-product-form') && !this.contains(target) && !form.closest("cart-drawer, quick-buy-drawer");
  }
  _onCartAddStart(event) {
    const { submitter, form } = event.detail || {};
    if (submitter instanceof HTMLElement && this.contains(submitter)) {
      this._cartInternalAdd = { submitter, dismissed: false };
    }
    if (event.detail?.externalCartAdd === true && submitter instanceof HTMLElement && submitter.isConnected && !this.contains(submitter)) {
      this._rememberReturnFocusTarget(submitter);
      return;
    }
    if (!this._isExternalCartAddTarget(submitter, form)) {
      return;
    }
    this._rememberReturnFocusTarget(submitter);
  }
  _onProductFormTriggerClicked(event) {
    const trigger = event.target instanceof Element ? event.target.closest('[type="submit"]') : null;
    if (!this._isExternalCartAddTarget(trigger)) {
      return;
    }
    this._rememberReturnFocusTarget(trigger);
  }
  _onProductFormSubmitted(event) {
    if (!this._isExternalCartAddTarget(event.submitter, event.target)) {
      return;
    }
    this._rememberReturnFocusTarget(event.submitter);
  }
  _onToggleClicked(event) {
    const trigger = event.target instanceof Element ? event.target.closest(`[aria-controls="${this.id}"]`) : null;
    this._rememberReturnFocusTarget(trigger);
    return super._onToggleClicked(event);
  }
  _restoreReturnFocusTarget(target = this._cartReturnFocusTarget) {
    const focusTarget = target instanceof HTMLElement && target.isConnected && !target.matches("[disabled]")
      ? target
      : this.controls.find((control) => control instanceof HTMLElement && control.isConnected && !control.matches("[disabled]") && control.getClientRects().length > 0);

    if (!(focusTarget instanceof HTMLElement)) {
      return false;
    }

    // Product-card Quick Add is intentionally hidden on desktop until its card
    // is hovered or contains focus. Reveal that one wrapper before focusing it
    // so the browser does not reject focus on a visibility:hidden descendant.
    const quickBuyWrapper = focusTarget.closest(".product-card__quick-buy");
    quickBuyWrapper?.classList.add("codex-focus-return");
    focusTarget.focus({ preventScroll: true });
    const restored = document.activeElement === focusTarget;
    if (restored) {
      requestAnimationFrame(() => {
        quickBuyWrapper?.classList.remove("codex-focus-return");
      });
    } else {
      quickBuyWrapper?.classList.remove("codex-focus-return");
    }
    return restored;
  }
  _onPrepareBundledSections(event) {
    event.detail.sections.push(extractSectionId(this));
  }
  /**
   * Update the cart drawer content when cart content changes.
   */
  async _onCartChanged(event) {
    if (event.detail?.skipCartDrawerUpdate) {
      this._syncCheckoutAccessibleName(event.detail?.cart);
      if (event.detail?.baseEvent === "variant:add") {
        this._announcePendingAddedToCart(event.detail);
      }
      return;
    }
    const sectionHTML = event.detail.cart?.["sections"]?.[extractSectionId(this)];
    if (!sectionHTML) {
      return;
    }
    if (event.detail?.baseEvent === "line-item:change") {
      // A completed add confirmation is historical context. Clear it before
      // the quantity/removal update publishes its own current status so stale
      // totals do not remain alongside the authoritative update.
      this._clearAddedToCartAnnouncement();
      this._setCartUpdateBusy(true);
    }
    this._replaceFromSectionHTML(sectionHTML, event.detail);
  }
  _pauseCartFocusTrap() {
    if (!this.open || this._cartFocusTrapPaused || !this.focusTrap?.active || this.focusTrap.paused) {
      return false;
    }
    this._cartFocusTrapPaused = true;
    this.focusTrap.pause();
    return true;
  }
  _resumeCartFocusTrap(focusTarget = null, canRestore = () => true, onReady = () => {}) {
    if (!this._cartFocusTrapPaused) {
      return false;
    }
    this._cartFocusTrapPaused = false;
    // Resume containment without the library's focus-and-select input path.
    // The shared post-unpause callback also handles a nested dialog delaying
    // this trap's resume until that newer dialog closes.
    this._cartFocusTrapSuppressInitialFocus = true;
    this._cartFocusTrapRestore = { focusTarget, canRestore, onReady };
    this.focusTrap?.unpause();
    return true;
  }
  _onCartFocusTrapUnpaused() {
    const restoration = this._cartFocusTrapRestore;
    this._cartFocusTrapRestore = null;
    this._cartFocusTrapSuppressInitialFocus = false;
    if (!restoration) {
      return;
    }
    const { focusTarget, canRestore, onReady } = restoration;
    if (this.open && focusTarget instanceof HTMLElement && focusTarget.isConnected && this.contains(focusTarget) && canRestore()) {
      focusTarget.focus({ preventScroll: true });
    }
    onReady();
  }
  _setCartUpdateBusy(isBusy) {
    if (isBusy) {
      // Keep the dialog itself quiet; VoiceOver announces aria-busy on role=dialog
      // as “Website dialogue busy.” Scope the state to the content being replaced.
      this.removeAttribute("aria-busy");
      const boundary = this.querySelector(".cart-drawer__line-items, .cart-drawer__inner, .empty-state");
      if (boundary instanceof HTMLElement) {
        this._cartUpdateBusyBoundary = boundary;
        boundary.setAttribute("aria-busy", "true");
      }
      return;
    }

    this._cartUpdateBusyBoundary?.removeAttribute("aria-busy");
    this._cartUpdateBusyBoundary = null;
    this.querySelector(".cart-drawer__line-items, .cart-drawer__inner, .empty-state")?.removeAttribute("aria-busy");
    this.removeAttribute("aria-busy");
  }
  _captureFocusState() {
    const activeElement = document.activeElement;

    if (!(activeElement instanceof HTMLElement) || !this.contains(activeElement)) {
      return null;
    }

    if (activeElement.matches("[data-cart-drawer-close]")) {
      return { type: "close" };
    }

    const lineItem = activeElement.closest("line-item");
    if (lineItem && this.contains(lineItem)) {
      const lineKeyElement = lineItem.querySelector("[data-line-key]");
      const lineKey = lineKeyElement?.getAttribute("data-line-key");

      if (lineKey) {
        let control = "title";
        if (activeElement.matches(".line-item__remove-button")) {
          control = "remove";
        } else if (activeElement.matches(".codex-cart-quantity__input")) {
          control = "quantity-input";
        } else if (activeElement.matches(".codex-cart-quantity__button")) {
          control = activeElement === lineItem.querySelector(".codex-cart-quantity__button:first-of-type") ? "quantity-decrease" : "quantity-increase";
        }
        return { type: "line-item", lineKey, control };
      }
    }

    const specialOfferForm = activeElement.closest("[data-cart-special-offer-cta]");
    if (specialOfferForm && this.contains(specialOfferForm)) {
      return { type: "special-offer", offer: specialOfferForm.getAttribute("data-cart-special-offer-cta") };
    }
    const specialOfferInformation = activeElement.closest(".cart-special-offer__information");
    if (specialOfferInformation && this.contains(specialOfferInformation)) {
      return { type: "special-offer-information", offer: specialOfferInformation.closest("[data-cart-special-offer]")?.getAttribute("data-cart-special-offer") };
    }

    const recommendationCarousel = activeElement.closest("[data-codex-cart-recommendations]");
    if (recommendationCarousel && this.contains(recommendationCarousel)) {
      if (this.dataset.codexCartRecommendationQuickAdd === "true") {
        const quickAddIndex = Number(this.dataset.codexCartRecommendationQuickAddIndex);
        const quickAddHandle = this.dataset.codexCartRecommendationQuickAddHandle || "";
        delete this.dataset.codexCartRecommendationQuickAdd;
        delete this.dataset.codexCartRecommendationQuickAddIndex;
        delete this.dataset.codexCartRecommendationQuickAddHandle;
        delete this.dataset.codexCartRecommendationQuickAddCarouselId;
        clearTimeout(this._codexCartRecommendationQuickAddTimer);
        this._codexCartRecommendationQuickAddTimer = null;
        return {
          type: "recommendation-quick-add",
          carouselId: recommendationCarousel.id,
          handle: quickAddHandle,
          index: Number.isInteger(quickAddIndex) && quickAddIndex >= 0 ? quickAddIndex : 0
        };
      }
      const slides = Array.from(recommendationCarousel.querySelectorAll(".horizontal-product"));
      const focusedSlide = activeElement.closest(".horizontal-product");
      const selectedSlide = focusedSlide || slides.find((slide) => slide.classList.contains("is-selected"));
      const selectedIndex = selectedSlide ? slides.indexOf(selectedSlide) : Number(recommendationCarousel.dataset.codexCartRecommendationIndex);
      return {
        type: "recommendation-carousel",
        carouselId: recommendationCarousel.id,
        handle: selectedSlide?.getAttribute("data-product-handle") || "",
        index: Number.isInteger(selectedIndex) && selectedIndex >= 0 ? selectedIndex : 0
      };
    }

    if (activeElement.id) {
      return { type: "drawer-control", id: activeElement.id };
    }
    if (activeElement.matches('form.buy-buttons button[type="submit"]')) {
      return { type: "checkout" };
    }
    if (activeElement.hasAttribute("aria-controls")) {
      return { type: "drawer-control", controls: activeElement.getAttribute("aria-controls") };
    }
    return null;
  }
  _focusDrawerClose() {
    const closeButton = this.querySelector("[data-cart-drawer-close]");
    if (!(closeButton instanceof HTMLElement)) {
      return false;
    }
    if (document.activeElement === closeButton) {
      return true;
    }
    closeButton.focus({ preventScroll: true });
    return document.activeElement === closeButton;
  }
  get initialFocus() {
    return () => this._cartFocusTrapSuppressInitialFocus ? false : this.querySelector("[data-cart-drawer-close]") || false;
  }
  show(...args) {
    const wasOpen = this.open;
    const showPromise = super.show(...args);
    if (!wasOpen && showPromise) {
      Promise.resolve(showPromise).then(() => {
        const focusClose = () => {
          // External integrations can leave focus on BODY or on a removed
          // trigger while the drawer transition settles. Recover only while
          // focus is outside the newly opened dialog.
          if (this.open && !this.contains(document.activeElement)) {
            this._focusDrawerClose();
          }
        };
        focusClose();
        requestAnimationFrame(focusClose);
        requestAnimationFrame(() => requestAnimationFrame(focusClose));
      });
    }
    return showPromise;
  }
  _restoreFocus(focusState) {
    if (!focusState) {
      return false;
    }

    const focusTarget = this._getFocusTarget(focusState);

    if (!(focusTarget instanceof HTMLElement)) {
      return false;
    }

    this._cartUpdateFocusTarget = focusTarget;
    if (focusTarget.matches(".codex-cart-quantity__input")) {
      // Defer selecting the replacement value until the user begins editing.
      // This keeps the control editable without making VoiceOver announce the
      // same value again after the status message.
      focusTarget.dataset.codexSelectOnInput = "true";
    }
    focusTarget.focus({ preventScroll: true });
    if (document.activeElement !== focusTarget && focusTarget.matches(".codex-cart-quantity__input")) {
      delete focusTarget.dataset.codexSelectOnInput;
    }
    return document.activeElement === focusTarget;
  }
  _getFocusTarget(focusState) {
    if (!focusState) {
      return null;
    }

    let focusTarget = null;

    if (focusState.type === "close") {
      focusTarget = this.querySelector("[data-cart-drawer-close]");
    } else if (focusState.type === "checkout") {
      focusTarget = this.querySelector('form.buy-buttons button[type="submit"]');
    } else if (focusState.type === "special-offer") {
      focusTarget = this.querySelector("[data-cart-special-offer-recommendations] [data-codex-cart-recommendations]")
        || this.querySelector(".cart-special-offer__information, [data-codex-cart-recommendations], [slot=\"footer\"] button[aria-controls=\"cart-drawer-note\"], form.buy-buttons button[type=\"submit\"]");
    } else if (focusState.type === "special-offer-information") {
      const offerCard = Array.from(this.querySelectorAll("[data-cart-special-offer]")).find((candidate) => candidate.getAttribute("data-cart-special-offer") === focusState.offer);
      focusTarget = offerCard?.querySelector(".cart-special-offer__information") || this.querySelector(".cart-special-offer__information, [data-codex-cart-recommendations]");
    } else if (focusState.type === "drawer-control") {
      focusTarget = focusState.id
        ? this.querySelector(`#${CSS.escape(focusState.id)}`)
        : this.querySelector(`[aria-controls="${CSS.escape(focusState.controls)}"]`);
    } else if (focusState.type === "line-item") {
      const lineItem = Array.from(this.querySelectorAll("line-item")).find((candidate) => candidate.querySelector("[data-line-key]")?.getAttribute("data-line-key") === focusState.lineKey);

      if (lineItem) {
        switch (focusState.control) {
          case "remove":
            focusTarget = lineItem.querySelector(".line-item__remove-button");
            break;
          case "quantity-input":
            focusTarget = lineItem.querySelector(".codex-cart-quantity__input");
            break;
          case "quantity-decrease":
            focusTarget = lineItem.querySelector(".codex-cart-quantity__button:first-of-type");
            break;
          case "quantity-increase":
            focusTarget = lineItem.querySelector(".codex-cart-quantity__button:last-of-type");
            break;
          default:
            focusTarget = lineItem.querySelector("a[href], .line-item__title-row > p");
            break;
        }
      }

      if (!focusTarget) {
        focusTarget = this.querySelector("line-item a[href], line-item .line-item__title-row > p");
      }
    } else if (focusState.type === "recommendation-carousel") {
      const recommendationCarousel = (focusState.carouselId ? this.querySelector(`#${CSS.escape(focusState.carouselId)}`) : null) || this.querySelector("[data-codex-cart-recommendations]");
      if (recommendationCarousel) {
        const slides = Array.from(recommendationCarousel.querySelectorAll(".horizontal-product"));
        const matchingSlide = focusState.handle ? slides.find((candidate) => candidate.getAttribute("data-product-handle") === focusState.handle) : null;
        const targetSlide = matchingSlide || slides[Math.min(focusState.index || 0, Math.max(slides.length - 1, 0))];
        focusTarget = targetSlide || recommendationCarousel;
      } else {
        focusTarget = this.querySelector(".cart-special-offer__information");
      }
    } else if (focusState.type === "recommendation-quick-add") {
      const recommendationCarousel = (focusState.carouselId ? this.querySelector(`#${CSS.escape(focusState.carouselId)}`) : null) || this.querySelector("[data-codex-cart-recommendations]");
      if (recommendationCarousel) {
        const slides = Array.from(recommendationCarousel.querySelectorAll(".horizontal-product"));
        const targetIndex = Math.min(Math.max(Number(focusState.index) || 0, 0), Math.max(slides.length - 1, 0));
        focusTarget = slides[targetIndex] || recommendationCarousel;
      } else {
        focusTarget = this.querySelector('.cart-special-offer__information, [slot="footer"] button[aria-controls="cart-drawer-note"], form.buy-buttons button[type="submit"], form.buy-buttons a[href]');
      }
    }

    if (!(focusTarget instanceof HTMLElement)) {
      focusTarget = this.querySelector("[data-cart-drawer-close], line-item a[href], button:not([disabled]), a[href], input:not([disabled])");
    }
    return focusTarget;
  }
  _syncItemCount(updatedDrawer) {
    const updatedItemCount = updatedDrawer.querySelector("[data-cart-drawer-item-count]");
    const currentItemCount = this.querySelector("[data-cart-drawer-item-count]");

    if (updatedItemCount && currentItemCount) {
      currentItemCount.textContent = updatedItemCount.textContent;
    } else if (updatedItemCount && !currentItemCount) {
      this.prepend(updatedItemCount.cloneNode(true));
    }
  }
  _getFormattedCartTotal(cart) {
    const cartTotal = Number(cart?.total_price);
    if (!Number.isFinite(cartTotal)) {
      return "";
    }

    const currency = cart?.currency || window.Shopify?.currency?.active;
    if (!currency) {
      return "";
    }

    try {
      return new Intl.NumberFormat(window.Shopify?.locale, { style: "currency", currency }).format(cartTotal / 100);
    } catch (error) {
      return "";
    }
  }
  _syncCheckoutAccessibleName(cart, updatedDrawer = null) {
    const checkoutButton = this.querySelector("form.buy-buttons button[type=\"submit\"]");
    if (!(checkoutButton instanceof HTMLElement)) {
      return;
    }

    const updatedCheckoutButton = updatedDrawer?.querySelector?.("form.buy-buttons button[type=\"submit\"]");
    const cartItemCount = Number(cart?.item_count);
    const formattedTotal = this._getFormattedCartTotal(cart);
    if (Number.isFinite(cartItemCount) && formattedTotal) {
      this.setAttribute("data-codex-cart-item-count", String(cartItemCount));
      this.setAttribute("data-codex-cart-total-price", String(Number(cart.total_price)));
      const checkoutLabel = checkoutButton.textContent.replace(/\s+/g, " ").trim() || "Checkout";
      checkoutButton.setAttribute("aria-label", `${checkoutLabel} with ${cartItemCount} ${cartItemCount === 1 ? "product" : "products"} in your cart, totaling ${formattedTotal}.`);
      return;
    }

    const updatedLabel = updatedCheckoutButton?.getAttribute("aria-label");
    if (updatedLabel) {
      checkoutButton.setAttribute("aria-label", updatedLabel);
    }
  }
  _announceCartChange(eventDetail) {
    if (eventDetail?.baseEvent !== "line-item:change") {
      return;
    }

    const quantity = Number(eventDetail.quantity);
    if (!Number.isFinite(quantity)) {
      return;
    }

    if (quantity === 0) {
      return;
    }

    if (eventDetail?.announceQuantity === false) {
      return;
    }

    const title = eventDetail.lineTitle || "Item";
    const quantityMessage = (this.getAttribute("data-cart-drawer-quantity-message") || "__title__ quantity updated to __quantity__.").replace("__title__", title).replace("__quantity__", quantity);
    const summaryMessage = this._getCartSummaryMessage(eventDetail.cart);
    this._announceDrawerStatus([quantityMessage, summaryMessage].filter(Boolean).join(" "));
  }
  _getCartSummaryMessage(cart) {
    const cartItemCount = Number(cart?.item_count);
    const cartTotal = Number(cart?.total_price);
    if (!Number.isFinite(cartItemCount) || !Number.isFinite(cartTotal)) {
      return "";
    }

    const currency = cart?.currency || Shopify.currency?.active;
    let formattedTotal = "";
    if (currency) {
      try {
        formattedTotal = new Intl.NumberFormat(Shopify.locale, { style: "currency", currency }).format(cartTotal / 100);
      } catch (error) {
      }
    }
    if (!formattedTotal) {
      return "";
    }

    return (this.getAttribute("data-cart-drawer-summary-message") || "The total number of products in your cart is now __item_count__. The total value is __total_price__.").replace("__item_count__", cartItemCount).replace("__total_price__", formattedTotal);
  }
  _getAddedToCartMessage(event, cart = event.detail?.cart) {
    const addedItem = event.detail?.items?.[0];
    const title = addedItem?.product_title || addedItem?.title || "Item";
    const addedMessage = (this.getAttribute("data-cart-drawer-added-message") || "__title__ added to your cart.").replace("__title__", title);
    const punctuatedAddedMessage = /[.!?]\s*$/.test(addedMessage) ? addedMessage : `${addedMessage}.`;
    return [punctuatedAddedMessage, this._getCartSummaryMessage(cart)].filter(Boolean).join(" ");
  }
  _announcePendingAddedToCart(eventDetail) {
    const pendingAnnouncement = this._pendingAddedToCartAnnouncement;
    if (!pendingAnnouncement) {
      return false;
    }

    const summaryMessage = this._getCartSummaryMessage(eventDetail?.cart);
    if (summaryMessage) {
      const event = eventDetail?.items?.length > 0 ? { detail: eventDetail } : pendingAnnouncement.event;
      pendingAnnouncement.message = this._getAddedToCartMessage(event, eventDetail.cart);
    }
    if (!pendingAnnouncement.message) {
      return false;
    }
    if (!pendingAnnouncement.drawerReady || !pendingAnnouncement.focusReady) {
      return true;
    }

    clearTimeout(pendingAnnouncement.fallbackTimer);
    this._pendingAddedToCartAnnouncement = null;
    this._announceAddedToCart(pendingAnnouncement.message);
    return true;
  }
  _announceAddedToCart(message) {
    const announcement = this.querySelector("#cart-drawer-added-announcement");
    if (!(announcement instanceof HTMLElement)) {
      return;
    }
    clearTimeout(this._addedAnnouncementTimer);
    announcement.textContent = "";
    this._addedAnnouncementTimer = window.setTimeout(() => {
      if (this.open && announcement.isConnected) {
        announcement.textContent = message;
      }
    }, 200);
  }
  _clearAddedToCartAnnouncement() {
    clearTimeout(this._addedAnnouncementTimer);
    clearTimeout(this._pendingAddedToCartAnnouncement?.fallbackTimer);
    this._pendingAddedToCartAnnouncement = null;
    const announcement = this.querySelector("#cart-drawer-added-announcement");
    if (announcement instanceof HTMLElement) {
      announcement.textContent = "";
    }
  }
  _announceDrawerStatus(message) {
    const announcement = this.querySelector("#cart-drawer-status-announcement");
    if (!(announcement instanceof HTMLElement)) {
      return;
    }
    clearTimeout(this._drawerStatusAnnouncementTimer);
    announcement.textContent = "";
    this._drawerStatusAnnouncementTimer = window.setTimeout(() => {
      if (this.open && announcement.isConnected) {
        announcement.textContent = message;
      }
    }, 250);
  }
  _clearDrawerStatus() {
    clearTimeout(this._drawerStatusAnnouncementTimer);
    const announcement = this.querySelector("#cart-drawer-status-announcement");
    if (announcement instanceof HTMLElement) {
      announcement.textContent = "";
    }
  }
  _clearRemovedItemNotice() {
    clearTimeout(this._cartRemovalNoticeTimer);
    this._cartRemovalNoticeTimer = null;
    const notice = this.querySelector("[data-codex-cart-removal-notice]");
    if (notice instanceof HTMLElement) {
      notice.hidden = true;
    }
  }
  _showRemovedItemNotice(eventDetail, shouldFocus = false) {
    const notice = this.querySelector("[data-codex-cart-removal-notice]");
    const link = notice?.querySelector("[data-codex-cart-removal-link]");
    if (!(notice instanceof HTMLElement) || !(link instanceof HTMLAnchorElement)) {
      return false;
    }

    const title = eventDetail?.lineTitle || "Item";
    const removedMessage = (this.getAttribute("data-cart-drawer-removed-message") || "__title__ was removed from your cart.").replace("__title__", title);
    link.textContent = title;
    link.setAttribute("aria-label", removedMessage);
    link.setAttribute("href", eventDetail?.lineUrl || "#");
    notice.hidden = false;
    clearTimeout(this._cartRemovalNoticeTimer);
    this._cartRemovalNoticeTimer = window.setTimeout(() => {
      if (!notice.isConnected) {
        return;
      }
      if (document.activeElement === link) {
        link.addEventListener("blur", () => this._clearRemovedItemNotice(), { once: true });
        return;
      }
      this._clearRemovedItemNotice();
    }, 10000);

    if (!shouldFocus) {
      return true;
    }
    link.focus({ preventScroll: true });
    return document.activeElement === link;
  }
  _finishDrawerUpdate(baseEvent, eventDetail, focusState, updatedDrawer, focusGuard, updateVersion) {
    this._syncItemCount(updatedDrawer);
    this._syncCheckoutAccessibleName(eventDetail?.cart, updatedDrawer);
    const isRemoval = baseEvent === "line-item:change" && Number(eventDetail?.quantity) === 0;
    const nonPointerRemoval = isRemoval && eventDetail?.nonPointerActivation === true;
    const cartItemCount = Number(eventDetail?.cart?.item_count);
    const isEmptyAfterRemoval = isRemoval && (Number.isFinite(cartItemCount) ? cartItemCount === 0 : !updatedDrawer.querySelector(".cart-drawer__inner"));
    const restoreFocusAndAnnounce = () => {
      if (this._cartUpdateVersion !== updateVersion) {
        focusGuard.cancel();
        return;
      }
      const focusTarget = isEmptyAfterRemoval || isRemoval && !nonPointerRemoval
        ? this.querySelector("[data-cart-drawer-close]")
        : isRemoval
          ? null
          : this._getFocusTarget(focusState);
      this._cartUpdateFocusTarget = focusTarget instanceof HTMLElement ? focusTarget : null;
      if (focusState?.type === "recommendation-quick-add" && focusTarget?.closest?.("[data-codex-cart-recommendations]")) {
        this.dataset.codexCartRecommendationQuickAddExit = "true";
      } else {
        delete this.dataset.codexCartRecommendationQuickAddExit;
      }
      if (focusTarget instanceof HTMLElement && focusTarget.matches(".codex-cart-quantity__input")) {
        // Keep the replacement input editable without selecting it during the
        // focus handoff. Selection is restored only when the user starts typing.
        focusTarget.dataset.codexSelectOnInput = "true";
      }

      const canRestore = () => this.open && this._cartUpdateVersion === updateVersion && focusGuard.canRestore();
      const finishAnnouncement = () => requestAnimationFrame(() => {
        const shouldFocusRemoval = nonPointerRemoval && !isEmptyAfterRemoval && canRestore();
        focusGuard.cancel();
        if (this._cartUpdateVersion !== updateVersion) {
          return;
        }
        this._setCartUpdateBusy(false);
        if (isRemoval) {
          if (isEmptyAfterRemoval) {
            this._announceDrawerStatus(this.getAttribute("data-cart-drawer-empty-message") || "Your cart is empty.");
          } else if (nonPointerRemoval) {
            const noticeFocused = this._showRemovedItemNotice(eventDetail, shouldFocusRemoval);
            if (!noticeFocused && shouldFocusRemoval) {
              const title = eventDetail?.lineTitle || "Item";
              const removedMessage = (this.getAttribute("data-cart-drawer-removed-message") || "__title__ was removed from your cart.").replace("__title__", title);
              this._announceDrawerStatus(removedMessage);
            }
          } else {
            const title = eventDetail?.lineTitle || "Item";
            const removedMessage = (this.getAttribute("data-cart-drawer-removed-message") || "__title__ was removed from your cart.").replace("__title__", title);
            this._announceDrawerStatus(removedMessage);
          }
        } else {
          this._announceCartChange(eventDetail);
        }
        if (baseEvent === "variant:add") {
          if (this._pendingAddedToCartAnnouncement) {
            this._pendingAddedToCartAnnouncement.focusReady = true;
          }
          this._announcePendingAddedToCart(eventDetail);
        }
        this._cartUpdateFocusTarget = null;
      });
      // Publish feedback after the actual trap handoff, including its delayed
      // initial-focus step, rather than racing that step with another timer.
      if (!this._resumeCartFocusTrap(focusTarget, canRestore, finishAnnouncement)) {
        if ((isEmptyAfterRemoval || isRemoval && !nonPointerRemoval) && canRestore()) {
          focusTarget?.focus({ preventScroll: true });
        } else if (!isRemoval && canRestore()) {
          this._restoreFocus(focusState);
        }
        finishAnnouncement();
      }
    };
    if (isRemoval && !isEmptyAfterRemoval) {
      window.setTimeout(() => requestAnimationFrame(restoreFocusAndAnnounce), 220);
    } else {
      requestAnimationFrame(restoreFocusAndAnnounce);
    }
    this._dispatchDrawerUpdated(baseEvent);
  }
  hide(...args) {
    const wasOpen = this.open;
    const returnFocusTarget = this._cartReturnFocusTarget;
    if (this._cartInternalAdd) {
      this._cartInternalAdd.dismissed = true;
    }
    this._cartFocusRestoreGuard?.cancel();
    this._clearAddedToCartAnnouncement();
    this._clearRemovedItemNotice();
    if (this._cartFocusTrapPaused) {
      this._cartUpdateFocusTarget = null;
      this._resumeCartFocusTrap();
    }
    if (wasOpen) {
      announceCartDrawer(this.getAttribute("data-cart-drawer-closed-message") || "Cart drawer closed.");
    }
    const hidePromise = super.hide(...args);
    // Deactivation can cancel the trap's delayed initial-focus callback.
    // Discard that pending handoff so a later opening starts normally.
    this._cartFocusTrapRestore = null;
    this._cartFocusTrapSuppressInitialFocus = false;
    Promise.resolve(hidePromise).then(() => {
      this._setCartUpdateBusy(false);
      this._clearAddedToCartAnnouncement();
      this._clearDrawerStatus();
      if (wasOpen && !this.open) {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!this.open) {
            this._restoreReturnFocusTarget(returnFocusTarget);
          }
          if (this._cartReturnFocusTarget === returnFocusTarget) {
            this._cartReturnFocusTarget = null;
          }
        }));
      }
    });
    return hidePromise;
  }
  async _replaceFromSectionHTML(sectionHTML, eventDetailOrBaseEvent) {
    if (!sectionHTML) {
      return;
    }
    const eventDetail = typeof eventDetailOrBaseEvent === "object" && eventDetailOrBaseEvent !== null ? eventDetailOrBaseEvent : { baseEvent: eventDetailOrBaseEvent };
    const baseEvent = eventDetail.baseEvent;
    const updatedDrawerContent = new DOMParser().parseFromString(sectionHTML, "text/html"), updatedDrawer = updatedDrawerContent.querySelector(".cart-drawer");
    if (!updatedDrawer) {
      this._resumeCartFocusTrap();
      return;
    }
    const updateVersion = this._cartUpdateVersion = (this._cartUpdateVersion || 0) + 1;
    this._clearRemovedItemNotice();
    const previousFocusState = this._cartFocusRestoreGuard?.canRestore() ? this._cartUpdateFocusState : null;
    this._cartFocusRestoreGuard?.cancel();
    const prepareUpdate = () => {
      const activeFocusState = this._captureFocusState();
      const requestedFocusState = eventDetail.focusState;
      const focusState = requestedFocusState?.type === "special-offer"
        ? requestedFocusState
        : activeFocusState?.type === "recommendation-carousel" && requestedFocusState?.type === "recommendation-quick-add" && activeFocusState.handle === requestedFocusState.handle
        ? requestedFocusState
        : activeFocusState || (document.activeElement === document.body ? requestedFocusState || previousFocusState : null);
      const focusGuard = createFocusRestorationGuard();
      this._cartFocusRestoreGuard = focusGuard;
      this._cartUpdateFocusState = focusState;
      this._pauseCartFocusTrap();
      return { focusState, focusGuard };
    };
    const currentInner = this.querySelector(".cart-drawer__inner"), updatedInner = updatedDrawer.querySelector(".cart-drawer__inner");
    if (updatedInner) {
      if (!currentInner) {
        const { focusState, focusGuard } = prepareUpdate();
        this.replaceChildren(document.createRange().createContextualFragment(updatedDrawer.innerHTML));
        this._finishDrawerUpdate(baseEvent, eventDetail, focusState, updatedDrawer, focusGuard, updateVersion);
      } else {
        const updateDelay = baseEvent === "variant:add" || baseEvent === "line-item:change" ? 0 : 1250;
        const applyUpdate = () => {
          if (this._cartUpdateVersion !== updateVersion || !this.isConnected) {
            return;
          }
          const { focusState, focusGuard } = prepareUpdate();
          currentInner.innerHTML = updatedInner.innerHTML;
          const currentFooter = this.querySelector('[slot="footer"]'), updatedFooter = updatedDrawer.querySelector('[slot="footer"]');
          if (currentFooter && updatedFooter) {
            currentFooter.replaceChildren(...updatedFooter.childNodes);
          }
          this._finishDrawerUpdate(baseEvent, eventDetail, focusState, updatedDrawer, focusGuard, updateVersion);
        };
        if (updateDelay === 0) {
          applyUpdate();
        } else {
          setTimeout(applyUpdate, updateDelay);
        }
      }
    } else {
      await animate4(this.children, { opacity: 0 }, { duration: 0.15 }).finished;
      if (this._cartUpdateVersion !== updateVersion || !this.isConnected) {
        return;
      }
      const { focusState, focusGuard } = prepareUpdate();
      this.replaceChildren(...updatedDrawer.childNodes);
      const emptyState = this.querySelector(".empty-state");
      if (emptyState) {
        animate4(emptyState, { opacity: [0, 1], transform: ["translateY(20px)", "translateY(0)"] }, { duration: 0.15 });
      }
      this._finishDrawerUpdate(baseEvent, eventDetail, focusState, updatedDrawer, focusGuard, updateVersion);
    }
  }
  _dispatchDrawerUpdated(baseEvent) {
    document.dispatchEvent(new CustomEvent("cart:drawer-updated", {
      detail: {
        baseEvent
      }
    }));
  }
  /**
   * Handle the case when the page is served from BF cache
   */
  _onPageShow(event) {
    if (!event.persisted) {
      return;
    }
    this._onCartRefresh();
  }
  /**
   * Listeners called when a new variant has been added
   */
  _onVariantAdded(event) {
    if (window.themeVariables.settings.cartType !== "drawer" || event.detail?.blockCartDrawerOpening) {
      return;
    }
    const submitter = event.detail?.submitter;
    const keepClosed = this._cartInternalAdd?.dismissed && this._cartInternalAdd.submitter === submitter;
    this._cartInternalAdd = null;
    if (submitter instanceof HTMLElement && submitter.isConnected && !this.contains(submitter)) {
      this._rememberReturnFocusTarget(submitter);
    } else if (!this._cartReturnFocusTarget) {
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement && activeElement !== document.body && !this.contains(activeElement)) {
        this._rememberReturnFocusTarget(activeElement);
      }
    }
    const recommendationQuickAddPending = this.dataset.codexCartRecommendationQuickAdd === "true";
    const recommendationQuickAddIndex = Number(this.dataset.codexCartRecommendationQuickAddIndex);
    const recommendationQuickAddHandle = this.dataset.codexCartRecommendationQuickAddHandle || "";
    const recommendationQuickAddCarouselId = this.dataset.codexCartRecommendationQuickAddCarouselId || "";
    const recommendationQuickAddFocusState = recommendationQuickAddPending ? {
      type: "recommendation-quick-add",
      carouselId: recommendationQuickAddCarouselId,
      handle: recommendationQuickAddHandle,
      index: Number.isInteger(recommendationQuickAddIndex) && recommendationQuickAddIndex >= 0 ? recommendationQuickAddIndex : 0
    } : null;
    const specialOfferId = submitter instanceof HTMLElement ? submitter.closest("[data-cart-special-offer-cta]")?.getAttribute("data-cart-special-offer-cta") : null;
    const specialOfferFocusState = specialOfferId ? { type: "special-offer", offer: specialOfferId } : null;
    const shouldFocusClose = !recommendationQuickAddPending && !specialOfferFocusState && (!this.open || !this.contains(document.activeElement));
    const addedMessage = this._getAddedToCartMessage(event);
    clearTimeout(this._pendingAddedToCartAnnouncement?.fallbackTimer);
    const pendingAnnouncement = { event, fallbackTimer: 0, drawerReady: false, focusReady: !this.open, message: "" };
    this._pendingAddedToCartAnnouncement = pendingAnnouncement;
    const wasOpen = this.open;
    const sectionHTML = event.detail.cart?.["sections"]?.[extractSectionId(this)];
    if (!sectionHTML) {
      pendingAnnouncement.focusReady = true;
    }
    if (sectionHTML) {
      if (recommendationQuickAddPending) {
        delete this.dataset.codexCartRecommendationQuickAdd;
        delete this.dataset.codexCartRecommendationQuickAddIndex;
        delete this.dataset.codexCartRecommendationQuickAddHandle;
        delete this.dataset.codexCartRecommendationQuickAddCarouselId;
        clearTimeout(this._codexCartRecommendationQuickAddTimer);
        this._codexCartRecommendationQuickAddTimer = null;
      }
      event.detail.cartDrawerUpdated = true;
      this._replaceFromSectionHTML(sectionHTML, {
        baseEvent: "variant:add",
        ...(specialOfferFocusState ? { focusState: specialOfferFocusState } : {}),
        ...(recommendationQuickAddFocusState ? { focusState: recommendationQuickAddFocusState } : {})
      });
    }
    const showPromise = keepClosed ? Promise.resolve() : this.show();
    if (wasOpen && shouldFocusClose) {
      Promise.resolve(showPromise).then(() => this._focusDrawerClose());
    }
    Promise.resolve(showPromise).then(() => {
      if (this._pendingAddedToCartAnnouncement !== pendingAnnouncement) {
        return;
      }

      pendingAnnouncement.drawerReady = true;

      if (pendingAnnouncement.message || this._getCartSummaryMessage(event.detail?.cart)) {
        this._announcePendingAddedToCart(event.detail);
        return;
      }

      pendingAnnouncement.fallbackTimer = window.setTimeout(() => {
        if (this._pendingAddedToCartAnnouncement !== pendingAnnouncement) {
          return;
        }
        if (this.open) {
          pendingAnnouncement.message ||= addedMessage;
          this._announcePendingAddedToCart(event.detail);
        }
      }, 1500);
    });
  }
  /**
   * Force a complete refresh of the cart drawer (this is called by dispatching the 'cart:refresh' on the document)
   */
  async _onCartRefresh(event) {
    const shouldOpenAfterRefresh = event?.detail?.openCartDrawerOnAdd === true;
    const cartAddResponse = event?.detail?.cartAddResponse;
    const sectionHTML = cartAddResponse?.["sections"]?.[extractSectionId(this)];
    const shouldAnnounceExternalAdd = shouldOpenAfterRefresh && cartAddResponse && typeof cartAddResponse === "object";
    const externalAddDetail = shouldAnnounceExternalAdd ? {
      baseEvent: "variant:add",
      items: Array.isArray(cartAddResponse.items)
        ? cartAddResponse.items
        : cartAddResponse.id
          ? [cartAddResponse]
          : [],
      cart: cartAddResponse
    } : null;

    if (shouldAnnounceExternalAdd) {
      clearTimeout(this._pendingAddedToCartAnnouncement?.fallbackTimer);
      this._pendingAddedToCartAnnouncement = {
        event: { detail: externalAddDetail },
        fallbackTimer: 0,
        drawerReady: this.open,
        focusReady: true,
        message: this._getAddedToCartMessage({ detail: externalAddDetail })
      };
    }

    const finishExternalAnnouncement = () => {
      const pendingAnnouncement = this._pendingAddedToCartAnnouncement;
      if (!pendingAnnouncement || pendingAnnouncement.event.detail !== externalAddDetail) {
        return;
      }
      pendingAnnouncement.drawerReady = true;
      this._announcePendingAddedToCart(externalAddDetail);
    };

    if (sectionHTML) {
      await this._replaceFromSectionHTML(sectionHTML, externalAddDetail || { baseEvent: "cart:refresh" });
      if (shouldOpenAfterRefresh) {
        const showPromise = this.show();
        if (showPromise) {
          Promise.resolve(showPromise).then(finishExternalAnnouncement);
        } else {
          finishExternalAnnouncement();
        }
      }
      return;
    }
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = await (await fetch(`${window.Shopify.routes.root}?section_id=${extractSectionId(this)}`)).text();
    const updatedDrawer = tempDiv.querySelector("#cart-drawer");
    if (!updatedDrawer) {
      return;
    }
    if (shouldOpenAfterRefresh) {
      await this._replaceFromSectionHTML(updatedDrawer.outerHTML, externalAddDetail || { baseEvent: "cart:refresh" });
      const showPromise = this.show();
      if (showPromise) {
        Promise.resolve(showPromise).then(finishExternalAnnouncement);
      } else {
        finishExternalAnnouncement();
      }
    } else {
      this.replaceChildren(...updatedDrawer.children);
      this._dispatchDrawerUpdated("cart:refresh");
    }
  }
};
var CartNotificationDrawer = class extends Drawer {
  constructor() {
    super();
    this._onVariantAddedListener = this._onVariantAdded.bind(this);
  }
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("variant:add", this._onVariantAddedListener);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("variant:add", this._onVariantAddedListener);
  }
  async show() {
    this.style.display = "block";
    return super.show();
  }
  async hide() {
    super.hide()?.then(() => {
      this.style.display = "none";
    });
  }
  _onVariantAdded(event) {
    if (window.themeVariables.settings.cartType !== "popover" || event.detail?.blockCartDrawerOpening) {
      return;
    }
    const tempContent = document.createElement("div");
    tempContent.innerHTML = event.detail.cart["sections"]["variant-added"];
    this.replaceChildren(...tempContent.querySelector(".shopify-section").children);
    this.show();
  }
};
var LineItem = class extends HTMLElement {
  connectedCallback() {
    this.pillLoaderElement = this.querySelector("pill-loader");
    this.addEventListener("line-item:will-change", this._onWillChange.bind(this));
    this.addEventListener("line-item:error", this._onErrored.bind(this));
    this.addEventListener("line-item:change", this._onChanged.bind(this));
  }
  _onWillChange() {
    this.pillLoaderElement.setAttribute("aria-busy", "true");
  }
  _onErrored() {
    this.pillLoaderElement.removeAttribute("aria-busy");
  }
  async _onChanged(event) {
    this.pillLoaderElement.removeAttribute("aria-busy");
    if (event.detail.cart["item_count"] === 0 || event.detail.quantity !== 0) {
      return;
    }
    let marginCompensation = 0;
    if (this.nextElementSibling) {
      marginCompensation = `-${getComputedStyle(this.nextElementSibling).paddingTop}`;
    }
    await animate4(this, { height: [`${this.clientHeight}px`, 0], marginBottom: [0, marginCompensation], overflow: "hidden", opacity: [1, 0] }, { duration: 0.2, easing: "ease" }).finished;
    this.remove();
  }
};
if (!window.customElements.get("cart-drawer")) {
  window.customElements.define("cart-drawer", CartDrawer);
}
if (!window.customElements.get("cart-notification-drawer")) {
  window.customElements.define("cart-notification-drawer", CartNotificationDrawer);
}
if (!window.customElements.get("line-item")) {
  window.customElements.define("line-item", LineItem);
}

// js/common/cart/cart-note.js
var CartNote = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", this._onNoteChanged);
  }
  _onNoteChanged(event) {
    if (event.target.getAttribute("name") !== "note") {
      return;
    }
    fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: event.target.value }),
      keepalive: true
      // Allows to make sure the request is fired even when submitting the form
    });
  }
};
var CartNoteDialog = class extends DialogElement {
  constructor() {
    super();
    this.addEventListener("change", this._onNoteChanged);
  }
  _onNoteChanged(event) {
    if (event.target.value === "") {
      this.controls.forEach((control) => control.innerHTML = `<span class="link text-sm text-subdued">${window.themeVariables.strings.addOrderNote}</span>`);
    } else {
      this.controls.forEach((control) => control.innerHTML = `<span class="link text-sm text-subdued">${window.themeVariables.strings.editOrderNote}</span>`);
    }
  }
};
if (!window.customElements.get("cart-note")) {
  window.customElements.define("cart-note", CartNote);
}
if (!window.customElements.get("cart-note-dialog")) {
  window.customElements.define("cart-note-dialog", CartNoteDialog);
}

// js/common/cart/free-shipping-bar.js
var FreeShippingBar = class extends HTMLElement {
  static get observedAttributes() {
    return ["threshold", "sample-threshold", "total-price"];
  }
  constructor() {
    super();
    this._onCartChangedListener = this._onCartChanged.bind(this);
    this._currencyFormatter = new Intl.NumberFormat(Shopify.locale, { style: "currency", currency: Shopify.currency.active });
  }
  async connectedCallback() {
    this.threshold = Math.round(this.threshold * (Shopify.currency.rate || 1));
    if (this.hasAttribute("sample-threshold")) {
      this.sampleThreshold = Math.round(this.sampleThreshold * (Shopify.currency.rate || 1));
    }
    document.addEventListener("cart:change", this._onCartChangedListener);
  }
  disconnectedCallback() {
    document.removeEventListener("cart:change", this._onCartChangedListener);
  }
  get threshold() {
    return parseFloat(this.getAttribute("threshold"));
  }
  set threshold(value) {
    this.setAttribute("threshold", value);
  }
  get hasSampleThreshold() {
    return this.hasAttribute("sample-threshold") && Number.isFinite(this.sampleThreshold) && this.sampleThreshold > this.threshold;
  }
  get sampleThreshold() {
    return parseFloat(this.getAttribute("sample-threshold"));
  }
  set sampleThreshold(value) {
    this.setAttribute("sample-threshold", value);
  }
  get progressThreshold() {
    return this.hasSampleThreshold ? this.sampleThreshold : this.threshold;
  }
  get totalPrice() {
    return parseFloat(this.getAttribute("total-price"));
  }
  set totalPrice(value) {
    this.setAttribute("total-price", value);
  }
  async attributeChangedCallback(attribute, oldValue, newValue) {
    await window.customElements.whenDefined("progress-bar");
    const progressBarElement = this.querySelector("progress-bar");
    switch (attribute) {
      case "threshold":
      case "sample-threshold":
        progressBarElement.valueMax = this.progressThreshold;
        break;
      case "total-price":
        progressBarElement.valueNow = newValue;
        break;
    }
    this._updateMessage();
    this._updateMilestones();
  }
  _updateMessage() {
    const messageElement = this.querySelector("[data-free-shipping-message]");
    if (!messageElement) {
      return;
    }
    if (this.hasSampleThreshold && this.totalPrice >= this.sampleThreshold) {
      messageElement.innerHTML = this.getAttribute("sample-reached-message");
    } else if (this.hasSampleThreshold && this.totalPrice >= this.threshold) {
      const replacement = this._formatRemainingAmount(this.sampleThreshold - this.totalPrice);
      messageElement.innerHTML = this.getAttribute("shipping-reached-sample-unreached-message").replace(/__remaining_amount__|({{.*}})/g, replacement);
    } else if (this.totalPrice >= this.threshold) {
      messageElement.innerHTML = this.getAttribute("reached-message");
    } else {
      const replacement = this._formatRemainingAmount(this.threshold - this.totalPrice);
      messageElement.innerHTML = this.getAttribute("unreached-message").replace(/__remaining_amount__|({{.*}})/g, replacement);
    }
  }
  _formatRemainingAmount(amount) {
    return `<span class="free-shipping-bar__highlight">${this._currencyFormatter.format(amount / 100).replace(/\$/g, "$$$$")}</span>`;
  }
  _updateMilestones() {
    this.querySelectorAll("[data-free-shipping-milestone]").forEach((milestone) => {
      const milestoneThreshold = milestone.getAttribute("data-free-shipping-milestone") === "sample" ? this.sampleThreshold : this.threshold;
      milestone.classList.toggle("is-reached", this.totalPrice >= milestoneThreshold);
    });
  }
  _onCartChanged(event) {
    const priceForItems = event.detail["cart"]["items"].filter((item) => item["requires_shipping"]).reduce((sum, item) => sum + item["final_line_price"], 0), cartDiscount = event.detail["cart"]["cart_level_discount_applications"].reduce((sum, discountAllocation) => sum + discountAllocation["total_allocated_amount"], 0);
    this.totalPrice = priceForItems - cartDiscount;
  }
};
if (!window.customElements.get("free-shipping-bar")) {
  window.customElements.define("free-shipping-bar", FreeShippingBar);
}

// CODEX: cart-page updates preserve keyboard focus and the shared quantity
// semantics without replacing the order note, app blocks or checkout form.
var CartPage = class extends HTMLElement {
  connectedCallback() {
    this._controller = new AbortController();
    const options = { signal: this._controller.signal };
    document.addEventListener("cart:prepare-bundled-sections", (event) => {
      event.detail.sections.push(this.dataset.sectionId);
    }, options);
    document.addEventListener("cart:change", (event) => this._onCartChanged(event.detail), options);
  }
  disconnectedCallback() {
    this._controller.abort();
    this._focusGuard?.cancel();
    clearTimeout(this._statusTimer);
  }
  _setCartUpdateBusy(busy) {
    this._quantityUpdatePending = busy;
    if (busy) {
      clearTimeout(this._statusTimer);
      this.querySelector("[data-cart-page-status]").textContent = "";
    }
    this.querySelector(".order-summary__body")?.setAttribute("aria-busy", String(busy));
  }
  _captureFocus() {
    const active = document.activeElement;
    const row = active?.closest?.("[data-cart-line-key]");
    if (!row || !this.contains(row)) return null;
    const selector = active.matches(".codex-cart-remove-text-button") ? ".codex-cart-remove-text-button"
      : active.matches(".codex-cart-quantity__input") ? ".codex-cart-quantity__input"
      : active.matches("[data-codex-quantity-control]") ? `[data-codex-quantity-control="${active.dataset.codexQuantityControl}"]`
      : "[data-cart-line-title]";
    return { key: row.dataset.cartLineKey, index: Array.from(row.parentElement.children).indexOf(row), selector };
  }
  _announce(message) {
    const status = this.querySelector("[data-cart-page-status]");
    clearTimeout(this._statusTimer);
    status.textContent = "";
    this._statusTimer = setTimeout(() => {
      if (status.isConnected) status.textContent = message;
    }, 250);
  }
  async _onCartChanged(detail) {
    const version = this._updateVersion = (this._updateVersion || 0) + 1;
    const sectionId = this.dataset.sectionId;
    let html = detail.cart?.sections?.[sectionId];
    try {
      // Shopify can return null for a failed bundled section. Fetch the page
      // section again so a successful cart mutation never leaves stale totals.
      if (!html) {
        const response = await fetch(`${Shopify.routes.root}cart?section_id=${encodeURIComponent(sectionId)}`);
        if (!response.ok) throw new Error("Cart section unavailable");
        html = await response.text();
      }
      if (version !== this._updateVersion || !this.isConnected) return;
      const updated = new DOMParser().parseFromString(html, "text/html").querySelector("cart-page");
      if (!updated) throw new Error("Cart section unavailable");
      const focus = this._captureFocus();
      this._focusGuard?.cancel();
      const guard = this._focusGuard = createFocusRestorationGuard();
      const currentBody = this.querySelector(".order-summary__body");
      const updatedBody = updated.querySelector(".order-summary__body");
      if (currentBody && updatedBody) {
        currentBody.replaceChildren(...updatedBody.childNodes);
        // The split bar uses server-classified product tags. Refresh this
        // header fragment alongside the cart rows after a quantity change.
        if (this.hasAttribute("data-split-shipping")) {
          const selector = ".cart-header > split-shipping-bars, .cart-header > free-shipping-bar";
          const currentShippingBar = this.querySelector(selector);
          const updatedShippingBar = updated.querySelector(selector);
          if (currentShippingBar && updatedShippingBar) currentShippingBar.replaceWith(updatedShippingBar);
          else if (currentShippingBar) currentShippingBar.remove();
          else if (updatedShippingBar) this.querySelector(".cart-header")?.append(updatedShippingBar);
        }
        const totals = this.querySelector("[data-cart-page-totals]");
        const updatedTotals = updated.querySelector("[data-cart-page-totals]");
        if (totals && updatedTotals) totals.replaceChildren(...updatedTotals.childNodes);
        const checkout = this.querySelector('button[name="checkout"]');
        const label = updated.querySelector('button[name="checkout"]')?.getAttribute("aria-label");
        if (checkout && label) checkout.setAttribute("aria-label", label);
      } else {
        this.querySelector("[data-cart-page-content]").replaceChildren(...updated.querySelector("[data-cart-page-content]").childNodes);
      }
      this._setCartUpdateBusy(false);
      requestAnimationFrame(() => {
        if (version !== this._updateVersion || !this.isConnected) { guard.cancel(); return; }
        const rows = Array.from(this.querySelectorAll("[data-cart-line-key]"));
        const sameRow = rows.find((row) => row.dataset.cartLineKey === focus?.key);
        const target = sameRow?.querySelector(focus?.selector)
          || rows[Math.min(focus?.index || 0, rows.length - 1)]?.querySelector("[data-cart-line-title]")
          || this.querySelector(".empty-state a, .empty-state button");
        if (focus && guard.canRestore()) target?.focus({ preventScroll: true });
        guard.cancel();
        if (detail.baseEvent === "line-item:change" && detail.cartPageUpdate) {
          const title = detail.lineTitle || "Item";
          if (detail.quantity === 0) {
            this._announce(`${title} was removed from your cart.${rows.length ? "" : " Your cart is empty."}`);
          } else if (document.activeElement !== target || !sameRow || !target.matches(".codex-cart-quantity__input, [data-codex-quantity-control]")) {
            // Focused quantity controls already expose the current quantity.
            // If the user has moved on, provide one concise status instead.
            this._announce(`${title} quantity updated to ${detail.quantity}.`);
          }
        }
      });
    } catch (error) {
      this._setCartUpdateBusy(false);
      this._announce("Your cart changed, but the page could not refresh. Reload the page to see the latest totals before checking out.");
    }
  }
};
if (!window.customElements.get("cart-page")) {
  window.customElements.define("cart-page", CartPage);
}

// js/common/cart/line-item-quantity.js
var LineItemQuantity = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", this._onQuantityChanged);
    this.addEventListener("click", this._onActionClicked);
  }
  _onQuantityChanged(event) {
    if (!event.target.hasAttribute("data-line-key")) {
      return;
    }
    event.target.removeAttribute("data-codex-quantity-interaction");
    this._changeLineItemQuantity(event.target.getAttribute("data-line-key"), parseInt(event.target.value, 10), "id", "quantity-input", { announceQuantity: false });
  }
  _onActionClicked(event) {
    if (!(event.target instanceof Element)) {
      return;
    }
    const quantityButton = event.target.closest("button[data-line-key][data-target-quantity]");
    const nonPointerActivation = event.isTrusted && event.detail === 0 && !document.documentElement.hasAttribute("data-codex-pointer-focus");
    if (quantityButton && this.contains(quantityButton)) {
      event.preventDefault();
      const lineKey = quantityButton.getAttribute("data-line-key");
      const targetQuantity = parseInt(quantityButton.getAttribute("data-target-quantity"), 10);
      const identifier = quantityButton.getAttribute("data-quantity-identifier") || "id";
      const control = quantityButton.matches(".line-item__remove-button, .codex-cart-remove-text-button")
        ? "remove"
        : quantityButton === this.querySelector(".codex-cart-quantity__button:first-of-type")
          ? "quantity-decrease"
          : "quantity-increase";
      this._changeLineItemQuantity(lineKey, targetQuantity, identifier, control, { announceQuantity: targetQuantity === 0, nonPointerActivation });
      return;
    }
    const removeLink = event.target.closest("a[href*=\"/cart/change\"]");
    if (!removeLink || !this.contains(removeLink)) {
      return;
    }
    event.preventDefault();
    const url = new URL(removeLink.href);
    const targetQuantity = parseInt(url.searchParams.get("quantity"), 10);
    const id = url.searchParams.get("id");
    const line = url.searchParams.get("line");
    if (Number.isNaN(targetQuantity)) {
      return;
    }
    if (id) {
      this._changeLineItemQuantity(id, targetQuantity, "id", "remove", { nonPointerActivation });
      return;
    }
    if (line) {
      this._changeLineItemQuantity(line, targetQuantity, "line", "remove", { nonPointerActivation });
    }
  }
  async _changeLineItemQuantity(lineKey, targetQuantity, identifier = "id", focusControl = null, options = {}) {
    if (lineKey == null || Number.isNaN(targetQuantity)) {
      return;
    }
    const cartPage = this.closest("cart-page");
    if (cartPage?._quantityUpdatePending) {
      this.querySelector(".quantity-input")?.removeAttribute("data-codex-quantity-request-pending");
      return;
    }
    const lineItem = this.closest("line-item, [data-cart-line-key]");
    const lineTitleElement = lineItem?.querySelector("[data-cart-line-title], .line-item__title-row a, .line-item__title-row > p");
    const lineTitle = (lineTitleElement?.dataset.cartProductTitle || lineTitleElement?.textContent || "").replace(/\s+/g, " ").trim();
    const lineUrl = lineTitleElement?.getAttribute("href") || "";
    const focusState = {
      type: "line-item",
      lineKey: lineItem?.querySelector("[data-line-key]")?.getAttribute("data-line-key") || lineKey,
      control: focusControl || "quantity-input"
    };
    const cartDrawer = this.closest("cart-drawer");
    const cartSurface = cartPage || cartDrawer;
    cartSurface?._setCartUpdateBusy(true);
    lineItem?.querySelector('[data-cart-quantity-error]')?.remove();
    lineItem?.dispatchEvent(new CustomEvent("line-item:will-change", { bubbles: true, detail: { targetQuantity, focusState } }));
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
    const payload = { quantity: targetQuantity, sections: [...new Set(sectionsToBundle)] };
    payload[identifier] = lineKey;
    if (document.querySelector("cart-page")) payload.sections_url = `${Shopify.routes.root}cart`;
    try {
      const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const cartContent = await response.json();
      if (!response.ok) throw new Error(cartContent.description || "Unable to update cart.");
      if (window.themeVariables.settings.pageType === "cart" && !document.querySelector("cart-page")) {
        window.location.reload();
        return;
      }
      const currentLineKey = lineItem?.querySelector("[data-line-key]")?.getAttribute("data-line-key") || lineKey;
      const updatedItem = cartContent.items.find((item) => item.key === currentLineKey);
      const detail = {
        quantity: updatedItem ? updatedItem.quantity : targetQuantity,
        cart: cartContent,
        lineKey,
        lineTitle,
        lineUrl,
        focusState,
        cartPageUpdate: Boolean(cartPage),
        nonPointerActivation: options.nonPointerActivation === true,
        announceQuantity: options.announceQuantity !== false
      };
      lineItem?.dispatchEvent(new CustomEvent("line-item:change", { bubbles: true, detail }));
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: { baseEvent: "line-item:change", ...detail }
      }));
    } catch (error) {
      cartSurface?._setCartUpdateBusy(false);
      const errorHolder = cartPage ? this : lineItem?.querySelector(".line-item__info");
      if (errorHolder) {
        const message = document.createElement("p");
        message.className = "text-error text-xs";
        message.setAttribute("role", "alert");
        message.setAttribute("data-cart-quantity-error", "");
        if (cartPage) message.setAttribute("data-cart-page-error", "");
        message.textContent = error instanceof TypeError ? "Unable to update cart. Please try again." : error.message || "Unable to update cart.";
        errorHolder.append(message);
      }
      const quantityInput = lineItem?.querySelector(".quantity-input");
      if (quantityInput) {
        quantityInput.value = quantityInput.defaultValue;
        quantityInput.removeAttribute("data-codex-quantity-request-pending");
        syncCodexQuantityButtonNames(quantityInput.closest('[data-codex-quantity-a11y="true"]'));
      }
      lineItem?.dispatchEvent(new CustomEvent("line-item:error", { bubbles: true }));
    }
  }
};
if (!window.customElements.get("line-item-quantity")) {
  window.customElements.define("line-item-quantity", LineItemQuantity);
}

// js/common/cart/shipping-estimator.js
var ShippingEstimator = class extends HTMLElement {
  constructor() {
    super();
    this._estimateShippingListener = this._estimateShipping.bind(this);
  }
  connectedCallback() {
    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;
    this.submitButton.addEventListener("click", this._estimateShippingListener);
  }
  disconnectedCallback() {
    this.submitButton.removeEventListener("click", this._estimateShippingListener);
  }
  /**
   * @doc https://shopify.dev/docs/themes/ajax-api/reference/cart#generate-shipping-rates
   */
  async _estimateShipping(event) {
    event.preventDefault();
    const zip = this.querySelector('[name="address[zip]"]').value, country = this.querySelector('[name="address[country]"]').value, province = this.querySelector('[name="address[province]"]').value;
    this.submitButton.setAttribute("aria-busy", "true");
    const prepareResponse = await fetch(`${Shopify.routes.root}cart/prepare_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`, { method: "POST" });
    if (prepareResponse.ok) {
      const shippingRates = await this._getAsyncShippingRates(zip, country, province);
      this._formatShippingRates(shippingRates);
    } else {
      const jsonError = await prepareResponse.json();
      this._formatError(jsonError);
    }
    this.resultsElement.hidden = false;
    this.submitButton.removeAttribute("aria-busy");
  }
  async _getAsyncShippingRates(zip, country, province) {
    const response = await fetch(`${Shopify.routes.root}cart/async_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`);
    const responseAsText = await response.text();
    if (responseAsText === "null") {
      return this._getAsyncShippingRates(zip, country, province);
    } else {
      return JSON.parse(responseAsText)["shipping_rates"];
    }
  }
  _formatShippingRates(shippingRates) {
    let formattedShippingRates = shippingRates.map((shippingRate) => {
      return `<li>${shippingRate["presentment_name"]}: ${shippingRate["currency"]} ${shippingRate["price"]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${shippingRates.length === 0 ? window.themeVariables.strings.shippingEstimatorNoResults : shippingRates.length === 1 ? window.themeVariables.strings.shippingEstimatorOneResult : window.themeVariables.strings.shippingEstimatorMultipleResults}</p>
        ${formattedShippingRates === "" ? "" : `<ul class="list-disc" role="list">${formattedShippingRates.join("")}</ul>`}
      </div>
    `;
  }
  _formatError(errors) {
    let formattedShippingRates = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${window.themeVariables.strings.shippingEstimatorError}</p>
        <ul class="list-disc" role="list">${formattedShippingRates}</ul>
      </div>
    `;
  }
};
if (!window.customElements.get("shipping-estimator")) {
  window.customElements.define("shipping-estimator", ShippingEstimator);
}

// js/common/facets/facet-apply-button.js
var FacetApplyButton = class extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener("click", this._closeDrawer);
    if (!this.hasAttribute("data-results-count")) {
      this.form.addEventListener("change", this._updateCount.bind(this));
      this.filterCountElement = document.createElement("span");
      this.appendChild(this.filterCountElement);
    }
  }
  connectedCallback() {
    if (this.filterCountElement) {
      this._updateCount();
    }
  }
  _updateCount() {
    const form = new FormData(this.form);
    const nonFilterKeys = ["page", "q", "type", "sort_by", "section_id"];
    let nonEmptyValuesCount = Array.from(form.entries()).filter(([key, value]) => value !== "" && !nonFilterKeys.includes(key)).length;
    this.filterCountElement.innerText = ` (${nonEmptyValuesCount})`;
  }
  async _closeDrawer() {
    const drawer = this.closest("facet-drawer");
    const returnFocusControl = drawer.controls.find((control) => control.isConnected && control.getClientRects().length > 0);
    await drawer.hide();
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (returnFocusControl?.isConnected) {
        returnFocusControl.focus({ preventScroll: true });
      }
    }));
  }
};
if (!window.customElements.get("facet-apply-button")) {
  window.customElements.define("facet-apply-button", FacetApplyButton, { extends: "button" });
}

// js/common/facets/facet-dialog.js
var FacetDialog = class extends DialogElement {
  get initialFocus() {
    return false;
  }
};
if (!window.customElements.get("facet-dialog")) {
  window.customElements.define("facet-dialog", FacetDialog);
}

// js/common/facets/facet-drawer.js
var FacetDrawer = class extends Drawer {
  constructor() {
    super();
    this._returnFocusTarget = null;
    this._escapeClosePromise = null;
    this._onEscapeKeydown = this._onEscapeKeydown.bind(this);
    this.addEventListener("dialog:after-hide", this._submitForm);
  }
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("keydown", this._onEscapeKeydown, true);
  }
  disconnectedCallback() {
    document.removeEventListener("keydown", this._onEscapeKeydown, true);
    super.disconnectedCallback();
  }
  get shouldHideOnFocusTrapDeactivate() {
    return false;
  }
  _onToggleClicked(event) {
    if (!this.open) {
      const toggle = event?.target instanceof Element ? event.target.closest(`[aria-controls="${this.id}"]`) : null;
      if (toggle instanceof HTMLElement) {
        this._returnFocusTarget = toggle;
      }
    }
    return super._onToggleClicked(event);
  }
  show(...args) {
    const target = this._returnFocusTarget;
    if (!this.open && (!target || !target.isConnected || target.getClientRects().length === 0)) {
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement && activeElement !== document.body) {
        this._returnFocusTarget = activeElement;
      }
    }
    return super.show(...args);
  }
  _getReturnFocusTarget() {
    const target = this._returnFocusTarget;
    if (target instanceof HTMLElement && target.isConnected && target.getClientRects().length > 0 && !target.hasAttribute("disabled")) {
      return target;
    }
    return this.controls.find((control) => control.isConnected && control.getClientRects().length > 0 && !control.hasAttribute("disabled")) || null;
  }
  _onEscapeKeydown(event) {
    if (!this.open || event.key !== "Escape" || !event.composedPath().includes(this)) {
      return;
    }
    if (event.composedPath().some((target) => target instanceof Element && target !== this && target.matches("facet-dialog[open], x-popover[open]"))) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this._escapeClosePromise) {
      return;
    }
    const returnFocusTarget = this._getReturnFocusTarget();
    this._escapeClosePromise = Promise.resolve(this.hide()).then(() => new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!this.open && returnFocusTarget?.isConnected && returnFocusTarget.getClientRects().length > 0 && !returnFocusTarget.hasAttribute("disabled")) {
          returnFocusTarget.focus({ preventScroll: true });
        }
        this._returnFocusTarget = null;
        resolve();
      }));
    })).finally(() => {
      this._escapeClosePromise = null;
    });
  }
  _submitForm() {
    const form = this.querySelector("#facet-form");
    if (!form || form.hasAttribute("update-on-change")) {
      return;
    }
    if (HTMLFormElement.prototype.requestSubmit) {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event("submit", { cancelable: true }));
    }
  }
};
if (!window.customElements.get("facet-drawer")) {
  window.customElements.define("facet-drawer", FacetDrawer);
}

// js/common/facets/facet-floating-filter.js
import { animate as animate5 } from "vendor";
var FacetFloatingFilter = class extends HTMLElement {
  connectedCallback() {
    this._footerObserver = new IntersectionObserver(this._onFooterVisibilityChanged.bind(this), { rootMargin: "50px 0px" });
    this._footerObserver.observe(document.querySelector(".shopify-section--footer"));
    if (!this.hasAttribute("delay-until-grid")) {
      return;
    }
    this._collectionSection = this.closest(".shopify-section");
    this.toggleAttribute("grid-in-view", this._collectionSection?.hasAttribute("data-filter-grid-in-view") ?? false);
    this._gridObserver = new IntersectionObserver(this._onGridVisibilityChanged.bind(this));
    this._onFacetUpdated = this._observeGrid.bind(this);
    this._collectionSection?.addEventListener("facet:updated", this._onFacetUpdated);
    this._observeGrid();
  }
  disconnectedCallback() {
    this._footerObserver?.disconnect();
    this._gridObserver?.disconnect();
    this._collectionSection?.removeEventListener("facet:updated", this._onFacetUpdated);
  }
  _observeGrid() {
    this._gridObserver.disconnect();
    this._grid = this.closest(".collection")?.querySelector("#product-grid");
    if (this._grid) {
      this._gridObserver.observe(this._grid);
    } else {
      this._setGridVisibility(true);
    }
  }
  _onGridVisibilityChanged(entries) {
    const entry = entries.find((item) => item.target === this._grid);
    if (entry) {
      this._setGridVisibility(entry.isIntersecting);
    }
  }
  _setGridVisibility(isVisible) {
    this.toggleAttribute("grid-in-view", isVisible);
    this._collectionSection?.toggleAttribute("data-filter-grid-in-view", isVisible);
  }
  _onFooterVisibilityChanged(entries) {
    if (entries[0].isIntersecting) {
      animate5(this, { opacity: 0, transform: [null, "translateY(15px)"], visibility: "hidden" }, { duration: 0.15 });
    } else {
      animate5(this, { opacity: 1, transform: [null, "translateY(0)"], visibility: "visible" }, { duration: 0.15 });
    }
  }
};
if (!window.customElements.get("facet-floating-filter")) {
  window.customElements.define("facet-floating-filter", FacetFloatingFilter);
}

// js/common/facets/facet-form.js
var abortController = null;
var openElements = /* @__PURE__ */ new Set();
document.addEventListener("facet:update", async (event) => {
  if (abortController) {
    abortController.abort();
  }
  abortController = new AbortController();
  const url = event.detail.url, shopifySection = document.getElementById(`shopify-section-${url.searchParams.get("section_id")}`);
  if (window.themeVariables.settings.pageType === "search") {
    const originalSearchQuery = new URL(location.href).searchParams.get("ool_original_query");
    if (originalSearchQuery) {
      url.searchParams.set("ool_original_query", originalSearchQuery);
    }
  }
  const activeFacetElement = shopifySection?.contains(document.activeElement) ? document.activeElement : null;
  const activeFacetElementId = activeFacetElement?.id || "";
  const activeFacetElementName = activeFacetElement?.getAttribute("name") || "", activeFacetElementValue = activeFacetElement?.getAttribute("value") || "";
  const activeFacetDialogId = activeFacetElement?.closest("facet-dialog, x-popover")?.id || "";
  const activeFacetContextSelector = activeFacetElement?.closest(".collection__facets") ? ".collection__facets" : activeFacetElement?.closest(".collection__top-bar") ? ".collection__top-bar" : "";
  const facetRemovalElement = event.target instanceof Element ? event.target.closest(FACET_REMOVAL_SELECTOR) : null;
  const facetRemovalAnnouncement = getFacetRemovalAnnouncement(facetRemovalElement);
  const facetRemovalHasFocus = facetRemovalElement instanceof HTMLElement && activeFacetElement === facetRemovalElement;
  const nextFacetFocusElement = facetRemovalHasFocus ? getNextFacetRemovalElement(activeFacetElement) || getFacetSortByFocusTarget(shopifySection) || getNextFacetFocusableElement(activeFacetElement) : null;
  const nextFacetFocusDescriptor = facetRemovalHasFocus ? getFacetFocusDescriptor(nextFacetFocusElement) : null;
  const facetRemovalFocusContext = facetRemovalHasFocus ? getFacetFocusContext(activeFacetElement) : null;
  const facetRemovalFocusGuard = facetRemovalHasFocus ? createFocusRestorationGuard() : null;
  const openFacetDrawer = document.querySelector(`facet-drawer#facets-drawer[open] #facet-form[section-id="${url.searchParams.get("section_id")}"][update-on-change]`)?.closest("facet-drawer");
  shopifySection.classList.add("is-loading");
  const clonedUrl = new URL(url);
  clonedUrl.searchParams.delete("section_id");
  history.replaceState({}, "", clonedUrl.toString());
  try {
    const tempContent = new DOMParser().parseFromString(await (await cachedFetch(url.toString(), { signal: abortController.signal })).text(), "text/html");
    Array.from(tempContent.querySelectorAll("details, facet-dialog")).forEach((item) => {
      if (openElements.has(item.id)) {
        item.setAttribute("open", "");
      }
    });
    const tempShopifySection = tempContent.querySelector(".shopify-section");
    // Announce the completed result count, not every card mutation or
    // hover/focus-revealed action inside the product grid.
    const resultsStatusMessage = tempShopifySection.querySelector("[data-facet-results-status]")?.getAttribute("data-facet-results-status");
    const facetStatusMessage = [facetRemovalAnnouncement, resultsStatusMessage].filter(Boolean).join(" ");
    if (openFacetDrawer) {
      const nextFacetDrawer = tempShopifySection.querySelector("facet-drawer#facets-drawer");
      if (nextFacetDrawer) {
        const activeElementId = openFacetDrawer.contains(document.activeElement) ? document.activeElement.id : "", currentFacetForm = openFacetDrawer.querySelector("#facet-form"), nextFacetForm = nextFacetDrawer.querySelector("#facet-form");
        openFacetDrawer._ignoreFocusTrapDeactivate = true;
        try {
          if (currentFacetForm && nextFacetForm) {
            currentFacetForm.replaceWith(document.importNode(nextFacetForm, true));
          }
          if (activeElementId && window.CSS?.escape) {
            openFacetDrawer.querySelector(`#${CSS.escape(activeElementId)}`)?.focus({ preventScroll: true });
          }
        } finally {
          requestAnimationFrame(() => openFacetDrawer._ignoreFocusTrapDeactivate = false);
        }
      }
      const currentCollection = shopifySection.querySelector(".collection"), nextCollection = tempShopifySection.querySelector(".collection");
      if (currentCollection && nextCollection) {
        currentCollection.className = nextCollection.className;
      }
      [".collection__top-bar", ".collection__sort-after-facets", ".facets__floating-filter", ".collection__active-facets", ".collection__results", ".collection__pagination"].forEach((selector) => {
        const currentElement = shopifySection.querySelector(selector), nextElement = tempShopifySection.querySelector(selector);
        if (currentElement && nextElement) {
          currentElement.replaceWith(document.importNode(nextElement, true));
        } else if (currentElement) {
          currentElement.remove();
        } else if (nextElement) {
          const nextElementContent = document.importNode(nextElement, true), collectionElement = shopifySection.querySelector(".collection");
          if (selector === ".collection__top-bar") {
            collectionElement?.prepend(nextElementContent);
          } else if (selector === ".facets__floating-filter") {
            shopifySection.querySelector(".collection__top-bar")?.after(nextElementContent);
          } else if (selector === ".collection__sort-after-facets") {
            shopifySection.querySelector(".collection__active-facets, .collection__facets")?.after(nextElementContent);
          } else if (selector === ".collection__active-facets") {
            collectionElement?.querySelector(".collection__facets")?.after(nextElementContent);
          } else if (selector === ".collection__results") {
            collectionElement?.append(nextElementContent);
          } else if (selector === ".collection__pagination") {
            shopifySection.querySelector(".collection__results")?.after(nextElementContent);
          }
        }
      });
      shopifySection.classList.remove("is-loading");
      if (facetStatusMessage) {
        announceStatus(facetStatusMessage);
      }
      shopifySection.dispatchEvent(new CustomEvent("facet:updated", { bubbles: true }));
      if (facetRemovalHasFocus) {
        requestAnimationFrame(() => {
          focusFacetRemovalTarget(shopifySection, nextFacetFocusDescriptor, facetRemovalFocusContext, openFacetDrawer, facetRemovalFocusGuard);
        });
      }
      return;
    }
    shopifySection.replaceChildren(...document.importNode(tempShopifySection, true).childNodes);
    document.querySelectorAll("body > facet-drawer#facets-drawer:not([open])").forEach((drawer) => drawer.remove());
    shopifySection.classList.remove("is-loading");
    if (facetStatusMessage) {
      announceStatus(facetStatusMessage);
    }
    shopifySection.dispatchEvent(new CustomEvent("facet:updated", { bubbles: true }));
    if (facetRemovalHasFocus) {
      requestAnimationFrame(() => {
        focusFacetRemovalTarget(shopifySection, nextFacetFocusDescriptor, facetRemovalFocusContext, null, facetRemovalFocusGuard);
      });
    } else if (activeFacetElement) {
      requestAnimationFrame(() => {
        const focusSearchRoot = activeFacetContextSelector ? shopifySection.querySelector(activeFacetContextSelector) : shopifySection;
        let replacementFocusTarget = activeFacetElementId ? Array.from(focusSearchRoot?.querySelectorAll("[id]") || []).find((element) => element.id === activeFacetElementId) : null;
        if (!replacementFocusTarget && activeFacetElementName) {
          replacementFocusTarget = Array.from(focusSearchRoot?.querySelectorAll("[name]") || []).find((element) => element.getAttribute("name") === activeFacetElementName && element.getAttribute("value") === activeFacetElementValue);
        }
        if (!replacementFocusTarget && activeFacetDialogId) {
          replacementFocusTarget = Array.from(focusSearchRoot?.querySelectorAll("[aria-controls]") || []).find((element) => element.getAttribute("aria-controls") === activeFacetDialogId);
        }
        if (replacementFocusTarget) {
          replacementFocusTarget.focus({ preventScroll: true });
        }
      });
    }
    const scrollToElement = window.matchMedia("(min-width: 700px) and (max-width: 999px)").matches ? shopifySection.querySelector(".collection__results") : shopifySection.querySelector(".collection__results product-list"), scrollToBoundingRect = scrollToElement.getBoundingClientRect();
    if (scrollToBoundingRect.top < parseInt(getComputedStyle(scrollToElement).scrollPaddingTop || 0)) {
      scrollToElement.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  } catch (e) {
    facetRemovalFocusGuard?.cancel();
  }
});
var FacetForm = class extends HTMLFormElement {
  constructor() {
    super();
    this._isDirty = false;
    this.addEventListener("change", this._onFormChanged);
    this.addEventListener("submit", this._onFormSubmitted);
  }
  connectedCallback() {
    Array.from(this.querySelectorAll("details, facet-dialog")).forEach((disclosureElement) => {
      if (disclosureElement.open) {
        openElements.add(disclosureElement.id);
      }
      disclosureElement.addEventListener("toggle", () => {
        if (disclosureElement.open) {
          openElements.add(disclosureElement.id);
        } else {
          openElements.delete(disclosureElement.id);
        }
      });
    });
  }
  _buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this)), url = new URL(this.action);
    url.search = "";
    searchParams.forEach((value, key) => url.searchParams.append(key, value));
    ["page", "filter.v.price.gte", "filter.v.price.lte"].forEach((optionToClear) => {
      if (url.searchParams.get(optionToClear) === "") {
        url.searchParams.delete(optionToClear);
      }
    });
    url.searchParams.set("section_id", this.getAttribute("section-id"));
    return url;
  }
  _onFormChanged() {
    this._isDirty = true;
    if (this.hasAttribute("update-on-change")) {
      if (HTMLFormElement.prototype.requestSubmit) {
        this.requestSubmit();
      } else {
        this.dispatchEvent(new Event("submit", { cancelable: true }));
      }
    } else {
      cachedFetch(this._buildUrl().toString());
    }
  }
  _onFormSubmitted(event) {
    event.preventDefault();
    if (!this._isDirty) {
      return;
    }
    this.dispatchEvent(new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url: this._buildUrl()
      }
    }));
    this._isDirty = false;
  }
};
if (!window.customElements.get("facet-form")) {
  window.customElements.define("facet-form", FacetForm, { extends: "form" });
}

// js/common/facets/facet-link.js
var FacetLink = class extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener("click", this._onFacetUpdate);
  }
  _onFacetUpdate(event) {
    event.preventDefault();
    const sectionId = event.target.closest(".shopify-section").id.replace("shopify-section-", ""), url = new URL(this.href);
    url.searchParams.set("section_id", sectionId);
    this.dispatchEvent(new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url
      }
    }));
  }
};
if (!window.customElements.get("facet-link")) {
  window.customElements.define("facet-link", FacetLink, { extends: "a" });
}

// js/common/facets/facet-sort-by.js
var FacetSortBy = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("listbox:change", this._onValueChanged);
    this.addEventListener("click", this._onTriggerClicked);
    this.addEventListener("keydown", this._onKeyDown);
  }
  _onTriggerClicked(event) {
    const trigger = event.target instanceof Element ? event.target.closest("button[aria-controls]") : null;
    if (event.detail !== 0 || !(trigger instanceof HTMLButtonElement) || !this.contains(trigger)) {
      return;
    }
    const popoverId = trigger.getAttribute("aria-controls"), popover = popoverId ? this.querySelector(`#${CSS.escape(popoverId)}`) : null;
    if (!(popover instanceof HTMLElement) || popover.hasAttribute("open")) {
      return;
    }
    popover.addEventListener("dialog:after-show", () => {
      if (!popover.hasAttribute("open")) {
        return;
      }
      const optionToFocus = popover.querySelector('[role="option"][aria-selected="true"]') || popover.querySelector('[role="option"]');
      optionToFocus?.focus({ preventScroll: true });
    }, { once: true });
  }
  _onKeyDown(event) {
    if (!["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    const sortTrigger = event.target instanceof Element ? event.target.closest("button[aria-controls]") : null;
    if (sortTrigger instanceof HTMLButtonElement && this.contains(sortTrigger)) {
      const popoverId = sortTrigger.getAttribute("aria-controls"), popover = popoverId ? this.querySelector(`#${CSS.escape(popoverId)}`) : null;
      if (popover instanceof HTMLElement && popover.hasAttribute("open") && ["ArrowUp", "ArrowDown"].includes(event.key)) {
        const options = Array.from(popover.querySelectorAll('[role="option"]')).filter((option) => option.getClientRects().length > 0);
        event.preventDefault();
        options[event.key === "ArrowUp" ? options.length - 1 : 0]?.focus({ preventScroll: true });
      }
      return;
    }
    const option = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
    if (!(option instanceof HTMLElement)) {
      return;
    }
    const listbox = option.closest("x-listbox"), options = listbox ? Array.from(listbox.querySelectorAll('[role="option"]')).filter((item) => item.getClientRects().length > 0) : [], firstOption = options[0], popover = listbox?.closest("x-popover");
    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      options[event.key === "Home" ? 0 : options.length - 1]?.focus({ preventScroll: true });
      return;
    }
    if (event.key !== "ArrowUp" || option !== firstOption || !(popover instanceof HTMLElement) || !popover.hasAttribute("open")) {
      return;
    }
    const popoverId = popover.id, trigger = popoverId ? this.querySelector(`button[aria-controls="${CSS.escape(popoverId)}"]`) : null;
    if (!(trigger instanceof HTMLButtonElement)) {
      return;
    }
    event.preventDefault();
    popover.hide().then(() => trigger.focus({ preventScroll: true }));
  }
  _onValueChanged(event) {
    const url = new URL(location.href), sectionId = event.target.closest(".shopify-section").id.replace("shopify-section-", "");
    url.searchParams.set("sort_by", event.detail.value);
    url.searchParams.set("section_id", sectionId);
    url.searchParams.delete("page");
    if (window.themeVariables.settings.pageType === "search") {
      url.searchParams.set("type", "product");
    }
    this.dispatchEvent(new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url
      }
    }));
  }
};
if (!window.customElements.get("facet-sort-by")) {
  window.customElements.define("facet-sort-by", FacetSortBy);
}

// js/common/feedback/pill-loader.js
import { animate as animate6, timeline as timeline4, stagger as stagger4 } from "vendor";
var PillLoader = class extends HTMLElement {
  static get observedAttributes() {
    return ["aria-busy"];
  }
  connectedCallback() {
    this.innerHTML = `
      <div class="loader-dots">
        <span></span>
        <span></span>
        <span></span>
      </div>
      
      <svg class="loader-checkmark" fill="none" width="9" height="8" viewBox="0 0 9 8">
        <path d="M1 3.5 3.3 6 8 1" stroke="currentColor" stroke-width="2"/>
      </svg>
    `;
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (newValue === "true") {
      timeline4([
        [this, { opacity: [0, 1], visibility: "visible", transform: ["translateY(5px)", "translateY(0)"] }, { duration: 0.15 }],
        [this.firstElementChild, { opacity: 1, transform: ["translateY(0)"] }, { duration: 0.15, at: "<" }],
        [this.lastElementChild, { opacity: 0 }, { duration: 0, at: "<" }]
      ]);
      animate6(this.firstElementChild.querySelectorAll("span"), { opacity: [1, 0.1] }, { duration: 0.35, delay: stagger4(0.35 / 3), direction: "alternate", repeat: Infinity });
    } else {
      timeline4([
        [this.firstElementChild, { opacity: 0, transform: ["translateY(0)", "translateY(-2px)"] }, { duration: 0.15 }],
        [this.lastElementChild, { opacity: 1, transform: ["translateY(2px)", "translateY(0)"] }, { duration: 0.15 }],
        [this, { opacity: 0, transform: ["translateY(0)", "translateY(-5px)"], visibility: "hidden" }, { duration: 0.15, at: "+0.8" }]
      ]);
    }
  }
};
if (!window.customElements.get("pill-loader")) {
  window.customElements.define("pill-loader", PillLoader);
}

// js/common/feedback/progress-bar.js
var ProgressBar = class extends HTMLElement {
  static get observedAttributes() {
    return ["aria-valuenow", "aria-valuemax"];
  }
  set valueMax(value) {
    this.setAttribute("aria-valuemax", value);
  }
  set valueNow(value) {
    this.setAttribute("aria-valuenow", value);
  }
  attributeChangedCallback() {
    this.style.setProperty("--progress", `${Math.min(1, this.getAttribute("aria-valuenow") / this.getAttribute("aria-valuemax"))}`);
  }
};
if (!window.customElements.get("progress-bar")) {
  window.customElements.define("progress-bar", ProgressBar);
}

// js/common/form/price-range.js
var PriceRange = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.rangeLowerBound = this.querySelector('input[type="range"]:first-child');
    this.rangeHigherBound = this.querySelector('input[type="range"]:last-child');
    this.textInputLowerBound = this.querySelector('input[name="filter.v.price.gte"]');
    this.textInputHigherBound = this.querySelector('input[name="filter.v.price.lte"]');
    this.textInputLowerBound.addEventListener("focus", () => this.textInputLowerBound.select(), { signal: this._abortController.signal });
    this.textInputHigherBound.addEventListener("focus", () => this.textInputHigherBound.select(), { signal: this._abortController.signal });
    this.textInputLowerBound.addEventListener("change", (event) => {
      event.preventDefault();
      event.target.value = Math.max(Math.min(parseInt(event.target.value), parseInt(this.textInputHigherBound.value || event.target.max) - 1), event.target.min);
      this.rangeLowerBound.value = event.target.value;
      this.rangeLowerBound.parentElement.style.setProperty("--range-min", `${parseInt(this.rangeLowerBound.value) / parseInt(this.rangeLowerBound.max) * 100}%`);
    }, { signal: this._abortController.signal });
    this.textInputHigherBound.addEventListener("change", (event) => {
      event.preventDefault();
      event.target.value = Math.min(Math.max(parseInt(event.target.value), parseInt(this.textInputLowerBound.value || event.target.min) + 1), event.target.max);
      this.rangeHigherBound.value = event.target.value;
      this.rangeHigherBound.parentElement.style.setProperty("--range-max", `${parseInt(this.rangeHigherBound.value) / parseInt(this.rangeHigherBound.max) * 100}%`);
    }, { signal: this._abortController.signal });
    this.rangeLowerBound.addEventListener("change", (event) => {
      event.stopPropagation();
      this.textInputLowerBound.value = event.target.value;
      this.textInputLowerBound.dispatchEvent(new Event("change", { bubbles: true }));
    }, { signal: this._abortController.signal });
    this.rangeHigherBound.addEventListener("change", (event) => {
      event.stopPropagation();
      this.textInputHigherBound.value = event.target.value;
      this.textInputHigherBound.dispatchEvent(new Event("change", { bubbles: true }));
    }, { signal: this._abortController.signal });
    this.rangeLowerBound.addEventListener("input", (event) => {
      event.target.value = Math.min(parseInt(event.target.value), parseInt(this.textInputHigherBound.value || event.target.max) - 1);
      event.target.parentElement.style.setProperty("--range-min", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
      this.textInputLowerBound.value = event.target.value;
    }, { signal: this._abortController.signal });
    this.rangeHigherBound.addEventListener("input", (event) => {
      event.target.value = Math.max(parseInt(event.target.value), parseInt(this.textInputLowerBound.value || event.target.min) + 1);
      event.target.parentElement.style.setProperty("--range-max", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
      this.textInputHigherBound.value = event.target.value;
    }, { signal: this._abortController.signal });
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
};
if (!window.customElements.get("price-range")) {
  window.customElements.define("price-range", PriceRange);
}

// js/common/form/quantity-selector.js
function syncCodexQuantityButtonNames(container) {
  if (!(container instanceof HTMLElement) || container.getAttribute("data-codex-quantity-a11y") !== "true") {
    return;
  }

  const input = container.querySelector("input.quantity-selector__input, input.codex-cart-quantity__input");
  if (!(input instanceof HTMLInputElement)) {
    return;
  }

  const quantity = input.value || input.getAttribute("value") || "";
  const step = input.step && input.step !== "any" ? input.step : "1";
  const numericQuantity = Number(quantity);
  const numericStep = Number(step);
  const quantityLabel = container.dataset.codexQuantityLabel || "Quantity";
  const productTitle = container.dataset.codexQuantityProductTitle || "";
  const usesStatusFeedback = container.getAttribute("data-codex-quantity-feedback") === "status";
  const actionLabels = {
    decrease: container.dataset.codexQuantityDecreaseLabel || "Decrease quantity",
    increase: container.dataset.codexQuantityIncreaseLabel || "Increase quantity"
  };

  container.querySelectorAll("button[data-codex-quantity-control]").forEach((button) => {
    const action = button.dataset.codexQuantityControl;
    if (!actionLabels[action]) {
      return;
    }

    if (button.matches(".codex-cart-quantity__button") && Number.isFinite(numericQuantity) && Number.isFinite(numericStep)) {
      let targetQuantity = action === "decrease" ? Math.max(0, numericQuantity - numericStep) : numericQuantity + numericStep;
      const maximum = input.max === "" ? NaN : Number(input.max);
      if (action === "increase" && Number.isFinite(maximum)) {
        targetQuantity = Math.min(targetQuantity, maximum);
      }
      button.dataset.targetQuantity = String(targetQuantity);
    }

    const labelParts = [`${actionLabels[action]} by ${step}`];
    if (!usesStatusFeedback) {
      labelParts.push(`${quantityLabel} is ${quantity}`);
    }
    if (productTitle) {
      labelParts.push(productTitle);
    }
    button.setAttribute("aria-label", labelParts.join(", "));
  });
}

var QuantitySelector = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.inputElement = this.querySelector("input");
    syncCodexQuantityButtonNames(this);
    this.inputElement?.addEventListener("input", () => syncCodexQuantityButtonNames(this), { signal: this._abortController.signal });
    this.inputElement?.addEventListener("change", () => syncCodexQuantityButtonNames(this), { signal: this._abortController.signal });
    this.querySelector("button:first-of-type").addEventListener("click", this.stepDown.bind(this), { signal: this._abortController.signal });
    this.querySelector("button:last-of-type").addEventListener("click", this.stepUp.bind(this), { signal: this._abortController.signal });
  }
  stepDown() {
    const button = this.querySelector('button[data-codex-quantity-control="decrease"]') || this.querySelector("button:first-of-type");
    const shouldRestoreFocus = this.getAttribute("data-codex-quantity-a11y") === "true" || document.activeElement === button;
    const previousValue = this.inputElement.value;
    this.inputElement.stepDown();
    this.inputElement.dispatchEvent(new Event("change", { bubbles: true }));
    syncCodexQuantityButtonNames(this);
    this._announceQuantityChange(previousValue);
    if (shouldRestoreFocus) {
      button.focus({ preventScroll: true });
    }
  }
  stepUp() {
    const button = this.querySelector('button[data-codex-quantity-control="increase"]') || this.querySelector("button:last-of-type");
    const shouldRestoreFocus = this.getAttribute("data-codex-quantity-a11y") === "true" || document.activeElement === button;
    const previousValue = this.inputElement.value;
    this.inputElement.stepUp();
    this.inputElement.dispatchEvent(new Event("change", { bubbles: true }));
    syncCodexQuantityButtonNames(this);
    this._announceQuantityChange(previousValue);
    if (shouldRestoreFocus) {
      button.focus({ preventScroll: true });
    }
  }
  _announceQuantityChange(previousValue) {
    if (this.inputElement.value === previousValue) {
      return;
    }
    if (this.getAttribute("data-codex-quantity-feedback") === "status") {
      const announcement = this.querySelector("[data-codex-quantity-announcement]");
      if (!(announcement instanceof HTMLElement)) {
        return;
      }
      const quantityLabel = this.dataset.codexQuantityLabel || "Quantity";
      const message = `${quantityLabel} is ${this.inputElement.value}`;
      clearTimeout(this._quantityAnnouncementTimer);
      announcement.textContent = "";
      this._quantityAnnouncementTimer = window.setTimeout(() => {
        if (this.isConnected && announcement.isConnected) {
          announcement.textContent = message;
        }
      }, 100);
      return;
    }
    if (this.getAttribute("data-codex-quantity-a11y") === "true") {
      return;
    }
    const label = this.inputElement.getAttribute("aria-label") || Array.from(this.inputElement.labels || []).map((element) => element.textContent.trim()).join(" ") || "Quantity";
    announceStatus(`${label}: ${this.inputElement.value}`);
  }
  disconnectedCallback() {
    clearTimeout(this._quantityAnnouncementTimer);
    this._abortController.abort();
  }
};
var QuantityInput = class extends HTMLInputElement {
  constructor() {
    super();
    this.addEventListener("keydown", this._onKeyDown);
    this.addEventListener("input", this._onValueInput);
    this.addEventListener("change", this._onValueChanged);
    this.addEventListener("focus", this._onFocus);
    this.addEventListener("blur", this._onBlur);
    this.addEventListener("click", this._onPointerEdit);
    this.addEventListener("paste", this._selectForEditing);
  }
  disconnectedCallback() {
    this._abortController?.abort();
  }
  get quantity() {
    return parseInt(this.value);
  }
  set quantity(quantity) {
    const isNumeric = (typeof quantity === "number" || typeof quantity === "string" && quantity.trim() !== "") && !isNaN(quantity);
    if (quantity === "") {
      return;
    }
    if (!isNumeric || quantity < 0) {
      quantity = parseInt(quantity) || 1;
    }
    this.value = Math.max(this.min || 1, Math.min(quantity, this.max || Number.MAX_VALUE)).toString();
    if (!this.checkValidity()) {
      this.stepDown();
    }
  }
  _onValueInput() {
    delete this.dataset.codexSelectOnInput;
    this.quantity = this.value;
    if (this.dataset.codexQuantityInteraction !== "keyboard-step") {
      this.dataset.codexQuantityInteraction = "direct";
    }
    this.style.setProperty("--quantity-input-characters-count", `${this.value.toString().length}ch`);
  }
  _onFocus() {
    // Focus alone is not an editing action. Selecting here can expose a bare
    // numeric value while an unrelated dialog/status is being announced.
    this.dataset.codexSelectOnInput = "true";
  }
  _onPointerEdit() {
    // Preserve the caret/selection the user just placed with pointer or touch.
    delete this.dataset.codexSelectOnInput;
  }
  _selectForEditing() {
    if (this.dataset.codexSelectOnInput === "true") {
      delete this.dataset.codexSelectOnInput;
      this.select();
    }
  }
  _onBlur() {
    delete this.dataset.codexSelectOnInput;
  }
  _onKeyDown(event) {
    const isTextEditingKey = event.key.length === 1 || event.key === "Backspace" || event.key === "Delete";
    if (isTextEditingKey && !event.altKey && !event.ctrlKey && !event.metaKey && !event.isComposing) {
      this._selectForEditing();
    } else if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key) || (event.ctrlKey || event.metaKey) && ["a", "x"].includes(event.key.toLowerCase())) {
      // Explicit caret movement/select-all takes precedence over deferred editing.
      delete this.dataset.codexSelectOnInput;
    }
    if ((event.key === "ArrowUp" || event.key === "ArrowDown") && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
      const lineItemQuantity = this.closest("line-item-quantity");
      const lineKey = this.getAttribute("data-line-key");

      if (lineItemQuantity && lineKey && typeof lineItemQuantity._changeLineItemQuantity === "function") {
        event.preventDefault();

        if (this.dataset.codexQuantityRequestPending === "true") {
          return;
        }

        const currentQuantity = Number(this.value);
        const step = Number(this.step) || 1;
        const minimum = Number(this.min) || 1;
        const maximumAttribute = this.max === "" ? NaN : Number(this.max);
        const maximum = Number.isFinite(maximumAttribute) ? maximumAttribute : Number.MAX_SAFE_INTEGER;
        const nextQuantity = Math.max(minimum, Math.min(currentQuantity + (event.key === "ArrowUp" ? step : -step), maximum));

        if (!Number.isFinite(currentQuantity) || nextQuantity === currentQuantity) {
          return;
        }

        this.dataset.codexQuantityRequestPending = "true";
        const update = lineItemQuantity._changeLineItemQuantity(lineKey, nextQuantity, "id", "quantity-input", { announceQuantity: false });
        if (update?.catch) {
          update.catch(() => {
            if (this.isConnected) {
              this.removeAttribute("data-codex-quantity-request-pending");
            }
          });
        }
        return;
      }

      this.dataset.codexQuantityInteraction = "keyboard-step";
      const cartDrawer = this.closest("cart-drawer");
      if (cartDrawer) {
        const previousValue = this.value;
        cartDrawer._setCartUpdateBusy?.(true);
        requestAnimationFrame(() => {
          if (this.isConnected && this.value === previousValue && this.dataset.codexQuantityInteraction === "keyboard-step") {
            cartDrawer._setCartUpdateBusy?.(false);
          }
        });
      }
    } else if (event.key !== "Tab") {
      this.dataset.codexQuantityInteraction = "direct";
    }
  }
  _onValueChanged() {
    if (this.value === "") {
      this.quantity = this.min || 1;
    }
    if (!this.checkValidity()) {
      this.stepDown();
    }
    this.style.setProperty("--quantity-input-characters-count", `${this.value.toString().length}ch`);
  }
};
if (!window.customElements.get("quantity-selector")) {
  window.customElements.define("quantity-selector", QuantitySelector);
}
document.addEventListener("input", (event) => {
  const input = event.target instanceof Element ? event.target.closest(".codex-cart-quantity__input") : null;
  syncCodexQuantityButtonNames(input?.closest('[data-codex-quantity-a11y="true"]'));
});
document.addEventListener("change", (event) => {
  const input = event.target instanceof Element ? event.target.closest(".codex-cart-quantity__input") : null;
  syncCodexQuantityButtonNames(input?.closest('[data-codex-quantity-a11y="true"]'));
});
if (!window.customElements.get("quantity-input")) {
  window.customElements.define("quantity-input", QuantityInput, { extends: "input" });
}

// js/common/form/resizable-textarea.js
var ResizableTextarea = class extends HTMLTextAreaElement {
  constructor() {
    super();
    this.addEventListener("input", this._onInput);
  }
  _onInput() {
    this.style.height = "auto";
    this.style.height = this.scrollHeight + 2 + "px";
  }
};
if (!window.customElements.get("resizable-textarea")) {
  window.customElements.define("resizable-textarea", ResizableTextarea, { extends: "textarea" });
}

// js/common/list/listbox.js
var _accessibilityInitialized, _hiddenInput, _Listbox_instances, onOptionClicked_fn, onInputChanged_fn, onKeyDown_fn;
var Listbox = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _Listbox_instances);
    __privateAdd(this, _accessibilityInitialized, false);
    __privateAdd(this, _hiddenInput);
    this.addEventListener("keydown", __privateMethod(this, _Listbox_instances, onKeyDown_fn));
  }
  static get observedAttributes() {
    return ["aria-activedescendant"];
  }
  connectedCallback() {
    if (!__privateGet(this, _accessibilityInitialized)) {
      this.setAttribute("role", "listbox");
      __privateSet(this, _hiddenInput, this.querySelector('input[type="hidden"]'));
      __privateGet(this, _hiddenInput)?.addEventListener("change", __privateMethod(this, _Listbox_instances, onInputChanged_fn).bind(this));
      Array.from(this.querySelectorAll('[role="option"]')).forEach((option) => {
        option.addEventListener("click", __privateMethod(this, _Listbox_instances, onOptionClicked_fn).bind(this));
        option.id = "option-" + (crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4));
        if (option.getAttribute("aria-selected") === "true") {
          this.setAttribute("aria-activedescendant", option.id);
        }
      });
      __privateSet(this, _accessibilityInitialized, true);
    }
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "aria-activedescendant" && oldValue !== null && newValue !== oldValue) {
      Array.from(this.querySelectorAll('[role="option"]')).forEach((option) => {
        if (option.id === newValue) {
          option.setAttribute("aria-selected", "true");
          if (__privateGet(this, _hiddenInput) && __privateGet(this, _hiddenInput).value !== option.value) {
            __privateGet(this, _hiddenInput).value = option.value;
            __privateGet(this, _hiddenInput).dispatchEvent(new Event("change", { bubbles: true }));
          }
          if (this.hasAttribute("aria-owns")) {
            this.getAttribute("aria-owns").split(" ").forEach((boundId) => {
              document.getElementById(boundId).textContent = option.getAttribute("title") || option.innerText || option.value;
            });
          }
          option.dispatchEvent(new CustomEvent("listbox:change", {
            bubbles: true,
            detail: {
              value: option.value
            }
          }));
        } else {
          option.setAttribute("aria-selected", "false");
        }
      });
    }
  }
};
_accessibilityInitialized = new WeakMap();
_hiddenInput = new WeakMap();
_Listbox_instances = new WeakSet();
onOptionClicked_fn = function(event) {
  this.setAttribute("aria-activedescendant", event.currentTarget.id);
  event.currentTarget.dispatchEvent(new CustomEvent("listbox:select", {
    bubbles: true,
    detail: {
      value: event.currentTarget.value
    }
  }));
};
onInputChanged_fn = function(event) {
  this.setAttribute("aria-activedescendant", this.querySelector(`[role="option"][value="${CSS.escape(event.target.value)}"]`).id);
};
onKeyDown_fn = function(event) {
  if (event.key === "ArrowUp") {
    event.target.previousElementSibling?.focus();
    event.preventDefault();
  } else if (event.key === "ArrowDown") {
    event.target.nextElementSibling?.focus();
    event.preventDefault();
  }
};
if (!window.customElements.get("x-listbox")) {
  window.customElements.define("x-listbox", Listbox);
}

// js/common/media/image.js
function imageLoaded(imageOrArray) {
  if (!imageOrArray) {
    return Promise.resolve();
  }
  imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);
  return Promise.all(imageOrArray.map((image) => {
    return new Promise((resolve) => {
      if (image.tagName === "IMG" && image.complete || !image.offsetParent) {
        resolve();
      } else {
        image.onload = () => resolve();
      }
    });
  }));
}
function generateSrcset(imageObjectOrString, widths = []) {
  let imageUrl, maxWidth;
  if (typeof imageObjectOrString === "string") {
    imageUrl = new URL(imageObjectOrString.startsWith("//") ? `https:${imageObjectOrString}` : imageObjectOrString);
    maxWidth = parseInt(imageUrl.searchParams.get("width"));
  } else {
    imageUrl = new URL(imageObjectOrString["src"].startsWith("//") ? `https:${imageObjectOrString["src"]}` : imageObjectOrString["src"]);
    maxWidth = imageObjectOrString["width"];
  }
  return widths.filter((width) => width <= maxWidth).map((width) => {
    imageUrl.searchParams.set("width", width.toString());
    return `${imageUrl.href} ${width}w`;
  }).join(", ");
}
function createMediaImg(media, widths = [], properties = {}) {
  const image = new Image(media["preview_image"]["width"], media["preview_image"]["height"]), src = media["preview_image"]["src"], featuredMediaUrl = new URL(src.startsWith("//") ? `https:${src}` : src);
  for (const propertyKey in properties) {
    image.setAttribute(propertyKey, properties[propertyKey]);
  }
  image.alt = media["alt"];
  image.src = featuredMediaUrl.href;
  image.srcset = generateSrcset(media["preview_image"], widths);
  return image;
}

// js/common/product/gift-card-recipient.js
var _recipientCheckbox, _recipientOtherProperties, _recipientSendOnProperty, _offsetProperty, _recipientFieldsContainer, _GiftCardRecipient_instances, synchronizeProperties_fn, formatDate_fn;
var GiftCardRecipient = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _GiftCardRecipient_instances);
    __privateAdd(this, _recipientCheckbox);
    __privateAdd(this, _recipientOtherProperties, []);
    __privateAdd(this, _recipientSendOnProperty);
    __privateAdd(this, _offsetProperty);
    __privateAdd(this, _recipientFieldsContainer);
  }
  connectedCallback() {
    const properties = Array.from(this.querySelectorAll('[name*="properties"]')), checkboxPropertyName = "properties[__shopify_send_gift_card_to_recipient]";
    __privateSet(this, _recipientCheckbox, properties.find((input) => input.name === checkboxPropertyName));
    __privateSet(this, _recipientOtherProperties, properties.filter((input) => input.name !== checkboxPropertyName));
    __privateSet(this, _recipientFieldsContainer, this.querySelector(".gift-card-recipient__fields"));
    __privateSet(this, _offsetProperty, this.querySelector('[name="properties[__shopify_offset]"]'));
    if (__privateGet(this, _offsetProperty)) {
      __privateGet(this, _offsetProperty).value = (/* @__PURE__ */ new Date()).getTimezoneOffset().toString();
    }
    __privateSet(this, _recipientSendOnProperty, this.querySelector('[name="properties[Send on]"]'));
    const minDate = /* @__PURE__ */ new Date();
    const maxDate = /* @__PURE__ */ new Date();
    maxDate.setDate(minDate.getDate() + 90);
    __privateGet(this, _recipientSendOnProperty)?.setAttribute("min", __privateMethod(this, _GiftCardRecipient_instances, formatDate_fn).call(this, minDate));
    __privateGet(this, _recipientSendOnProperty)?.setAttribute("max", __privateMethod(this, _GiftCardRecipient_instances, formatDate_fn).call(this, maxDate));
    __privateGet(this, _recipientCheckbox)?.addEventListener("change", __privateMethod(this, _GiftCardRecipient_instances, synchronizeProperties_fn).bind(this));
    __privateMethod(this, _GiftCardRecipient_instances, synchronizeProperties_fn).call(this);
  }
};
_recipientCheckbox = new WeakMap();
_recipientOtherProperties = new WeakMap();
_recipientSendOnProperty = new WeakMap();
_offsetProperty = new WeakMap();
_recipientFieldsContainer = new WeakMap();
_GiftCardRecipient_instances = new WeakSet();
synchronizeProperties_fn = function() {
  __privateGet(this, _recipientOtherProperties).forEach((property) => property.disabled = !__privateGet(this, _recipientCheckbox).checked);
  __privateGet(this, _recipientFieldsContainer).toggleAttribute("hidden", !__privateGet(this, _recipientCheckbox).checked);
};
formatDate_fn = function(date) {
  const offset = date.getTimezoneOffset();
  const offsetDate = new Date(date.getTime() - offset * 60 * 1e3);
  return offsetDate.toISOString().split("T")[0];
};
if (!window.customElements.get("gift-card-recipient")) {
  window.customElements.define("gift-card-recipient", GiftCardRecipient);
}

// js/common/product/product-card.js
import { Delegate as Delegate3 } from "vendor";
var ProductCard = class extends HTMLElement {
  constructor() {
    super();
    this._delegate = new Delegate3(this);
  }
  connectedCallback() {
    this._delegate.on("change", '.product-card__swatch-list [type="radio"], .product-card__variant-list [type="radio"]', this._onSwatchChanged.bind(this));
    this._delegate.on("pointerover", '.product-card__swatch-list [type="radio"] + label, .product-card__variant-list [type="radio"] + label', this._onSwatchHovered.bind(this), true);
  }
  disconnectedCallback() {
    this._delegate.off();
  }
  async _onSwatchHovered(event, target) {
    const control = target.control;
    const primaryMediaElement = this.querySelector(".product-card__image--primary");
    if (control.hasAttribute("data-variant-media")) {
      this._createImageElement(JSON.parse(control.getAttribute("data-variant-media")), primaryMediaElement.className, primaryMediaElement.sizes);
    }
  }
  async _onSwatchChanged(event, target) {
    if (target.hasAttribute("data-variant-id")) {
      this.querySelectorAll(`a[href^="${Shopify.routes.root}products/${this.getAttribute("handle")}"`).forEach((link) => {
        const url = new URL(link.href);
        url.searchParams.set("variant", target.getAttribute("data-variant-id"));
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
    }
    if (!target.hasAttribute("data-variant-media")) {
      return;
    }
    const newMedia = JSON.parse(target.getAttribute("data-variant-media")), primaryMediaElement = this.querySelector(".product-card__image--primary"), secondaryMediaElement = this.querySelector(".product-card__image--secondary"), newPrimaryMediaElement = this._createImageElement(newMedia, primaryMediaElement.className, primaryMediaElement.sizes);
    let newSecondaryMediaElement = null;
    if (secondaryMediaElement && target.hasAttribute("data-variant-secondary-media")) {
      let newSecondaryMedia = JSON.parse(target.getAttribute("data-variant-secondary-media"));
      newSecondaryMediaElement = this._createImageElement(newSecondaryMedia, secondaryMediaElement.className, secondaryMediaElement.sizes);
      newSecondaryMediaElement.ariaHidden = "true";
    }
    if (primaryMediaElement.src !== newPrimaryMediaElement.src) {
      if (secondaryMediaElement && newSecondaryMediaElement) {
        secondaryMediaElement.replaceWith(newSecondaryMediaElement);
      }
      await primaryMediaElement.animate({ opacity: [1, 0] }, { duration: 150, easing: "ease-in", fill: "forwards" }).finished;
      await new Promise((resolve) => newPrimaryMediaElement.complete ? resolve() : newPrimaryMediaElement.onload = () => resolve());
      primaryMediaElement.replaceWith(newPrimaryMediaElement);
      newPrimaryMediaElement.animate({ opacity: [0, 1] }, { duration: 150, easing: "ease-in" });
    }
  }
  _createImageElement(media, classes, sizes) {
    return createMediaImg(media, [200, 300, 400, 500, 600, 700, 800, 1e3, 1200, 1400, 1600, 1800], { class: classes, sizes });
  }
};
if (!window.customElements.get("product-card")) {
  window.customElements.define("product-card", ProductCard);
}

// js/common/product/product-form.js
var ProductForm = class extends HTMLFormElement {
  #submitting = false;
  constructor() {
    super();
    this.addEventListener("submit", this._onSubmit);
  }
  connectedCallback() {
    this.id.disabled = false;
  }
  async _onSubmit(event) {
    event.preventDefault();
    if (this.#submitting) {
      return;
    }
    if (!this.checkValidity()) {
      this.reportValidity();
      return;
    }
    const submitter = event.submitter instanceof HTMLElement ? event.submitter : this.querySelector('[type="submit"]:focus');
    if (submitter instanceof HTMLElement) {
      this.dispatchEvent(new CustomEvent("cart:add:start", {
        bubbles: true,
        detail: {
          form: this,
          submitter
        }
      }));
    }
    const submitButtons = Array.from(this.elements).filter((button) => button.type === "submit");
    const setSubmitButtonsBusy = (isBusy) => {
      submitButtons.forEach((submitButton) => {
        isBusy ? submitButton.setAttribute("aria-busy", "true") : submitButton.removeAttribute("aria-busy");
      });
    };
    setSubmitButtonsBusy(true);
    const shouldRedirectToCart = window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart";
    const isQuickBuy = this.closest("quick-buy-drawer") !== null;
    let sectionsToBundle = [];
    if (!shouldRedirectToCart) {
      if (window.themeVariables.settings.cartType === "popover" || isQuickBuy) {
        sectionsToBundle.push("variant-added");
      }
      if (window.themeVariables.settings.cartType === "drawer") {
        document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
      }
    }
    sectionsToBundle = [...new Set(sectionsToBundle)];
    const formData = new FormData(this);
    if (sectionsToBundle.length > 0) {
      formData.set("sections", sectionsToBundle.join(","));
      formData.set("sections_url", `${Shopify.routes.root}variants/${this.id.value}`);
    }
    this.#submitting = true;
    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: "POST",
        _themeProductFormRequest: true,
        headers: {
          "X-Requested-With": "XMLHttpRequest"
          // Needed for Shopify to check inventory
        }
      });
      const responseJson = await response.json();
      if (response.ok) {
        if (shouldRedirectToCart) {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }
        const responseSections = responseJson["sections"] || {};
        const addedItems = responseJson.hasOwnProperty("items") ? responseJson["items"] : [responseJson];
        const cartContent = { sections: responseSections };
        const variantAddedEvent = new CustomEvent("variant:add", {
          bubbles: true,
          detail: {
            items: addedItems,
            cart: cartContent,
            submitter: submitter instanceof HTMLElement ? submitter : null
          }
        });
        this.dispatchEvent(variantAddedEvent);
        const cartDrawerWasUpdatedFromAddEvent = Boolean(variantAddedEvent.detail.cartDrawerUpdated);
        const cartRefreshId = ProductForm._latestCartRefreshId = (ProductForm._latestCartRefreshId || 0) + 1;
        (async () => {
          try {
            const refreshedCartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
            if (cartRefreshId !== ProductForm._latestCartRefreshId) {
              return;
            }
            refreshedCartContent["sections"] = responseSections;
            document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
              bubbles: true,
              detail: {
                baseEvent: "variant:add",
                cart: refreshedCartContent,
                items: addedItems,
                skipCartDrawerUpdate: cartDrawerWasUpdatedFromAddEvent
              }
            }));
          } catch (error) {
            if (cartRefreshId === ProductForm._latestCartRefreshId) {
              document.dispatchEvent(new CustomEvent("cart:refresh"));
            }
          }
        })();
      } else {
        this.dispatchEvent(new CustomEvent("cart:error", {
          bubbles: true,
          detail: {
            error: responseJson["description"]
          }
        }));
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      }
    } finally {
      setSubmitButtonsBusy(false);
      this.#submitting = false;
    }
  }
};
if (!window.customElements.get("product-form")) {
  window.customElements.define("product-form", ProductForm, { extends: "form" });
}

// js/common/product/product-form-listeners.js
var BuyButtons = class extends HTMLElement {
  constructor() {
    super();
    this._onCartErrorListener = this._onCartError.bind(this);
    this._onVariantAddedListener = this._onVariantAdded.bind(this);
  }
  connectedCallback() {
    this._productForm = document.forms[this.getAttribute("form")];
    this._productForm?.addEventListener("cart:error", this._onCartErrorListener);
    this._productForm?.addEventListener("variant:add", this._onVariantAddedListener);
  }
  disconnectedCallback() {
    this._productForm?.removeEventListener("cart:error", this._onCartErrorListener);
    this._productForm?.removeEventListener("variant:add", this._onVariantAddedListener);
  }
  _onVariantAdded(event) {
    if (window.themeVariables.settings.cartType === "drawer" || window.themeVariables.settings.cartType === "popover") {
      if (window.themeVariables.settings.cartType === "drawer") {
        return;
      }
      const items = event.detail.items;
      let content = [];
      items.forEach((item) => {
        content.push(window.themeVariables.strings.addedToCartWithTitle.replace("{{ product_title }}", item?.title || ""));
      });
      announceStatus(content.join(", "));
    }
  }
  _onCartError(event) {
    const errorBanner = document.createElement("div");
    errorBanner.classList.add("banner", "banner--error", "justify-center");
    errorBanner.setAttribute("role", "alert");
    errorBanner.setAttribute("aria-live", "polite");
    errorBanner.style.gridColumn = "1/-1";
    errorBanner.style.marginBottom = "1rem";
    errorBanner.innerHTML = `
      <svg role="presentation" focusable="false" width="18" height="18" class="offset-icon icon icon-error" style="--icon-height: 18px" viewBox="0 0 18 18">
        <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>
        <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
      </svg>
      
      <p>${event.detail.error}</p>
    `;
    this.before(errorBanner);
    setTimeout(async () => {
      await errorBanner.animate({ opacity: [1, 0] }, { duration: 250, fill: "forwards" }).finished;
      errorBanner.remove();
    }, 5e3);
  }
};
if (!window.customElements.get("buy-buttons")) {
  window.customElements.define("buy-buttons", BuyButtons);
}

// js/common/product/product-gallery.js
import { PhotoSwipeLightbox } from "vendor";
var ProductGallery = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this._zoomOpener = null;
    this._zoomFocusCleanup = null;
    const form = document.forms[this.getAttribute("form")];
    form?.addEventListener("product:rerender", this._onSectionRerender.bind(this), { signal: this._abortController.signal });
    form?.addEventListener("variant:change", this._onVariantChanged.bind(this), { signal: this._abortController.signal });
    this._carousels = Array.from(this.querySelectorAll("media-carousel"));
    this._pageDots = Array.from(this.querySelectorAll("page-dots"));
    this._galleryStatus = this.querySelector("[data-product-gallery-status]");
    this._viewInSpaceButton = this.querySelector("[data-shopify-xr]");
    this._customCursor = this.querySelector(".product-gallery__cursor");
    this._galleryFocusFromTab = false;
    // M-A11Y-GALLERY-001: one owner for mobile selection and media semantics.
    this._mobileGalleryQuery = window.matchMedia("(max-width: 999px)");
    this._mobileGallerySummary = this.querySelector("[data-mobile-gallery-summary]");
    this._mobileGallerySnapshot = new Map();
    this._mobileGalleryNodePositions = [];
    this._mobileGalleryLastFocusedElement = null;
    this._mobileGalleryActive = false;
    this._mobileGalleryQuery.addEventListener("change", () => this._syncGalleryTabStops(), { signal: this._abortController.signal });
    this.addEventListener("click", this._onMobileGalleryClickCapture.bind(this), { capture: true, signal: this._abortController.signal });
    this.addEventListener("click", () => this._mobileGalleryUserSelection = false, { signal: this._abortController.signal });
    ["carousel:filter", "control:filter"].forEach((type) => {
      this.addEventListener(type, this._onMobileGalleryFilter.bind(this), { capture: true, signal: this._abortController.signal });
    });
    this._carousels.forEach((carousel) => carousel.addEventListener("carousel:filter", () => {
      requestAnimationFrame(() => { if (this.isConnected) this._syncGalleryTabStops(); });
    }, { signal: this._abortController.signal }));
    this.addEventListener("carousel:change", this._onCarouselChanged, { signal: this._abortController.signal });
    this.addEventListener("focusin", this._onGalleryFocusIn.bind(this), { signal: this._abortController.signal });
    this.addEventListener("focusout", this._onGalleryFocusOut.bind(this), { signal: this._abortController.signal });
    document.addEventListener("keydown", this._onGalleryDocumentKeydown.bind(this), { capture: true, signal: this._abortController.signal });
    document.addEventListener("focusin", this._onGalleryDocumentFocusIn.bind(this), { capture: true, signal: this._abortController.signal });
    document.addEventListener("pointerdown", this._onGalleryDocumentPointerdown.bind(this), { capture: true, signal: this._abortController.signal });
    this._carousels[0]?.addEventListener("focus", this._onGalleryFocus.bind(this), { signal: this._abortController.signal });
    this._carousels[0]?.addEventListener("blur", this._onGalleryBlur.bind(this), { signal: this._abortController.signal });
    if (this._viewInSpaceButton) {
      this.addEventListener("carousel:settle", this._updateViewInSpaceButton);
    }
    if (this.hasAttribute("allow-zoom")) {
      this.addEventListener("lightbox:open", (event) => {
        const eventTarget = event.target instanceof HTMLElement ? event.target : null;
        const opener = eventTarget?.matches("[data-product-gallery-zoom], media-carousel") ? eventTarget : document.activeElement;
        this.openZoom(event.detail.index, opener);
      }, { signal: this._abortController.signal });
    }
    this._updateZoomLabels();
    this._updateGalleryControlLabels();
    this._syncGalleryTabStops();
    // ProductGallery is defined before MediaCarousel in this bundle. Image-only
    // galleries need a sync after child upgrade as well as the initial pass.
    customElements.whenDefined("media-carousel").then(() => {
      if (this.isConnected) {
        this._updateGalleryControlLabels();
        this._syncGalleryTabStops();
      }
    });
  }
  disconnectedCallback() {
    this._zoomFocusCleanup?.();
    clearTimeout(this._galleryStatusTimer);
    clearTimeout(this._zoomStatusTimer);
    this._abortController.abort();
    this._restoreMobileGallery();
  }
  _getGalleryInstructionsId() {
    if (this._mobileGalleryActive) return "";
    return this._carousels?.[0]?.getAttribute("data-gallery-instructions-id") || "";
  }
  // Mobile selectors and text live outside the horizontal media scroller.
  // VoiceOver need not discover or auto-scroll offscreen generic slide groups.
  _setMobileGalleryAttributes(element, attributes) {
    if (!(element instanceof Element)) return;
    let saved = this._mobileGallerySnapshot.get(element);
    if (!saved) this._mobileGallerySnapshot.set(element, saved = new Map());
    Object.entries(attributes).forEach(([name, value]) => {
      if (!saved.has(name)) saved.set(name, element.getAttribute(name));
      if (value === null) element.removeAttribute(name);
      else if (element.getAttribute(name) !== String(value)) element.setAttribute(name, String(value));
    });
  }
  _getMobileGalleryFocusTarget(slide) {
    if (!(slide instanceof HTMLElement) || slide.hidden) return null;
    const videoMedia = slide.querySelector("video-media");
    if (videoMedia) {
      return videoMedia.hasAttribute("loaded")
        ? videoMedia.querySelector("video, iframe")
        : videoMedia.querySelector(".video-media__play-button");
    }
    if (slide.getAttribute("data-media-type") === "model") return slide;
    const carousel = this._carousels?.[0];
    const index = carousel?.items.indexOf(slide);
    return this.querySelector("[data-mobile-gallery-picker]")?.items?.[index]
      || this.querySelector('[data-mobile-gallery-direction="1"]')
      || this._mobileGallerySummary;
  }
  _syncMobileGallery(selectedSlide) {
    const carousel = this._carousels?.[0];
    if (!carousel?.items) return; // Child element may not be upgraded yet.
    if (!this._mobileGalleryActive) {
      this._clearGalleryKeyboardInstructions();
      this._mobileGalleryActive = true;
      this._galleryFocusFromTab = false;
      clearTimeout(this._galleryStatusTimer);
      if (this._galleryStatus) this._galleryStatus.textContent = "";
    }
    const items = carousel.items.filter((slide) => !slide.hidden);
    if (!items.includes(selectedSlide)) {
      selectedSlide = items[0];
      if (selectedSlide && carousel.selectedSlide !== selectedSlide) {
        carousel.select(carousel.items.indexOf(selectedSlide), { animate: false, exact: true });
      }
    }
    const interactiveMedia = selectedSlide && selectedSlide.getAttribute("data-media-type") !== "image";
    const multipleVideos = items.filter((slide) => slide.querySelector("video-media")).length > 1;
    this._setMobileGalleryAttributes(this._mobileGallerySummary, { hidden: items.length ? null : "" });
    const hasPicker = this._pageDots.some((pageDots) => pageDots.hasAttribute("data-mobile-gallery-picker"));
    const itemDescription = selectedSlide ? this._getMobileGalleryItemLabel(selectedSlide) : "";
    const description = hasPicker ? this._getGalleryAutomaticLabel()
      : selectedSlide ? `${this._getGalleryAutomaticLabel()}. ${itemDescription}` : "";
    if (this._mobileGalleryItemDescription !== itemDescription) {
      this._mobileGalleryItemDescription = itemDescription;
      if (!this._mobileGalleryUserSelection && this._galleryStatus) this._galleryStatus.textContent = "";
    }
    if (this._mobileGallerySummary.textContent !== description) {
      this._mobileGallerySummary.textContent = description;
      if (!this._mobileGalleryUserSelection && this._galleryStatus) this._galleryStatus.textContent = "";
    }
    this._setMobileGalleryAttributes(this._galleryStatus, { "aria-hidden": null, "aria-live": "polite" });

    const activeElement = document.activeElement;
    const focusedSlide = activeElement instanceof Element ? activeElement.closest(".product-gallery__media") : null;
    // Move actual keyboard focus before hiding the old slide or making a
    // desktop image target inert at the mobile breakpoint. Keep player focus.
    if (focusedSlide && carousel.contains(focusedSlide)
      && (focusedSlide !== selectedSlide || activeElement.matches("img") || !interactiveMedia)) {
      this._mobileGallerySummary.focus({ preventScroll: true });
    }
    this._setMobileGalleryAttributes(carousel, {
      role: "group", "aria-roledescription": null,
      "aria-label": this._getGalleryAutomaticLabel(),
      "aria-hidden": interactiveMedia ? null : "true", tabindex: "-1"
    });
    this._syncMobileGalleryOrder(carousel);
    carousel.items.forEach((slide) => {
      const selected = slide === selectedSlide;
      const interactive = selected && slide.getAttribute("data-media-type") !== "image";
      this._setMobileGalleryAttributes(slide, {
        "aria-hidden": interactive ? null : "true", inert: selected ? null : "",
        tabindex: "-1", role: null, "aria-label": null, "aria-roledescription": null
      });
      // Preserve alt for PhotoSwipe. Selectors (or the free-scroll summary)
      // provide the alternative; HTML cannot disable optional OS recognition.
      slide.querySelectorAll("img").forEach((image) => this._setMobileGalleryAttributes(image, {
        "aria-hidden": "true", inert: "", tabindex: "-1", "aria-label": null
      }));
      const videoMedia = slide.querySelector("video-media");
      if (videoMedia) {
        const playButton = videoMedia.querySelector(".video-media__play-button");
        const player = videoMedia.querySelector("video, iframe");
        const target = this._getMobileGalleryFocusTarget(slide);
        const playLabel = window.themeVariables.strings.playVideo || "Play video";
        this._setMobileGalleryAttributes(playButton, { "aria-hidden": null });
        [playButton, player].forEach((control) => {
          this._setMobileGalleryAttributes(control, { tabindex: selected && control === target ? "0" : "-1" });
        });
        this._setMobileGalleryAttributes(target, {
          "aria-label": target === playButton
            ? multipleVideos ? `${playLabel}. ${this._getGalleryItemAnnouncement(slide)}` : playLabel
            : `${this._getGalleryItemAnnouncement(slide)}, video`
        });
      } else if (interactive) {
        this._setMobileGalleryAttributes(slide, { role: "group", "aria-label": this._getGalleryItemAnnouncement(slide), tabindex: "0" });
      }
    });
    this._pageDots.forEach((pageDots) => {
      const mobilePicker = pageDots.hasAttribute("data-mobile-gallery-picker");
      // Hidden desktop thumbnails also react to carousel:change. They must
      // use mobile state too, or they reintroduce aria-current on every move.
      this._setMobileGalleryAttributes(pageDots, { "aria-hidden": mobilePicker ? null : "true", "data-mobile-gallery-active": "" });
      this._setMobileGalleryAttributes(pageDots.closest("scroll-shadow"), { "aria-hidden": mobilePicker ? null : "true" });
      pageDots.items.forEach((button, index) => {
        const slide = carousel.items[index];
        const current = slide === selectedSlide;
        this._setMobileGalleryAttributes(button, {
          "aria-hidden": mobilePicker ? null : "true", tabindex: mobilePicker && slide && !slide.hidden ? "0" : "-1",
          "aria-label": slide ? this._getMobileGalleryItemLabel(slide) : null,
          "aria-current": null, "aria-disabled": mobilePicker ? String(current) : null, "data-codex-current": String(current)
        });
      });
    });
    const fallback = this.querySelector("[data-mobile-gallery-fallback]");
    this._setMobileGalleryAttributes(fallback, { hidden: items.length > 1 ? null : "" });
    fallback?.querySelectorAll("[data-mobile-gallery-direction]").forEach((button) => {
      const direction = Number(button.getAttribute("data-mobile-gallery-direction"));
      const index = items.indexOf(selectedSlide) + direction;
      this._setMobileGalleryAttributes(button, { "aria-disabled": String(index < 0 || index >= items.length) });
    });
    if (activeElement instanceof HTMLElement && this.contains(activeElement) && activeElement.closest("[hidden]")) {
      this._mobileGallerySummary.focus({ preventScroll: true });
    }
  }
  _restoreMobileGallery() {
    if (!this._mobileGalleryActive) return;
    const selectedIndex = this._carousels?.[0]?.selectedIndex;
    // Breakpoint CSS can hide the mobile controls before this callback runs,
    // leaving BODY focused. Retain only the last real in-gallery DOM focus.
    const focusedControl = document.activeElement === document.body
      ? this._mobileGalleryLastFocusedElement : document.activeElement;
    const moveFocus = focusedControl === this._mobileGallerySummary
      || this.querySelector("[data-mobile-gallery-picker]")?.contains(focusedControl)
      || this.querySelector("[data-mobile-gallery-fallback]")?.contains(focusedControl)
      || this._mobileGalleryNodePositions.some(({ node }) => node.contains(focusedControl));
    this._mobileGalleryActive = false;
    this._mobileGallerySnapshot.forEach((attributes, element) => attributes.forEach((value, name) => {
      if (value === null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    }));
    this._mobileGallerySnapshot.clear();
    this._mobileGalleryNodePositions.reverse().forEach(({ node, placeholder }) => placeholder.replaceWith(node));
    this._mobileGalleryNodePositions = [];
    this._mobileGalleryLastFocusedElement = null;
    this._mobileGalleryItemDescription = null;
    if (this._mobileGallerySummary) this._mobileGallerySummary.textContent = "";
    if (this._galleryStatus) this._galleryStatus.textContent = "";
    this._pageDots?.forEach((pageDots) => {
      if (pageDots.items) pageDots.select(selectedIndex, false);
    });
    this._updateGalleryControlLabels();
    if (moveFocus && this.isConnected) {
      const zoomStillVisible = focusedControl?.matches("[data-product-gallery-zoom]") && focusedControl.getClientRects().length;
      (zoomStillVisible ? focusedControl : this._getGalleryFocusTarget(this._carousels[0]?.selectedSlide))?.focus({ preventScroll: true });
    }
  }
  _onMobileGalleryClickCapture(event) {
    if (!this._mobileGalleryActive) return;
    const button = event.target instanceof Element ? event.target.closest("button") : null;
    if (!button || !this.contains(button)) return;
    if (button.matches(".video-media__play-button")) {
      // Native accessibility activation may arrive without prior DOM focus.
      // The existing VideoMedia handoff can then follow this original opener.
      button.focus({ preventScroll: true });
    } else if (button.closest("[data-mobile-gallery-picker]")) {
      this._mobileGalleryUserSelection = button.getAttribute("aria-disabled") !== "true";
    } else if (button.hasAttribute("data-mobile-gallery-direction") && button.getAttribute("aria-disabled") !== "true") {
      const carousel = this._carousels[0];
      const items = carousel.items.filter((slide) => !slide.hidden);
      const next = items[items.indexOf(carousel.selectedSlide) + Number(button.getAttribute("data-mobile-gallery-direction"))];
      if (next) {
        this._mobileGalleryUserSelection = true;
        carousel.select(carousel.items.indexOf(next), { animate: false, exact: true });
      }
    }
  }
  _onMobileGalleryFilter(event) {
    if (!this._mobileGalleryActive || !(event.target instanceof Element)) return;
    // Filtering can hide a focused video or picker before the next frame.
    // Preserve real keyboard focus without guessing a screen reader's cursor.
    const items = event.target.items || Array.from(event.target.children);
    const index = items.findIndex((item) => item.contains(document.activeElement));
    if (index >= 0 && event.detail.filteredIndexes.includes(index)) {
      this._mobileGallerySummary.focus({ preventScroll: true });
    }
  }
  _getMobileGalleryItemLabel(slide) {
    const label = this._getGalleryItemAnnouncement(slide);
    const type = this._getGalleryMediaType(slide);
    return type === "image" ? label : `${label}, ${type}`;
  }
  _syncMobileGalleryOrder(carousel) {
    if (this._mobileGalleryNodePositions.length) return;
    const picker = this._pageDots.find((pageDots) => pageDots.hasAttribute("data-mobile-gallery-picker"));
    const controls = picker?.closest("scroll-shadow") || picker || this.querySelector("[data-mobile-gallery-fallback]");
    const zoom = carousel.parentElement.querySelector(":scope > .product-gallery__zoom");
    const focusedElement = document.activeElement;
    // Read the existing selectors before the selected media's real controls.
    // Move no media/player nodes, and make no DOM changes during navigation.
    [zoom, controls].filter(Boolean).forEach((node) => {
      const placeholder = document.createComment("Mobile gallery control position");
      node.before(placeholder);
      this._mobileGalleryNodePositions.push({ node, placeholder });
      carousel.before(node);
    });
    if (this.isConnected && document.activeElement === document.body
      && this._mobileGalleryNodePositions.some(({ node }) => node.contains(focusedElement))) {
      focusedElement.focus({ preventScroll: true });
    }
  }
  _getGalleryAutomaticLabel() {
    return this._carousels?.[0]?.getAttribute("data-gallery-name") || "Product gallery";
  }
  _setGalleryFocusContext(active) {
    if (this._mobileGalleryActive) return;
    const carousel = this._carousels?.[0];
    if (!carousel) {
      return;
    }
    if (active) {
      carousel.removeAttribute("aria-label");
    } else {
      carousel.setAttribute("aria-label", this._getGalleryAutomaticLabel());
    }
  }
  _setGalleryFocusEntryLabel(target, slide) {
    if (!(target instanceof HTMLElement) || !slide) {
      return;
    }
    const itemLabel = this._getGalleryFocusTarget(slide)?.getAttribute("aria-label") || this._getGalleryItemAnnouncement(slide);
    target.setAttribute("aria-label", `${this._getGalleryAutomaticLabel()}. ${itemLabel}`);
  }
  _addGalleryKeyboardInstructions(target) {
    const instructionsId = this._getGalleryInstructionsId();
    if (!(target instanceof HTMLElement) || !instructionsId) {
      return;
    }
    const instructions = this.querySelector(`#${CSS.escape(instructionsId)}`);
    if (!(instructions instanceof HTMLElement)) {
      return;
    }
    // Keep the instruction out of automatic reading, but make it available
    // to assistive technology before it is referenced by aria-describedby.
    instructions.removeAttribute("hidden");
    const describedBy = (target.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean);
    if (!describedBy.includes(instructionsId)) {
      target.setAttribute("aria-describedby", [...describedBy, instructionsId].join(" "));
    }
  }
  _removeGalleryKeyboardInstructions(target) {
    const instructionsId = this._getGalleryInstructionsId();
    if (!(target instanceof HTMLElement) || !instructionsId) {
      return;
    }
    const describedBy = (target.getAttribute("aria-describedby") || "").split(/\s+/).filter((id) => id !== instructionsId);
    if (describedBy.length > 0) {
      target.setAttribute("aria-describedby", describedBy.join(" "));
    } else {
      target.removeAttribute("aria-describedby");
    }
    if (!this.querySelector(`[aria-describedby~="${CSS.escape(instructionsId)}"]`)) {
      const instructions = this.querySelector(`#${CSS.escape(instructionsId)}`);
      instructions?.setAttribute("hidden", "");
    }
  }
  _clearGalleryKeyboardInstructions() {
    const instructionsId = this._getGalleryInstructionsId();
    if (!instructionsId) {
      return;
    }
    this.querySelectorAll("[aria-describedby]").forEach((target) => this._removeGalleryKeyboardInstructions(target));
  }
  _onGalleryDocumentKeydown(event) {
    if (this._mobileGalleryActive) return;
    const carousel = this._carousels?.[0];
    if (event.key !== "Tab" || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    this._galleryFocusFromTab = true;
    const selectedSlide = carousel?.selectedSlide;
    const focusTarget = this._getGalleryFocusTarget(selectedSlide);
    this._setGalleryFocusContext(true);
    this._setGalleryFocusEntryLabel(focusTarget, selectedSlide);
    this._addGalleryKeyboardInstructions(focusTarget);
  }
  _onGalleryDocumentFocusIn(event) {
    const carousel = this._carousels?.[0];
    const target = event.target instanceof Node ? event.target : null;
    if (this._mobileGalleryActive) {
      this._mobileGalleryLastFocusedElement = target instanceof HTMLElement && this.contains(target) ? target : null;
    }
    if (!carousel || (target && carousel.contains(target))) {
      return;
    }
    this._galleryFocusFromTab = false;
    this._clearGalleryKeyboardInstructions();
    this._setGalleryFocusContext(false);
  }
  _onGalleryDocumentPointerdown(event) {
    this._galleryFocusFromTab = false;
    if (!this.contains(event.target)) this._mobileGalleryLastFocusedElement = null;
  }
  _getGalleryMediaDescription(slide) {
    return slide?.getAttribute("data-gallery-alt")?.trim() || slide?.querySelector("img")?.getAttribute("alt")?.trim() || this.getAttribute("data-gallery-label") || "Product media";
  }
  _getGalleryMediaType(slide) {
    switch (slide?.getAttribute("data-media-type")) {
      case "video":
      case "external_video":
        return "video";
      case "model":
        return "3D model";
      default:
        return "image";
    }
  }
  _getGalleryItemAnnouncement(slide = this._carousels?.[0]?.selectedSlide, includeGalleryContext = false) {
    const carousel = this._carousels?.[0];
    if (!carousel || !slide) {
      return "";
    }
    const visibleItems = carousel.visibleItems.length ? carousel.visibleItems : carousel.items.filter((item) => !item.hidden);
    const itemIndex = Math.max(0, visibleItems.indexOf(slide));
    const fallbackTemplate = "Item {{ index }} of {{ count }}: {{ description }}";
    const localizedTemplate = window.themeVariables?.strings?.accessibilityItemNth;
    const template = localizedTemplate && !/translation missing|product\.gallery\./i.test(localizedTemplate) ? localizedTemplate : fallbackTemplate;
    const description = this._getGalleryMediaDescription(slide);
    let announcement = template.replace(/\{\{\s*index\s*\}\}/g, itemIndex + 1).replace(/\{\{\s*count\s*\}\}/g, visibleItems.length).replace(/\{\{\s*description\s*\}\}/g, description);
    if (!/\{\{\s*description\s*\}\}/.test(template)) {
      announcement = `${announcement}: ${description}`;
    }
    if (!includeGalleryContext) {
      return announcement;
    }
    const galleryName = carousel.getAttribute("data-gallery-name") || "Product gallery";
    return `${galleryName}, ${announcement}, carousel`;
  }
  _getGalleryFocusTarget(slide, includeGalleryContext = false) {
    if (this._mobileGalleryActive) return this._getMobileGalleryFocusTarget(slide);
    const videoMedia = slide?.querySelector("video-media");
    if (videoMedia) {
      const playButton = videoMedia.querySelector(".video-media__play-button");
      const player = videoMedia.querySelector("video, iframe");
      const videoTarget = videoMedia.hasAttribute("loaded") ? player : playButton;
      // The poster, launch button and player share one roving gallery stop.
      videoMedia.querySelector("img")?.setAttribute("tabindex", "-1");
      [playButton, player].forEach((target) => {
        if (target && target !== videoTarget) target.tabIndex = -1;
      });
      if (videoTarget instanceof HTMLElement) {
        const itemLabel = this._getGalleryItemAnnouncement(slide);
        videoTarget.setAttribute("aria-label", videoTarget === playButton
          ? `${window.themeVariables.strings.playVideo || "Play video"}. ${itemLabel}`
          : `${itemLabel}, ${this._getGalleryMediaType(slide)}`);
        return videoTarget;
      }
    }
    const image = slide?.querySelector("img");
    if (image instanceof HTMLElement) {
      image.setAttribute("aria-label", this._getGalleryItemAnnouncement(slide));
      return image;
    }
    const galleryTarget = slide instanceof Element && slide.matches("[data-gallery-focus-target]")
      ? slide
      : slide?.querySelector("[data-gallery-focus-target]");
    if (galleryTarget instanceof HTMLElement) {
      galleryTarget.setAttribute("role", "group");
      galleryTarget.setAttribute("aria-label", `${this._getGalleryItemAnnouncement(slide)}, ${this._getGalleryMediaType(slide)}`);
      return galleryTarget;
    }
    if (!(slide instanceof HTMLElement)) {
      return null;
    }
    slide.setAttribute("role", "group");
    slide.setAttribute("aria-label", this._getGalleryItemAnnouncement(slide));
    return slide;
  }
  _syncGalleryTabStops(selectedSlide = this._carousels?.[0]?.selectedSlide, includeGalleryContext = false) {
    if (this._mobileGalleryQuery?.matches && this._mobileGallerySummary) {
      this._syncMobileGallery(selectedSlide);
      return;
    }
    this._restoreMobileGallery();
    const carousel = this._carousels?.[0];
    if (!carousel) {
      return;
    }
    const visibleItems = carousel.visibleItems;
    if (!visibleItems) {
      return; // Child custom elements may still be upgrading on connection.
    }
    selectedSlide = visibleItems.includes(selectedSlide) ? selectedSlide : visibleItems[0];
    if (!selectedSlide) {
      return;
    }
    carousel.items.forEach((slide) => {
      const focusTarget = this._getGalleryFocusTarget(slide);
      if (focusTarget instanceof HTMLElement) {
        focusTarget.tabIndex = slide === selectedSlide ? 0 : -1;
      }
    });
    const selectedFocusTarget = this._getGalleryFocusTarget(selectedSlide, includeGalleryContext);
    if (selectedFocusTarget instanceof HTMLElement) {
      selectedFocusTarget.tabIndex = 0;
    }
  }
  _focusGalleryItem(slide, attempt = 0, includeGalleryContext = false) {
    this._syncGalleryTabStops(slide, includeGalleryContext);
    const focusTarget = this._getGalleryFocusTarget(slide, includeGalleryContext);
    if (focusTarget instanceof HTMLElement && focusTarget.getClientRects().length > 0) {
      focusTarget.focus({ preventScroll: true });
      return;
    }
    if (attempt < 6 && slide?.isConnected) {
      requestAnimationFrame(() => this._focusGalleryItem(slide, attempt + 1, includeGalleryContext));
    }
  }
  _onGalleryFocusIn(event) {
    if (this._mobileGalleryActive) return;
    const carousel = this._carousels?.[0];
    const target = event.target instanceof Element ? event.target : null;
    const slide = target?.closest(".product-gallery__media");
    if (!carousel || !slide || !carousel.contains(slide) || event.relatedTarget && carousel.contains(event.relatedTarget)) {
      return;
    }
    const focusTarget = this._getGalleryFocusTarget(slide);
    if (focusTarget instanceof HTMLElement) {
      if (this._galleryFocusFromTab) {
        this._setGalleryFocusContext(true);
        this._setGalleryFocusEntryLabel(focusTarget, slide);
        this._addGalleryKeyboardInstructions(focusTarget);
      } else {
        this._setGalleryFocusContext(false);
        // _getGalleryFocusTarget supplies the actionable video name as well
        // as the image/item name; do not overwrite Play with a description.
        this._removeGalleryKeyboardInstructions(focusTarget);
      }
    }
    this._galleryFocusFromTab = false;
  }
  _onGalleryFocusOut(event) {
    if (this._mobileGalleryActive) return;
    const carousel = this._carousels?.[0];
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!carousel || !target || !carousel.contains(target) || event.relatedTarget && carousel.contains(event.relatedTarget)) {
      return;
    }
    this._removeGalleryKeyboardInstructions(target);
    this._setGalleryFocusContext(false);
  }
  _getZoomItemAnnouncement(pswp) {
    if (!pswp || typeof pswp.getItemData !== "function") {
      return "";
    }
    const itemData = pswp.getItemData(pswp.currIndex) || {};
    const localizedTemplate = window.themeVariables?.strings?.galleryImageItem;
    const template = localizedTemplate && !/translation missing|product\.gallery\./i.test(localizedTemplate) ? localizedTemplate : "Image {{ index }} of {{ count }}: {{ description }}";
    const description = itemData.alt?.trim() || itemData.thumbnailElement?.alt?.trim() || this.getAttribute("data-gallery-label") || "Product image";
    return template.replace(/\{\{\s*index\s*\}\}/g, pswp.currIndex + 1).replace(/\{\{\s*count\s*\}\}/g, pswp.getNumItems()).replace(/\{\{\s*description\s*\}\}/g, description);
  }
  _getGalleryNavigationKey(event) {
    const keyNames = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
    const eventKey = typeof event.key === "string" ? event.key : "";
    if (keyNames.includes(eventKey)) {
      return eventKey;
    }
    if (eventKey.toLowerCase() === "home") {
      return "Home";
    }
    if (eventKey.toLowerCase() === "end") {
      return "End";
    }
    const keyIdentifier = { Left: "ArrowLeft", Right: "ArrowRight", Up: "ArrowUp", Down: "ArrowDown", Home: "Home", End: "End" }[event.keyIdentifier];
    if (keyIdentifier) {
      return keyIdentifier;
    }
    if (keyNames.includes(event.code)) {
      return event.code;
    }
    if (event.code === "Numpad7") {
      return "Home";
    }
    if (event.code === "Numpad1") {
      return "End";
    }
    if (["UIKeyInputHome", "UIKeyInputEnd"].includes(event.key)) {
      return event.key === "UIKeyInputHome" ? "Home" : "End";
    }
    const keyCode = event.keyCode || event.which;
    return [36, 115].includes(keyCode) ? "Home" : [35, 119].includes(keyCode) ? "End" : null;
  }
  _announceZoomItem(status, pswp) {
    if (!(status instanceof HTMLElement)) {
      return;
    }
    clearTimeout(this._zoomStatusTimer);
    status.textContent = "";
    const announcement = this._getZoomItemAnnouncement(pswp);
    this._zoomStatusTimer = setTimeout(() => {
      if (this._photoswipe?.pswp === pswp && pswp.isOpen) {
        status.textContent = announcement;
      }
    }, 100);
  }
  _updateZoomImageLabel(pswp) {
    const image = pswp?.currSlide?.content?.element || this._getZoomDialog()?.querySelector(".pswp__img");
    if (!(image instanceof HTMLElement)) {
      return null;
    }
    image.tabIndex = -1;
    image.setAttribute("aria-label", this._getZoomItemAnnouncement(pswp));
    return image;
  }
  _focusZoomImage(pswp, attempt = 0) {
    const image = this._updateZoomImageLabel(pswp);
    if (image instanceof HTMLElement && image.getClientRects().length > 0) {
      image.focus({ preventScroll: true });
      return;
    }
    if (attempt < 6 && pswp?.isOpen) {
      requestAnimationFrame(() => this._focusZoomImage(pswp, attempt + 1));
    }
  }
  _updateGalleryControlLabels() {
    if (this._mobileGalleryActive) return; // Mobile labels belong to _syncMobileGallery.
    const carousel = this._carousels?.[0];
    if (!carousel?.items) {
      return;
    }
    this._pageDots.forEach((pageDots) => {
      const items = pageDots.items || Array.from(pageDots.children);
      items.forEach((button, index) => {
        const slide = carousel.items[index];
        if (!slide) {
          return;
        }
        const baseLabel = button.dataset.galleryBaseLabel || button.getAttribute("aria-label") || button.textContent.trim() || "Go to item";
        button.dataset.galleryBaseLabel = baseLabel;
        button.setAttribute("aria-label", `${baseLabel}: ${this._getGalleryMediaDescription(slide)}`);
      });
    });
  }
  _announceGalleryItem(slide = this._carousels?.[0]?.selectedSlide) {
    if (this._mobileGalleryActive) {
      if (this._galleryStatus) {
        this._galleryStatus.textContent = this._mobileGalleryUserSelection ? this._getGalleryItemAnnouncement(slide) : "";
      }
      return;
    }
    const carousel = this._carousels?.[0];
    if (!this._galleryStatus || !carousel || document.activeElement !== carousel || carousel._keyboardFocusTarget) {
      return;
    }
    clearTimeout(this._galleryStatusTimer);
    this._galleryStatus.removeAttribute("aria-hidden");
    this._galleryStatus.textContent = "";
    const announcement = this._getGalleryItemAnnouncement(slide);
    this._galleryStatusTimer = setTimeout(() => {
      if (carousel.contains(document.activeElement)) {
        this._galleryStatus.textContent = announcement;
      }
    }, 0);
  }
  _onGalleryFocus() {
    const carousel = this._carousels?.[0];
    const focusFromTab = this._galleryFocusFromTab;
    this._galleryFocusFromTab = false;
    requestAnimationFrame(() => {
      if (carousel && document.activeElement === carousel) {
        this._focusGalleryItem(carousel.selectedSlide);
        if (focusFromTab) {
          const focusTarget = this._getGalleryFocusTarget(carousel.selectedSlide);
          this._setGalleryFocusContext(true);
          this._setGalleryFocusEntryLabel(focusTarget, carousel.selectedSlide);
          this._addGalleryKeyboardInstructions(focusTarget);
        }
      }
    });
  }
  _onGalleryBlur(event) {
    if (event.relatedTarget && this._carousels?.[0]?.contains(event.relatedTarget)) {
      return;
    }
    this._setGalleryFocusContext(false);
    if (this._galleryStatus) {
      clearTimeout(this._galleryStatusTimer);
      this._galleryStatus.textContent = "";
      this._galleryStatus.setAttribute("aria-hidden", "true");
    }
  }
  _updateZoomLabels() {
    const zoomLabel = window.themeVariables?.strings?.zoomGallery || "Zoom";
    this.querySelectorAll("[data-product-gallery-zoom]").forEach((button) => {
      button.setAttribute("aria-label", zoomLabel);
    });
  }
  _getZoomDialog() {
    return this._photoswipe?.pswp?.element instanceof HTMLElement ? this._photoswipe.pswp.element : null;
  }
  _getZoomFocusableElements(dialog) {
    return Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((element) => {
      const style = window.getComputedStyle(element);
      return style.display !== "none" && style.visibility !== "hidden" && element.getClientRects().length > 0;
    });
  }
  _focusZoomDialog(attempt = 0) {
    const dialog = this._getZoomDialog();
    if (!(dialog instanceof HTMLElement) || !this._photoswipe?.pswp?.isOpen) {
      return;
    }
    const closeButton = dialog.querySelector(".pswp__button--close");
    if (!(closeButton instanceof HTMLElement) && attempt < 6) {
      requestAnimationFrame(() => this._focusZoomDialog(attempt + 1));
      return;
    }
    const focusTarget = closeButton instanceof HTMLElement ? closeButton : dialog;
    if (focusTarget === dialog) {
      dialog.tabIndex = -1;
    }
    if (document.activeElement !== focusTarget) {
      focusTarget.focus({ preventScroll: true });
    }
  }
  _installZoomFocusManagement(dialog) {
    this._zoomFocusCleanup?.();
    const onKeydown = (event) => {
      const key = this._getGalleryNavigationKey(event);
      const pswp = this._photoswipe?.pswp;
      const focusedElement = document.activeElement;
      if (pswp?.isOpen && key && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey && dialog.contains(focusedElement)) {
        event.preventDefault();
        event.stopPropagation();
        if (key === "Home") {
          pswp.goTo(0);
        } else if (key === "End") {
          pswp.goTo(pswp.getNumItems() - 1);
        } else if (key === "ArrowLeft" || key === "ArrowUp") {
          pswp.prev();
        } else if (key === "ArrowRight" || key === "ArrowDown") {
          pswp.next();
        }
        if (key === "Home" || key === "End") {
          requestAnimationFrame(() => this._focusZoomImage(pswp));
        }
        return;
      }
      if (event.key !== "Tab" || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }
      const focusableElements = this._getZoomFocusableElements(dialog);
      const firstFocusable = focusableElements[0] || dialog;
      const lastFocusable = focusableElements[focusableElements.length - 1] || dialog;
      const focusedImage = dialog.querySelector(".pswp__img[tabindex='-1']");
      if (!dialog.contains(focusedElement)) {
        event.preventDefault();
        event.stopPropagation();
        (event.shiftKey ? lastFocusable : firstFocusable).focus({ preventScroll: true });
      } else if (event.shiftKey && (focusedElement === firstFocusable || focusedElement === dialog || focusedElement === focusedImage)) {
        event.preventDefault();
        event.stopPropagation();
        lastFocusable.focus({ preventScroll: true });
      } else if (!event.shiftKey && (focusedElement === lastFocusable || focusedElement === dialog || focusedElement === focusedImage)) {
        event.preventDefault();
        event.stopPropagation();
        firstFocusable.focus({ preventScroll: true });
      } else {
        // Allow the browser to move between interior controls, but keep
        // PhotoSwipe's document-level Tab handler from moving focus to the
        // dialog root before the browser completes the default action.
        event.stopPropagation();
      }
    };
    const onFocusIn = (event) => {
      if (dialog.contains(event.target) || this._photoswipe?.pswp?.isDestroying) {
        return;
      }
      requestAnimationFrame(() => {
        if (this._photoswipe?.pswp?.isOpen && !dialog.contains(document.activeElement)) {
          this._focusZoomDialog();
        }
      });
    };
    dialog.addEventListener("keydown", onKeydown);
    document.addEventListener("focusin", onFocusIn, true);
    this._zoomFocusCleanup = () => {
      dialog.removeEventListener("keydown", onKeydown);
      document.removeEventListener("focusin", onFocusIn, true);
      this._zoomFocusCleanup = null;
    };
  }
  _getZoomOpener(opener) {
    const isVisibleFocusable = (element) => element instanceof HTMLElement
      && element.tabIndex >= 0
      && !element.hasAttribute("disabled")
      && element.getClientRects().length > 0;
    if (isVisibleFocusable(opener)) {
      return opener;
    }
    const visibleZoomButton = Array.from(this.querySelectorAll("[data-product-gallery-zoom]"))
      .find((button) => isVisibleFocusable(button));
    return visibleZoomButton || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  }
  get filteredIndexes() {
    return JSON.parse(this.getAttribute("filtered-indexes")).map((index) => parseInt(index) - 1);
  }
  get photoswipe() {
    if (this._photoswipe) {
      return this._photoswipe;
    }
    const photoswipe = new PhotoSwipeLightbox({
      pswpModule: () => import("photoswipe"),
      bgOpacity: 1,
      maxZoomLevel: parseInt(this.getAttribute("allow-zoom")) || 3,
      closeTitle: window.themeVariables.strings.closeGallery,
      zoomTitle: window.themeVariables.strings.zoomGallery,
      errorMsg: window.themeVariables.strings.errorGallery,
      // UX
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      zoom: false,
      closeSVG: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
        <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>
        <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>
        <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>
      </svg>`
    });
    photoswipe.addFilter("thumbEl", (thumbEl, data) => data["thumbnailElement"]);
    photoswipe.on("uiRegister", () => {
      photoswipe.pswp.ui.registerElement({
        name: "bottom-bar",
        order: 5,
        appendTo: "wrapper",
        html: `
          <div class="pagination">
            <button class="pagination__item group" rel="prev" aria-label="${window.themeVariables.strings.accessibilityPrevious}">
              <span class="animated-arrow animated-arrow--reverse"></span>
            </button>
            
            <span class="pagination__current text-sm">
              <span class="pagination__current-page">1</span> / <span class="pagination__page-count"></span>
            </span>
            
            <button class="pagination__item group" rel="next" aria-label="${window.themeVariables.strings.accessibilityNext}">
              <span class="animated-arrow"></span>
            </button>
          </div>
          <div data-product-zoom-status class="sr-only" role="status" aria-live="assertive" aria-atomic="true"></div>
        `,
        onInit: (el, pswp) => {
          const status = el.querySelector("[data-product-zoom-status]");
          el.querySelector(".pagination__page-count").innerText = pswp.getNumItems();
          el.querySelector('[rel="prev"]')?.addEventListener("click", () => pswp.prev());
          el.querySelector('[rel="next"]')?.addEventListener("click", () => pswp.next());
          pswp.on("change", () => {
            el.querySelector(".pagination__current-page").innerText = pswp.currIndex + 1;
            this._updateZoomImageLabel(pswp);
            this._announceZoomItem(status, pswp);
          });
        }
      });
    });
      photoswipe.on("afterInit", () => {
      const dialog = photoswipe.pswp.element;
      dialog?.setAttribute("role", "dialog");
      dialog?.setAttribute("aria-modal", "true");
      dialog?.setAttribute("aria-label", this.getAttribute("data-dialog-label") || window.themeVariables.strings.zoomGallery);
      if (dialog instanceof HTMLElement) {
        this._installZoomFocusManagement(dialog);
        this._focusZoomDialog();
        this._updateZoomImageLabel(photoswipe.pswp);
        this._announceZoomItem(dialog.querySelector("[data-product-zoom-status]"), photoswipe.pswp);
      }
    });
    photoswipe.on("destroy", () => {
      this._zoomFocusCleanup?.();
      const opener = this._zoomOpener;
      this._zoomOpener = null;
      requestAnimationFrame(() => {
        if (opener?.isConnected && opener.getClientRects().length > 0) {
          opener.focus({ preventScroll: true });
        }
      });
    });
    this._photoswipe = photoswipe;
    photoswipe.init();
    return photoswipe;
  }
  /**
   * Open the zoom by dynamically creating a data source based on the filtered items
   */
  openZoom(index = 0, opener = document.activeElement) {
    this._zoomOpener = this._getZoomOpener(opener);
    const dataSource = Array.from(this.querySelectorAll('.product-gallery__media[data-media-type="image"]:not([hidden]) > img')).map((image) => {
      return {
        thumbnailElement: image,
        src: image.src,
        srcset: image.srcset,
        msrc: image.currentSrc || image.src,
        width: parseInt(image.getAttribute("width")),
        height: parseInt(image.getAttribute("height")),
        alt: image.alt,
        thumbCropped: true
      };
    });
    this.photoswipe.loadAndOpen(index, dataSource);
  }
  _updateViewInSpaceButton(event) {
    if (event.detail.slide.getAttribute("data-media-type") === "model") {
      this._viewInSpaceButton.setAttribute("data-shopify-model3d-id", event.detail.slide.getAttribute("data-media-id"));
    } else {
      this._viewInSpaceButton.setAttribute("data-shopify-model3d-id", this._viewInSpaceButton.getAttribute("data-shopify-model3d-default-id"));
    }
  }
  _onCarouselChanged(event) {
    if (this._customCursor) {
      this._customCursor.toggleAttribute("hidden", event.detail.slide.getAttribute("data-media-type") !== "image");
    }
    this._syncGalleryTabStops(event.detail.slide);
    this._updateZoomLabels();
    this._updateGalleryControlLabels();
    this._announceGalleryItem(event.detail.slide);
  }
  _onVariantChanged(event) {
    if (!event.detail.variant) {
      return;
    }
    let newMediaPosition;
    if (event.detail.previousVariant === null) {
      newMediaPosition = event.detail.variant["featured_media"]["position"] - 1;
    } else if (event.detail.variant["featured_media"] && event.detail.previousVariant["featured_media"] && event.detail.previousVariant["featured_media"]["id"] !== event.detail.variant["featured_media"]["id"] || !event.detail.previousVariant["featured_media"] && event.detail.variant["featured_media"]) {
      newMediaPosition = event.detail.variant["featured_media"]["position"] - 1;
    }
    if (newMediaPosition !== void 0) {
      this._carousels.forEach((carousel) => carousel.select(newMediaPosition, { animate: false }));
    }
  }
  _onSectionRerender(event) {
    const galleryMarkup = deepQuerySelector(event.detail.htmlFragment, `${this.tagName}[form="${this.getAttribute("form")}"]`);
    if (!galleryMarkup) {
      return;
    }
    if (galleryMarkup.filteredIndex !== this.filteredIndexes) {
      this.setAttribute("filtered-indexes", galleryMarkup.getAttribute("filtered-indexes"));
      const filteredIndexes = this.filteredIndexes;
      this._carousels.forEach((carousel) => carousel.dispatchEvent(new CustomEvent("carousel:filter", { detail: { filteredIndexes } })));
      this._pageDots.forEach((pageDots) => pageDots.dispatchEvent(new CustomEvent("control:filter", { detail: { filteredIndexes } })));
    }
  }
};
var MediaCarousel = class extends ScrollCarousel {
  connectedCallback() {
    super.connectedCallback();
    this._onGestureChangedListener = this._onGestureChanged.bind(this);
    this._keyboardNavigationListener = this._onKeyboardNavigation.bind(this);
    window.addEventListener("keydown", this._keyboardNavigationListener, { capture: true, signal: this._abortController.signal });
    this._keyboardScrollResizeObserver = new ResizeObserver(() => this._syncKeyboardScrollRegion());
    this._keyboardScrollResizeObserver.observe(this);
    this._syncKeyboardScrollRegion();
    this.addEventListener("gesturestart", this._onGestureStart, { capture: false, signal: this._abortController.signal });
    this.addEventListener("carousel:settle", this._onMediaSettled, { signal: this._abortController.signal });
    this.addEventListener("carousel:filter", () => requestAnimationFrame(() => this._syncKeyboardScrollRegion()), { signal: this._abortController.signal });
    this.addEventListener("click", this._onGalleryClick);
  }
  disconnectedCallback() {
    this._keyboardScrollResizeObserver?.disconnect();
    super.disconnectedCallback();
  }
  _syncKeyboardScrollRegion() {
    if (!this._scrollArea) {
      return;
    }
    if (this.closest("product-gallery")) {
      this.tabIndex = -1;
      return;
    }
    this.tabIndex = this._scrollArea.scrollDirection === "none" ? -1 : 0;
  }
  _onKeyboardNavigation(event) {
    const eventPath = typeof event.composedPath === "function" ? event.composedPath() : [];
    const activeElement = document.activeElement;
    const focusIsInsideCarousel = activeElement === this || this.contains(activeElement) || eventPath.includes(this);
    if (!focusIsInsideCarousel || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    // Arrow/Home/End keys belong to the player's controls once it is open.
    if (eventPath.some((target) => target instanceof Element && target.matches("video, audio, iframe"))) {
      const gallery = this.closest("product-gallery");
      if (event.key === "Escape" && !document.fullscreenElement && gallery && this.selectedSlide) {
        const slide = this.selectedSlide;
        slide.tabIndex = -1;
        slide.setAttribute("role", "group");
        slide.setAttribute("aria-label", gallery._getGalleryItemAnnouncement(slide, true));
        event.preventDefault();
        event.stopPropagation();
        slide.focus({ preventScroll: true });
      }
      return;
    }
    const keyNames = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
    const keyIdentifier = { Left: "ArrowLeft", Right: "ArrowRight", Up: "ArrowUp", Down: "ArrowDown", Home: "Home", End: "End" }[event.keyIdentifier];
    const eventKey = typeof event.key === "string" ? event.key : "";
    let key = null;
    if (keyNames.includes(eventKey)) {
      key = eventKey;
    } else if (eventKey.toLowerCase() === "home") {
      key = "Home";
    } else if (eventKey.toLowerCase() === "end") {
      key = "End";
    } else if (keyIdentifier) {
      key = keyIdentifier;
    } else if (keyNames.includes(event.code)) {
      key = event.code;
    } else if (event.code === "Numpad7") {
      key = "Home";
    } else if (event.code === "Numpad1") {
      key = "End";
    } else if (["UIKeyInputHome", "UIKeyInputEnd"].includes(event.key)) {
      key = event.key === "UIKeyInputHome" ? "Home" : "End";
    } else if ([36, 115].includes(event.keyCode || event.which)) {
      key = "Home";
    } else if ([35, 119].includes(event.keyCode || event.which)) {
      key = "End";
    }
    if (!key) {
      return;
    }
    const productGallery = this.closest("product-gallery");
    if (productGallery && (key === "Home" || key === "End")) {
      return;
    }
    const visibleItems = this.visibleItems;
    const keyboardItems = visibleItems.length ? visibleItems : this.items.filter((item) => !item.hidden);
    if (!keyboardItems.length) {
      return;
    }
    const currentIndex = Math.max(0, keyboardItems.indexOf(this.selectedSlide));
    if ((key === "Home" && currentIndex === 0) || (key === "End" && currentIndex === keyboardItems.length - 1)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (keyboardItems.length < 2) {
      return;
    }
    const isPrevious = key === "ArrowLeft" || key === "ArrowUp";
    const targetIndex = key === "Home" ? 0 : key === "End" ? keyboardItems.length - 1 : Math.max(0, Math.min(keyboardItems.length - 1, currentIndex + (isPrevious ? -1 : 1)));
    const targetSlide = keyboardItems[targetIndex];
    event.preventDefault();
    event.stopPropagation();
    this._keyboardFocusTarget = productGallery ? targetSlide : null;
    if (!productGallery) {
      this.focus({ preventScroll: true });
    }
    this.select(this.items.indexOf(targetSlide), { animate: false, exact: key === "Home" || key === "End", force: key === "Home" || key === "End" });
    requestAnimationFrame(() => {
      if (!this.isConnected) {
        return;
      }
      if (productGallery) {
        productGallery._focusGalleryItem?.(targetSlide);
        this._keyboardFocusTarget = null;
      } else if (document.activeElement !== this) {
        this.focus({ preventScroll: true });
      }
    });
  }
  _onMediaSettled(event) {
    const media = event.detail.slide;
    const mobileGallery = this.closest("product-gallery")?._mobileGalleryActive;
    this.items.filter((item) => ["video", "external_video", "model"].includes(item.getAttribute("data-media-type"))
      && !(mobileGallery && item === media)).forEach((item) => item.firstElementChild.pause());
    switch (media.getAttribute("data-media-type")) {
      case "external_video":
      case "video":
        if (this.hasAttribute("autoplay") && !mobileGallery) {
          media.firstElementChild.play();
        }
        break;
      case "model":
        if (window.matchMedia("(min-width: 1000px)").matches) {
          media.firstElementChild.play();
        }
        break;
    }
  }
  _onGalleryClick(event) {
    if (event.target.matches("button, a[href], button :scope, a[href] :scope") || !window.matchMedia("screen and (pointer: fine)").matches) {
      return;
    }
    if (this.selectedSlide.getAttribute("data-media-type") !== "image") {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect(), offsetX = event.clientX - rect.left;
    offsetX > this.clientWidth / 2 ? this.next() : this.previous();
  }
  _onGestureStart(event) {
    event.preventDefault();
    this.addEventListener("gesturechange", this._onGestureChangedListener, { capture: false, signal: this._abortController.signal });
  }
  _onGestureChanged(event) {
    event.preventDefault();
    if (event.scale > 1.5) {
      const visibleImages = this.visibleItems.filter((item) => item.getAttribute("data-media-type") === "image"), openIndex = visibleImages.indexOf(this.selectedSlide);
      this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true, detail: { index: openIndex } }));
      this.removeEventListener("gesturechange", this._onGestureChangedListener);
    }
  }
};
var ProductZoomButton = class extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener("click", this._onButtonClicked);
  }
  _onButtonClicked() {
    let media = this.closest(".product-gallery__media"), openIndex;
    if (media) {
      const visibleImages = this.closest("media-carousel").visibleItems.filter((item) => item.getAttribute("data-media-type") === "image");
      openIndex = visibleImages.indexOf(media);
    } else {
      const carousel = this.closest(".product-gallery__media-list-wrapper").querySelector("media-carousel"), visibleImages = carousel.visibleItems.filter((item) => item.getAttribute("data-media-type") === "image");
      openIndex = visibleImages.indexOf(carousel.selectedSlide);
    }
    this.dispatchEvent(new CustomEvent("lightbox:open", {
      bubbles: true,
      detail: {
        index: openIndex
      }
    }));
  }
};
if (!window.customElements.get("product-gallery")) {
  window.customElements.define("product-gallery", ProductGallery);
}
if (!window.customElements.get("product-zoom-button")) {
  window.customElements.define("product-zoom-button", ProductZoomButton, { extends: "button" });
}
if (!window.customElements.get("media-carousel")) {
  window.customElements.define("media-carousel", MediaCarousel);
}

// js/common/product/product-loader.js
var ProductLoader = class {
  static loadedProducts = {};
  static load(productHandle) {
    if (!productHandle) {
      return;
    }
    if (this.loadedProducts[productHandle]) {
      return this.loadedProducts[productHandle];
    }
    this.loadedProducts[productHandle] = new Promise(async (resolve, reject) => {
      const response = await fetch(`${Shopify.routes.root}products/${productHandle}.js`);
      if (response.ok) {
        const responseAsJson = await response.json();
        resolve(responseAsJson);
      } else {
        reject(`
          Attempted to load information for product with handle ${productHandle}, but this product is in "draft" mode. You won't be able to
          switch between variants or access to per-variant information. To fully preview this product, change temporarily its status
          to "active".
        `);
      }
    });
    return this.loadedProducts[productHandle];
  }
};

// js/common/product/quick-add.js
var ProductQuickAdd = class extends HTMLElement {
  #scopeFromPassed = false;
  #scopeToReached = false;
  #intersectionObserver = new IntersectionObserver(this._onObserved.bind(this));
  connectedCallback() {
    this._scopeFrom = document.getElementById(this.getAttribute("form"));
    this._scopeTo = document.querySelector(".footer");
    if (!this._scopeFrom || !this._scopeTo) {
      return;
    }
    this.#intersectionObserver.observe(this._scopeFrom);
    this.#intersectionObserver.observe(this._scopeTo);
  }
  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
  }
  _onObserved(entries) {
    entries.forEach((entry) => {
      if (entry.target === this._scopeFrom) {
        this.#scopeFromPassed = entry.boundingClientRect.bottom < 0;
      }
      if (entry.target === this._scopeTo) {
        this.#scopeToReached = entry.isIntersecting;
      }
    });
    this.classList.toggle("is-visible", this.#scopeFromPassed && !this.#scopeToReached);
  }
};
if (!window.customElements.get("product-quick-add")) {
  window.customElements.define("product-quick-add", ProductQuickAdd);
}

// js/common/product/product-rerender.js
var _abortController3, _ProductRerender_instances, onRerender_fn;
var ProductRerender = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductRerender_instances);
    __privateAdd(this, _abortController3);
  }
  connectedCallback() {
    __privateSet(this, _abortController3, new AbortController());
    if (!this.id || !this.hasAttribute("observe-form")) {
      console.warn('The <product-rerender> requires an ID to identify the element to re-render, and an "observe-form" attribute referencing to the form to monitor.');
    }
    document.forms[this.getAttribute("observe-form")].addEventListener("product:rerender", __privateMethod(this, _ProductRerender_instances, onRerender_fn).bind(this), { signal: __privateGet(this, _abortController3).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController3).abort();
    this._productFocusGuard?.cancel();
  }
};
_abortController3 = new WeakMap();
_ProductRerender_instances = new WeakSet();
onRerender_fn = function(event) {
  const matchingElement = deepQuerySelector(event.detail.htmlFragment, `#${this.id}`);
  if (!matchingElement) {
    return;
  }
  const pendingFocus = this._productFocusGuard?.canRestore() ? this._productFocusState : null;
  const focusedElement = pendingFocus?.element || document.activeElement;
  const focusedElementId = pendingFocus?.id || (focusedElement instanceof HTMLElement && this.contains(focusedElement) ? focusedElement.id : "");
  const fullRerender = !this.hasAttribute("allow-partial-rerender") || event.detail.productChange;
  const focusScope = fullRerender ? matchingElement : this;
  this._productFocusGuard?.cancel();
  const focusGuard = focusedElementId ? createFocusRestorationGuard() : null;
  const updateVersion = (this._productUpdateVersion || 0) + 1;
  focusScope._productUpdateVersion = updateVersion;
  focusScope._productFocusGuard = focusGuard;
  focusScope._productFocusState = focusedElementId ? { element: focusedElement, id: focusedElementId } : null;
  focusScope.setAttribute("aria-busy", "true");
  if (fullRerender) {
    this.replaceWith(matchingElement);
  } else {
    const blockTypes = ["sku", "badges", "price", "custom-price", "payment-terms", "variant-picker", "quantity-selector", "volume-pricing", "inventory", "buy-buttons", "pickup-availability", "liquid"];
    blockTypes.forEach((blockType) => {
      this.querySelectorAll(`[data-block-type="${blockType}"]`).forEach((element) => {
        const matchingBlock = matchingElement.querySelector(`[data-block-type="${blockType}"][data-block-id="${element.getAttribute("data-block-id")}"]`);
        if (matchingBlock) {
          matchingBlock.querySelectorAll("noscript").forEach((noscript) => {
            noscript.remove();
          });
          if (blockType === "buy-buttons") {
            element.querySelector("buy-buttons").replaceWith(matchingBlock.querySelector("buy-buttons"));
          } else if (blockType === "quantity-selector") {
            const existingQuantity = element.querySelector(".quantity-selector__input")?.quantity;
            element.replaceWith(matchingBlock);
            const newSelector = matchingBlock.querySelector(".quantity-selector__input");
            if (newSelector) {
              newSelector.quantity = existingQuantity;
            }
          } else {
            element.replaceWith(matchingBlock);
          }
        }
      });
    });
  }
  requestAnimationFrame(() => {
    if (!focusScope.isConnected || focusScope._productUpdateVersion !== updateVersion) {
      focusGuard?.cancel();
      return;
    }
    if (focusedElementId && !focusedElement.isConnected && focusGuard?.canRestore()) {
      const element = document.getElementById(focusedElementId);
      if (element instanceof HTMLElement && focusScope.contains(element)) {
        element.focus({ preventScroll: true });
      }
    }
    focusGuard?.cancel();
    focusScope._productFocusState = null;
    requestAnimationFrame(() => {
      if (focusScope._productUpdateVersion === updateVersion) {
        focusScope.removeAttribute("aria-busy");
      }
    });
  });
};
if (!window.customElements.get("product-rerender")) {
  window.customElements.define("product-rerender", ProductRerender);
}

// js/common/product/quick-buy-drawer.js
import { animate as animate7, timeline as timeline5 } from "vendor";
var QuickBuyDrawer = class extends Drawer {
  constructor() {
    super();
    this._hasLoaded = false;
    this.addEventListener("variant:add", this._onVariantAdded.bind(this));
  }
  async show() {
    this.style.display = "block";
    if (!this._hasLoaded) {
      [this, ...this.controls].forEach((control) => control.setAttribute("aria-busy", "true"));
      const responseContent = await (await fetch(`${window.Shopify.routes.root}products/${this.getAttribute("handle")}`)).text();
      [this, ...this.controls].forEach((control) => control.setAttribute("aria-busy", "false"));
      const quickBuyContent = new DOMParser().parseFromString(responseContent, "text/html").getElementById("quick-buy-content").content;
      Array.from(quickBuyContent.querySelectorAll("noscript")).forEach((noScript) => noScript.remove());
      this.replaceChildren(quickBuyContent);
      Shopify.PaymentButton?.init();
      this._hasLoaded = true;
    }
    return super.show();
  }
  async hide() {
    return super.hide()?.then(() => {
      this.style.display = "none";
    });
  }
  _onVariantAdded(event) {
    event.detail.blockCartDrawerOpening = true;
    const contentShadow = this.shadowRoot.querySelector('[part="content"]'), fromHeight = contentShadow.clientHeight;
    animate7(contentShadow.children, { opacity: 0, visibility: "hidden" }, { duration: 0.15 });
    this.replaceChildren(...new DOMParser().parseFromString(event.detail.cart["sections"]["variant-added"], "text/html").querySelector(".shopify-section").children);
    requestAnimationFrame(async () => {
      await timeline5([
        [contentShadow, { height: [`${fromHeight}px`, `${contentShadow.clientHeight}px`] }, { duration: 0.35, easing: [0.86, 0, 0.07, 1] }],
        [contentShadow.children, { opacity: [0, 1], visibility: "visible" }, { duration: 0.15 }]
      ]).finished;
      contentShadow.style.height = null;
    });
    this._hasLoaded = false;
  }
};
if (!window.customElements.get("quick-buy-drawer")) {
  window.customElements.define("quick-buy-drawer", QuickBuyDrawer);
}

// js/common/product/variant-picker.js
import { Delegate as Delegate4 } from "vendor";
var CACHE_EVICTION_TIME = 1e3 * 60 * 5;
var _preloadedHtml, _delegate, _intersectionObserver, _form, _selectedVariant, _VariantPicker_instances, getActiveOptionValues_fn, getOptionValuesFromOption_fn, onOptionChanged_fn, onOptionPreload_fn, onIntersection_fn, renderForCombination_fn, createHashKeyForHtml_fn;
var _VariantPicker = class _VariantPicker extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _VariantPicker_instances);
    __privateAdd(this, _delegate, new Delegate4(document.body));
    __privateAdd(this, _intersectionObserver, new IntersectionObserver(__privateMethod(this, _VariantPicker_instances, onIntersection_fn).bind(this)));
    __privateAdd(this, _form);
    __privateAdd(this, _selectedVariant);
  }
  async connectedCallback() {
    __privateSet(this, _selectedVariant, JSON.parse(this.querySelector("script[data-variant]")?.textContent || "{}"));
    __privateSet(this, _form, document.forms[this.getAttribute("form-id")]);
    __privateGet(this, _delegate).on("change", `input[data-option-position][form="${this.getAttribute("form-id")}"]`, __privateMethod(this, _VariantPicker_instances, onOptionChanged_fn).bind(this));
    __privateGet(this, _delegate).on("pointerenter", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, __privateMethod(this, _VariantPicker_instances, onOptionPreload_fn).bind(this), true);
    __privateGet(this, _delegate).on("touchstart", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, __privateMethod(this, _VariantPicker_instances, onOptionPreload_fn).bind(this), true);
    __privateGet(this, _intersectionObserver).observe(this);
  }
  disconnectedCallback() {
    __privateGet(this, _delegate).off();
    __privateGet(this, _intersectionObserver).unobserve(this);
  }
  get selectedVariant() {
    return __privateGet(this, _selectedVariant);
  }
  get productHandle() {
    return this.getAttribute("handle");
  }
  get updateUrl() {
    return this.hasAttribute("update-url");
  }
  /**
   * Select a variant using a list of option values. The list of option values might lead to no variant (for instance)
   * in the case of a combination that does not exist
   */
  async selectCombination({ optionValues, productChange }) {
    const previousVariant = this.selectedVariant;
    const newContent = document.createRange().createContextualFragment(await __privateMethod(this, _VariantPicker_instances, renderForCombination_fn).call(this, optionValues));
    if (!productChange) {
      const newVariantPicker = deepQuerySelector(newContent, `${this.tagName}[form-id="${this.getAttribute("form-id")}"]`);
      const newVariant = JSON.parse(newVariantPicker.querySelector("script[data-variant]")?.textContent || "{}");
      __privateSet(this, _selectedVariant, newVariant);
      __privateGet(this, _form).id.value = __privateGet(this, _selectedVariant)?.id;
      __privateGet(this, _form).id.dispatchEvent(new Event("change", { bubbles: true }));
      if (this.updateUrl && __privateGet(this, _selectedVariant)?.id) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("variant", __privateGet(this, _selectedVariant).id);
        window.history.replaceState({ path: newUrl.toString() }, "", newUrl.toString());
      }
    }
    __privateGet(this, _form).dispatchEvent(new CustomEvent("product:rerender", {
      detail: {
        htmlFragment: newContent,
        productChange
      }
    }));
    if (!productChange) {
      __privateGet(this, _form).dispatchEvent(new CustomEvent("variant:change", {
        bubbles: true,
        detail: {
          formId: __privateGet(this, _form).id,
          variant: __privateGet(this, _selectedVariant),
          previousVariant
        }
      }));
    }
    Shopify?.PaymentButton?.init();
  }
};
_preloadedHtml = new WeakMap();
_delegate = new WeakMap();
_intersectionObserver = new WeakMap();
_form = new WeakMap();
_selectedVariant = new WeakMap();
_VariantPicker_instances = new WeakSet();
/**
 * Get the option values for the active combination
 */
getActiveOptionValues_fn = function() {
  return Array.from(__privateGet(this, _form).elements).filter((item) => item.matches("input[data-option-position]:checked")).sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input) => input.value);
};
/**
 * Get the option values for a given input
 */
getOptionValuesFromOption_fn = function(input) {
  const optionValues = [input, ...Array.from(__privateGet(this, _form).elements).filter((item) => item.matches(`input[data-option-position]:not([name="${input.name}"]):checked`))].sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input2) => input2.value);
  return optionValues;
};
onOptionChanged_fn = async function(event) {
  if (!event.target.name.includes("option")) {
    return;
  }
  this.selectCombination({
    optionValues: __privateMethod(this, _VariantPicker_instances, getActiveOptionValues_fn).call(this),
    productChange: event.target.hasAttribute("data-product-url")
  });
};
/**
 * To improve the user experience, we preload a variant whenever the user hovers over a specific option
 */
onOptionPreload_fn = function(event, target) {
  __privateMethod(this, _VariantPicker_instances, renderForCombination_fn).call(this, __privateMethod(this, _VariantPicker_instances, getOptionValuesFromOption_fn).call(this, target.control));
};
/**
 * When the variant picker is intersecting the viewport, we preload the options to improve the user experience
 * so that switching variants is nearly instant
 */
onIntersection_fn = function(entries) {
  const prerenderOptions = () => {
    Array.from(__privateGet(this, _form).elements).filter((item) => item.matches("input[data-option-position]:not(:checked)")).forEach((input) => {
      __privateMethod(this, _VariantPicker_instances, renderForCombination_fn).call(this, __privateMethod(this, _VariantPicker_instances, getOptionValuesFromOption_fn).call(this, input));
    });
  };
  if (entries[0].isIntersecting) {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(prerenderOptions, { timeout: 2e3 });
    } else {
      prerenderOptions();
    }
  }
};
renderForCombination_fn = async function(optionValues) {
  const optionValuesAsString = optionValues.join(",");
  const hashKey = __privateMethod(this, _VariantPicker_instances, createHashKeyForHtml_fn).call(this, optionValuesAsString);
  let productUrl = `${Shopify.routes.root}products/${this.productHandle}`;
  for (const optionValue of optionValues) {
    const inputForOptionValue = Array.from(__privateGet(this, _form).elements).find((item) => item.matches(`input[value="${optionValue}"]`));
    if (inputForOptionValue?.dataset.productUrl) {
      productUrl = inputForOptionValue.dataset.productUrl;
      break;
    }
  }
  if (!__privateGet(_VariantPicker, _preloadedHtml).has(hashKey)) {
    const sectionQueryParam = this.getAttribute("context") === "quick_buy" ? "" : `&section_id=${this.getAttribute("section-id")}`;
    const promise = new Promise(async (resolve) => {
      resolve(await (await fetch(`${productUrl}?option_values=${optionValuesAsString}${sectionQueryParam}`)).text());
    });
    __privateGet(_VariantPicker, _preloadedHtml).set(hashKey, { htmlPromise: promise, timestamp: Date.now() });
    if (__privateGet(_VariantPicker, _preloadedHtml).size > 100) {
      __privateGet(_VariantPicker, _preloadedHtml).delete(Array.from(__privateGet(_VariantPicker, _preloadedHtml).keys())[0]);
    }
  }
  return __privateGet(_VariantPicker, _preloadedHtml).get(hashKey).htmlPromise;
};
createHashKeyForHtml_fn = function(optionValuesAsString) {
  return `${optionValuesAsString}-${this.getAttribute("section-id")}`;
};
__privateAdd(_VariantPicker, _preloadedHtml, /* @__PURE__ */ new Map());
var VariantPicker = _VariantPicker;
if (!window.customElements.get("variant-picker")) {
  window.customElements.define("variant-picker", VariantPicker);
}

// js/common/media/base-media.js
import { inView as inView4 } from "vendor";
var BaseMedia = class extends HTMLElement {
  static get observedAttributes() {
    return ["playing"];
  }
  connectedCallback() {
    this._abortController = new AbortController();
    if (this.hasAttribute("autoplay")) {
      inView4(this, this.play.bind(this), { margin: "0px 0px 0px 0px" });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get playing() {
    return this.hasAttribute("playing");
  }
  get player() {
    return this._playerProxy = this._playerProxy || new Proxy(this._playerTarget(), {
      get: (target, prop) => {
        return async () => {
          target = await target;
          this._playerHandler(target, prop);
        };
      }
    });
  }
  play() {
    if (!this.playing) {
      this.player.play();
    }
  }
  pause() {
    if (this.playing) {
      this.player.pause();
    }
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "playing") {
      if (oldValue === null && newValue === "") {
        this.dispatchEvent(new CustomEvent("media:play", { bubbles: true }));
        if (this.hasAttribute("group")) {
          Array.from(document.querySelectorAll(`[group="${this.getAttribute("group")}"]`)).filter((item) => item !== this).forEach((itemToPause) => {
            itemToPause.pause();
          });
        }
      } else if (newValue === null) {
        this.dispatchEvent(new CustomEvent("media:pause", { bubbles: true }));
      }
    }
  }
};

// js/common/media/model.js
var ModelMedia = class extends BaseMedia {
  connectedCallback() {
    super.connectedCallback();
    this.player;
  }
  _playerTarget() {
    return new Promise((resolve) => {
      this.setAttribute("loaded", "");
      window.Shopify.loadFeatures([
        {
          name: "shopify-xr",
          version: "1.0",
          onLoad: this._setupShopifyXr.bind(this)
        },
        {
          name: "model-viewer-ui",
          version: "1.0",
          onLoad: () => {
            const modelViewer = this.querySelector("model-viewer");
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_play", () => this.setAttribute("playing", ""));
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_pause", () => this.removeAttribute("playing"));
            this.setAttribute("can-play", "");
            resolve(new window.Shopify.ModelViewerUI(modelViewer, { focusOnPlay: true }));
          }
        }
      ]);
    });
  }
  _playerHandler(target, prop) {
    target[prop]();
  }
  async _setupShopifyXr() {
    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", this._setupShopifyXr.bind(this));
    } else {
      const models = (await ProductLoader.load(this.getAttribute("handle")))["media"].filter((media) => media["media_type"] === "model");
      window.ShopifyXR.addModels(models);
      window.ShopifyXR.setupXRElements();
    }
  }
};
if (!window.customElements.get("model-media")) {
  window.customElements.define("model-media", ModelMedia);
}

// js/common/media/video.js
var onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});
var VideoMedia = class extends BaseMedia {
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute("autoplay")) {
      this.addEventListener("click", this.play, { once: true, signal: this._abortController.signal });
      this.nextElementSibling?.querySelector(".video-play-button")?.addEventListener("click", this.play.bind(this), { once: true, signal: this._abortController.signal });
    }
    if (this.hasAttribute("show-play-button") && !this.hasAttribute("autoplay") && !this.querySelector(".video-media__play-button")) {
      const playButton = document.createElement("button");
      playButton.type = "button";
      playButton.className = "video-media__play-button";
      playButton.setAttribute("aria-label", window.themeVariables.strings.playVideo || "Play video");
      playButton.appendChild(document.createRange().createContextualFragment(`
        <svg aria-hidden="true" focusable="false" fill="none" width="48" height="48" viewBox="0 0 48 48">
          <path d="M48 24C48 10.745 37.255 0 24 0S0 10.745 0 24s10.745 24 24 24 24-10.745 24-24Z" fill="${window.themeVariables.settings.pageBackground}"/>
          <path d="M18.578 32.629a.375.375 0 0 1-.578-.316V15.687c0-.297.328-.476.578-.316l12.931 8.314c.23.147.23.483 0 .63L18.578 32.63Z" fill="${window.themeVariables.settings.textColor}"/>
          <path d="M24 .5C36.979.5 47.5 11.021 47.5 24S36.979 47.5 24 47.5.5 36.979.5 24 11.021.5 24 .5Z" stroke="${window.themeVariables.settings.textColor}" stroke-opacity=".12"/>
        </svg>
      `));
      this.appendChild(playButton);
    }
    this.closest("product-gallery")?._syncGalleryTabStops();
  }
  play({ restart = false } = {}) {
    if (restart && !this.hasAttribute("host")) {
      this.querySelector("video").currentTime = 0;
    }
    super.play();
  }
  _revealPlayer(player) {
    const playButton = this.querySelector(".video-media__play-button");
    const moveFocus = playButton && document.activeElement === playButton;
    this.setAttribute("loaded", "");
    const gallery = this.closest("product-gallery");
    gallery?._removeGalleryKeyboardInstructions(playButton);
    gallery?._syncGalleryTabStops();
    // Synchronous handoff cannot steal focus after a delayed provider load.
    if (moveFocus && player instanceof HTMLElement) {
      if (player.matches("video")) gallery?._addGalleryKeyboardInstructions(player);
      player.focus({ preventScroll: true });
    }
  }
  _playerTarget() {
    if (this.hasAttribute("host")) {
      return new Promise(async (resolve) => {
        const templateElement = this.querySelector("template");
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
        }
        this._revealPlayer(this.querySelector("iframe"));
        const muteVideo = this.hasAttribute("autoplay") || window.matchMedia("screen and (max-width: 999px)").matches;
        const script = document.createElement("script");
        script.type = "text/javascript";
        if (this.getAttribute("host") === "youtube") {
          if (!window.YT || !window.YT.Player) {
            script.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          await onYouTubePromise;
          this.setAttribute("can-play", "");
          const player = new YT.Player(this.querySelector("iframe"), {
            events: {
              "onReady": () => {
                if (muteVideo) {
                  player.mute();
                }
                resolve(player);
              },
              "onStateChange": (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute("playing", "");
                } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute("playing");
                }
              }
            }
          });
        }
        if (this.getAttribute("host") === "vimeo") {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = "https://player.vimeo.com/api/player.js";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          const player = new Vimeo.Player(this.querySelector("iframe"));
          if (muteVideo) {
            player.setMuted(true);
          }
          this.setAttribute("can-play", "");
          player.on("play", () => {
            this.setAttribute("playing", "");
          });
          player.on("pause", () => this.removeAttribute("playing"));
          player.on("ended", () => this.removeAttribute("playing"));
          resolve(player);
        }
      });
    } else {
      const videoElement = this.querySelector("video");
      this._revealPlayer(videoElement);
      this.setAttribute("can-play", "");
      videoElement.addEventListener("play", () => {
        this.setAttribute("playing", "");
        this.removeAttribute("suspended");
      });
      videoElement.addEventListener("pause", () => {
        if (!videoElement.seeking && videoElement.paused) {
          this.removeAttribute("playing");
        }
      });
      return videoElement;
    }
  }
  _playerHandler(target, prop) {
    const gallery = this.closest("product-gallery");
    const slide = this.closest(".product-gallery__media");
    // A provider can become ready after the user has previewed another item.
    // Resolve the current selection before honoring its pending play request.
    if (prop === "play" && gallery && (!this.isConnected
      || (gallery._mobileGalleryActive && (slide?.hidden || gallery._carousels[0]?.selectedSlide !== slide)))) {
      prop = "pause";
    }
    if (this.getAttribute("host") === "youtube") {
      prop === "play" ? target.playVideo() : target.pauseVideo();
    } else {
      if (prop === "play" && !this.hasAttribute("host")) {
        target.play().catch((error) => {
          if (error.name === "NotAllowedError") {
            this.setAttribute("suspended", "");
            target.controls = true;
            const replacementImageSrc = target.previousElementSibling?.currentSrc;
            if (replacementImageSrc) {
              target.poster = replacementImageSrc;
            }
          }
        });
      } else {
        target[prop]();
      }
    }
  }
};
if (!window.customElements.get("video-media")) {
  window.customElements.define("video-media", VideoMedia);
}

// js/common/navigation/animated-details.js
var AnimatedDetails = class extends HTMLDetailsElement {
  constructor() {
    super();
    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;
    this._open = this.hasAttribute("open");
    this.summaryElement.addEventListener("click", this._onSummaryClicked.bind(this));
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", () => this.open = true);
      this.addEventListener("shopify:block:deselect", () => this.open = false);
    }
  }
  set open(value) {
    if (value !== this._open) {
      this._open = value;
      if (this.isConnected) {
        this._transition(value);
      } else {
        value ? this.setAttribute("open", "") : this.removeAttribute("open");
      }
    }
  }
  get open() {
    return this._open;
  }
  _onSummaryClicked(event) {
    event.preventDefault();
    this.open = !this.open;
  }
  _transition(value) {
  }
};

// js/common/navigation/accordion-disclosure.js
var AccordionDisclosure = class extends AnimatedDetails {
  _transition(value) {
    // Keep the native disclosure state in step with activation so assistive
    // technology receives the collapsed state before the visual transition.
    this.contentElement.inert = !value;
    value ? this.setAttribute("open", "") : this.removeAttribute("open");
  }
};
if (!window.customElements.get("accordion-disclosure")) {
  window.customElements.define("accordion-disclosure", AccordionDisclosure, { extends: "details" });
}

// js/common/navigation/tabs.js
import { animate as animate8 } from "vendor";
var _Tabs_instances, setupComponent_fn, onSlotChange_fn;
var Tabs = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _Tabs_instances);
    if (!this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(this.querySelector("template").content.cloneNode(true));
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.selectedIndex = this.buttons.indexOf(event.target));
    }
    this.shadowRoot.addEventListener("slotchange", __privateMethod(this, _Tabs_instances, onSlotChange_fn).bind(this));
    this.addEventListener("keydown", this._handleKeyboard);
  }
  static get observedAttributes() {
    return ["selected-index"];
  }
  connectedCallback() {
    this._abortController = new AbortController();
    __privateMethod(this, _Tabs_instances, setupComponent_fn).call(this);
    this.selectedIndex = this.selectedIndex;
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get animationDuration() {
    return this.hasAttribute("animation-duration") ? parseFloat(this.getAttribute("animation-duration")) : 0.15;
  }
  get selectedIndex() {
    return parseInt(this.getAttribute("selected-index")) || 0;
  }
  set selectedIndex(index) {
    this.setAttribute("selected-index", Math.min(Math.max(index, 0), this.buttons.length - 1).toString());
    this.style.setProperty("--selected-index", this.selectedIndex.toString());
    this.style.setProperty("--item-count", this.buttons.length.toString());
  }
  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons?.forEach((button, index) => button.setAttribute("aria-selected", index === parseInt(newValue) ? "true" : "false"));
    if (name === "selected-index" && oldValue !== null && oldValue !== newValue) {
      let fromPanel = !this.hasAttribute("external-panels") ? this.panels[parseInt(oldValue)] : document.getElementById(this.buttons[parseInt(oldValue)]?.getAttribute("controlled-panel"));
      let toPanel = !this.hasAttribute("external-panels") ? this.panels[parseInt(newValue)] : document.getElementById(this.buttons[parseInt(newValue)]?.getAttribute("controlled-panel"));
      if (fromPanel && toPanel) {
        this._transition(fromPanel, toPanel);
      }
    }
  }
  _setupAccessibility() {
    const componentID = crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4);
    this.buttons?.forEach((button, index) => {
      button.setAttribute("role", "tab");
      button.id = `tab-${componentID}-${index}`;
      const panel = this.hasAttribute("external-panels") ? document.getElementById(button.getAttribute("controlled-panel")) : this.panels?.[index];
      if (panel) {
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", button.id);
        if (!panel.id) {
          panel.id = `tab-panel-${componentID}-${index}`;
        }
        button.setAttribute("aria-controls", panel.id);
        panel.hidden = index !== this.selectedIndex;
      }
    });
  }
  /**
   * As per https://www.w3.org/WAI/ARIA/apg/example-index/tabs/tabs-automatic.html, when a tab is currently focused,
   * left and right arrow should switch the tab
   */
  _handleKeyboard(event) {
    const index = this.buttons.indexOf(document.activeElement);
    if (index === -1 || !["ArrowLeft", "ArrowRight"].includes(event.key)) {
      return;
    }
    if (event.key === "ArrowLeft") {
      this.selectedIndex = (this.selectedIndex - 1 + this.buttons.length) % this.buttons.length;
    } else {
      this.selectedIndex = (this.selectedIndex + 1 + this.buttons.length) % this.buttons.length;
    }
    this.buttons[this.selectedIndex].focus();
  }
  /**
   * Perform a custom transition (can be overridden in subclasses). To "from" and "to" are hash representing the panel
   */
  async _transition(fromPanel, toPanel) {
    await animate8(fromPanel, { opacity: [1, 0] }, { duration: this.animationDuration }).finished;
    fromPanel.hidden = true;
    toPanel.hidden = false;
    await animate8(toPanel, { opacity: [0, 1] }, { duration: this.animationDuration }).finished;
  }
};
_Tabs_instances = new WeakSet();
setupComponent_fn = function() {
  this.buttons = Array.from(this.shadowRoot.querySelector('slot[name="title"]').assignedNodes(), (item) => item.matches("button") && item || item.querySelector("button"));
  this.panels = this.shadowRoot.querySelector('slot[name="content"]') ? Array.from(this.shadowRoot.querySelector('slot[name="content"]').assignedNodes()) : null;
  this.buttons.forEach((button, index) => button.addEventListener("click", () => this.selectedIndex = index, { signal: this._abortController.signal }));
  if (this.hasAttribute("external-panels")) {
    Array.from(this.buttons).forEach((button, index) => {
      button.setAttribute("aria-selected", (this.selectedIndex === index).toString());
      const panel = document.getElementById(button.getAttribute("controlled-panel"));
      if (panel) {
        panel.hidden = !(this.selectedIndex === index);
      }
    });
  }
  this._setupAccessibility();
};
onSlotChange_fn = function() {
  __privateMethod(this, _Tabs_instances, setupComponent_fn).call(this);
};
if (!window.customElements.get("x-tabs")) {
  window.customElements.define("x-tabs", Tabs);
}

// js/common/search/predictive-search.js
import { animate as animate9 } from "vendor";
var PredictiveSearch = class extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(document.createRange().createContextualFragment(`<slot name="idle"></slot>`));
  }
  connectedCallback() {
    this._searchForm = this.closest("form");
    this._queryInput = this._searchForm.elements["q"];
    this._searchForm.addEventListener("submit", this._onFormSubmitted.bind(this));
    this._searchForm.addEventListener("reset", this._onSearchCleared.bind(this));
    this._queryInput.addEventListener("input", debounce(this._onInputChanged.bind(this), this.autoCompleteDelay));
  }
  /**
   * Return the delay in ms before we send the autocomplete request. Using a value too low can cause the results to
   * refresh too often, so we recommend to keep the default one
   */
  get autoCompleteDelay() {
    return 280;
  }
  /**
   * Check if the store supports the predictive API (some languages do not). When not supported, the predictive
   * search is simply disabled and only the standard search is used
   */
  supportsPredictiveApi() {
    return JSON.parse(document.getElementById("shopify-features").innerHTML)["predictiveSearch"];
  }
  /**
   * Check if the input is not empty, and if so, trigger the predictive search
   */
  _onInputChanged() {
    if (this._queryInput.value === "") {
      return this._onSearchCleared();
    }
    this._abortController?.abort();
    this._abortController = new AbortController();
    try {
      return this.supportsPredictiveApi() ? this._doPredictiveSearch() : this._doFallbackSearch();
    } catch (e) {
      if (e.name !== "AbortError") {
        throw e;
      }
    }
  }
  /**
   * Prevent the form submission if the query is empty
   */
  _onFormSubmitted(event) {
    if (this._queryInput.value === "") {
      return event.preventDefault();
    }
  }
  get ignoredNoResultSearchWords() {
    return ["ultra", "best", "top", "good", "great", "what", "which", "where", "when", "why", "how", "is", "are", "does", "for", "from", "with", "to", "and", "or", "on", "so", "this", "most"];
  }
  _buildNoResultFallbackQuery(query) {
    const ignoredWords = new Set(this.ignoredNoResultSearchWords);
    const trimmedQuery = query.trim();
    const fallbackQuery = trimmedQuery.split(/\s+/).filter((word) => {
      const normalizedWord = word.toLowerCase().replace(/^[^a-z0-9]+|[^a-z0-9]+$/gi, "");
      return normalizedWord !== "" && !ignoredWords.has(normalizedWord);
    }).join(" ");
    return fallbackQuery !== trimmedQuery ? fallbackQuery : "";
  }
  _predictiveSearchHasNoResults(nodeElement) {
    return Boolean(nodeElement.querySelector('[slot="results"] .empty-state'));
  }
  async _fetchPredictiveSearchResultNode(query) {
    const queryParams = `q=${encodeURIComponent(query)}&section_id=${this.getAttribute("section-id")}&resources[limit]=10&resources[limit_scope]=each&resources[options][fields]=title,product_type,variants.title,vendor,tag`;
    return new DOMParser().parseFromString(await (await cachedFetch(`${window.Shopify.routes.root}search/suggest?${queryParams}`, { signal: this._abortController.signal })).text(), "text/html");
  }
  /**
   * Do the actual predictive search
   */
  async _doPredictiveSearch() {
    await this._transitionToSlot("loading");
    let nodeElement = await this._fetchPredictiveSearchResultNode(this._queryInput.value);
    const fallbackQuery = this._buildNoResultFallbackQuery(this._queryInput.value);

    if (fallbackQuery !== "" && this._predictiveSearchHasNoResults(nodeElement)) {
      nodeElement = await this._fetchPredictiveSearchResultNode(fallbackQuery);
    }

    this.querySelector('[slot="results"]').replaceWith(document.importNode(nodeElement.querySelector('[slot="results"]'), true));
    return this._transitionToSlot("results");
  }
  /**
   * For merchants using store that do not support the predictive search, we have to fallback to standard search. Unfortunately,
   * the standard search is less convenient, and we have to simulate one request for each desired resource type
   */
  async _doFallbackSearch() {
    await this._transitionToSlot("loading");
    const queryParams = `q=${this._queryInput.value}&section_id=${this.getAttribute("section-id")}&resources[limit]=10&resources[limit_scope]=each`;
    const nodeElement = new DOMParser().parseFromString(await (await cachedFetch(`${window.Shopify.routes.root}search?${queryParams}`, { signal: this._abortController.signal })).text(), "text/html");
    this.querySelector('[slot="results"]').replaceWith(document.importNode(nodeElement.querySelector('[slot="results"]'), true));
    return this._transitionToSlot("results");
  }
  /**
   * If any search is pending, we abort them, and transition to the idle slot
   */
  _onSearchCleared() {
    this._abortController?.abort();
    this._queryInput.focus();
    return this._transitionToSlot("idle");
  }
  /**
   * Transition between different slot. To prevent useless animation, the slot does not transition if we go to the same slot
   */
  async _transitionToSlot(toSlotName) {
    if (this.shadowRoot.firstElementChild.name === toSlotName) {
      return;
    }
    await animate9(this.shadowRoot.firstElementChild.assignedNodes(), { opacity: [1, 0] }, { duration: 0.1 }).finished;
    this.shadowRoot.firstElementChild.setAttribute("name", toSlotName);
    return animate9(this.shadowRoot.firstElementChild.assignedNodes(), { opacity: [0, 1], transform: ["translateY(5px)", "translateY(0)"] }, { duration: 0.1 }).finished;
  }
};
if (!window.customElements.get("predictive-search")) {
  window.customElements.define("predictive-search", PredictiveSearch);
}

// js/common/search/search-drawer.js
var SearchDrawer = class extends Drawer {
  get shouldAppendToBody() {
    return false;
  }
  get openFrom() {
    return window.matchMedia(`${window.themeVariables.breakpoints["sm-max"]}`).matches ? "top" : this.getAttribute("open-from") || "right";
  }
};
if (!window.customElements.get("search-drawer")) {
  window.customElements.define("search-drawer", SearchDrawer);
}

// js/common/text/section-header.js
import { animate as animate10, inView as inView5 } from "vendor";
var _SectionHeader_instances, reveal_fn2;
var SectionHeader = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _SectionHeader_instances);
  }
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      inView5(this, __privateMethod(this, _SectionHeader_instances, reveal_fn2).bind(this), { margin: "0px 0px -100px 0px" });
    }
  }
};
_SectionHeader_instances = new WeakSet();
reveal_fn2 = function() {
  const heading = this.querySelector('[reveal-on-scroll="true"]'), headingKeyframe = getHeadingKeyframe(heading);
  animate10(...headingKeyframe);
};
if (!window.customElements.get("section-header")) {
  window.customElements.define("section-header", SectionHeader);
}

// js/common/ui/marquee-text.js
var MarqueeText = class extends HTMLElement {
  constructor() {
    super();
    if (window.ResizeObserver) {
      new ResizeObserver(this._calculateDuration.bind(this)).observe(this);
    }
  }
  _calculateDuration(entries) {
    const scrollingSpeed = parseInt(this.getAttribute("scrolling-speed") || 5), contentWidth = entries[0].contentRect.width, slowFactor = 1 + (Math.min(1600, contentWidth) - 375) / (1600 - 375);
    this.style.setProperty("--marquee-animation-duration", `${(scrollingSpeed * slowFactor * entries[0].target.querySelector("span").clientWidth / contentWidth).toFixed(3)}s`);
  }
};
if (!window.customElements.get("marquee-text")) {
  window.customElements.define("marquee-text", MarqueeText);
}

// js/sections/announcement-bar.js
import { timeline as timeline7 } from "vendor";
var AnnouncementBar = class extends EffectCarousel {
  connectedCallback() {
    super.connectedCallback();
    this._announcementRegion = this.closest("[data-codex-announcement-region]");
    this._announcementStatus = this.closest(".shopify-section")?.querySelector("[data-codex-announcement-status]") || this._announcementRegion?.querySelector("[data-codex-announcement-status]");
    this._announcementManualNavigation = false;
    this._announcementFocusWithin = false;
    this._announcementHovering = false;
    this._announcementStatusTimer = null;

    this.addEventListener("control:prev", this._markManualNavigation, { capture: true, signal: this._abortController.signal });
    this.addEventListener("control:next", this._markManualNavigation, { capture: true, signal: this._abortController.signal });
    this.addEventListener("carousel:change", this._handleAnnouncementChange, { signal: this._abortController.signal });

    if (this._announcementRegion) {
      this._announcementRegion.addEventListener("click", this._handleAnnouncementControlClick, { capture: true, signal: this._abortController.signal });
      this._announcementRegion.addEventListener("focusin", this._handleAnnouncementFocusIn, { signal: this._abortController.signal });
      this._announcementRegion.addEventListener("focusout", this._handleAnnouncementFocusOut, { signal: this._abortController.signal });
      this._announcementRegion.addEventListener("mouseenter", this._handleAnnouncementMouseEnter, { signal: this._abortController.signal });
      this._announcementRegion.addEventListener("mouseleave", this._handleAnnouncementMouseLeave, { signal: this._abortController.signal });
    }

    this._syncAnnouncementSlides();
  }

  disconnectedCallback() {
    clearTimeout(this._announcementStatusTimer);
    super.disconnectedCallback();
  }

  async select(index, options = {}) {
    const result = await super.select(index, options);
    this._syncAnnouncementPlayer();
    return result;
  }

  _markManualNavigation = () => {
    this._announcementManualNavigation = true;
    this._syncAnnouncementPlayer();
  };

  _handleAnnouncementControlClick = (event) => {
    const control = event.target instanceof Element
      ? event.target.closest('button[aria-controls="announcement-bar"]')
      : null;

    if (control) {
      this._markManualNavigation();
    }
  };

  _handleAnnouncementChange = (event) => {
    const isManualNavigation = this._announcementManualNavigation;
    this._syncAnnouncementSlides();
    this._announcementManualNavigation = false;
    const slide = this.items[event.detail.index];
    const message = slide?.querySelector("a")?.textContent?.trim()
      || Array.from(slide?.childNodes || [])
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => node.textContent)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
    if (isManualNavigation && message) {
      this._announceSelectedMessage(event.detail.index, message, isManualNavigation);
    }
  };

  _handleAnnouncementFocusIn = () => {
    this._announcementFocusWithin = true;
    this._syncAnnouncementPlayer();
  };

  _handleAnnouncementFocusOut = (event) => {
    if (!this._announcementRegion?.contains(event.relatedTarget)) {
      this._announcementFocusWithin = false;
      this._syncAnnouncementPlayer();
    }
  };

  _handleAnnouncementMouseEnter = () => {
    this._announcementHovering = true;
    this._syncAnnouncementPlayer();
  };

  _handleAnnouncementMouseLeave = () => {
    this._announcementHovering = false;
    this._syncAnnouncementPlayer();
  };

  _syncAnnouncementPlayer() {
    if (!this.player) {
      return;
    }

    if (this._announcementFocusWithin || this._announcementHovering) {
      this.player.pause();
    } else if (this.hasAttribute("autoplay")) {
      this.player.resume(true);
    }
  }

  _syncAnnouncementSlides() {
    this.items.forEach((slide, index) => {
      slide.removeAttribute("aria-roledescription");
      slide.removeAttribute("aria-hidden");
      slide.classList.toggle("reveal-invisible", index !== this.selectedIndex);
      this._syncAnnouncementSlideLinks(slide, index === this.selectedIndex);
    });
  }

  _syncAnnouncementSlideLinks(slide, isSelected) {
    slide.querySelectorAll("a, button, input, select, textarea, [tabindex]").forEach((element) => {
      if (isSelected) {
        if (element.hasAttribute("data-codex-announcement-original-tabindex")) {
          const originalTabIndex = element.getAttribute("data-codex-announcement-original-tabindex");
          if (originalTabIndex === "") {
            element.removeAttribute("tabindex");
          } else {
            element.setAttribute("tabindex", originalTabIndex);
          }
          element.removeAttribute("data-codex-announcement-original-tabindex");
        }
        return;
      }

      if (!element.hasAttribute("data-codex-announcement-original-tabindex")) {
        element.setAttribute("data-codex-announcement-original-tabindex", element.getAttribute("tabindex") || "");
      }
      element.setAttribute("tabindex", "-1");
    });
  }

  _announceSelectedMessage(index, message, assertive = false) {
    if (!(this._announcementStatus instanceof HTMLElement)) {
      return;
    }

    clearTimeout(this._announcementStatusTimer);
    this._announcementStatus.textContent = "";
    this._announcementStatus.setAttribute("aria-live", assertive ? "assertive" : "polite");

    window.setTimeout(() => {
      this._announcementStatus.textContent = `Item ${index + 1} of ${this.items.length}: ${message}`;
      this._announcementStatusTimer = window.setTimeout(() => {
        this._announcementStatus.textContent = "";
        this._announcementStatus.setAttribute("aria-live", "polite");
      }, 3000);
    }, 100);
  }

  _transitionTo(fromSlide, toSlide) {
    timeline7([
      // Keep announcement text at full contrast throughout the transition.
      // The slide classes still hide the inactive item after selection; fading
      // the text itself creates a transient low-contrast color that can fail
      // WCAG 1.4.3 and can be captured by automated scanners.
      [fromSlide, { transform: ["translateY(0)", "translateY(-5px)"] }, { duration: 0.2 }],
      [toSlide, { transform: ["translateY(5px)", "translateY(0)"] }, { duration: 0.2, at: "<" }]
    ]);
  }
};
if (!window.customElements.get("announcement-bar")) {
  window.customElements.define("announcement-bar", AnnouncementBar);
}

// js/sections/before-after-image.js
var SplitCursor = class extends HTMLElement {
  _abortController;
  connectedCallback() {
    this._parentSection = this.closest(".shopify-section");
    this._dragging = false;
    this._offsetX = this._currentX = 0;
    this._abortController = new AbortController();
    this._parentSection.addEventListener("pointerdown", this._onPointerDown.bind(this), { signal: this._abortController.signal });
    this._parentSection.addEventListener("pointermove", this._onPointerMove.bind(this), { signal: this._abortController.signal });
    this._parentSection.addEventListener("pointerup", this._onPointerUp.bind(this), { signal: this._abortController.signal });
    this.addEventListener("keydown", this._onKeyboardNavigation, { signal: this._abortController.signal });
    this._recalculateOffset();
    window.addEventListener("resize", this._recalculateOffset.bind(this), { signal: this._abortController.signal });
  }
  disconnectedCallback() {
    this._abortController?.abort();
  }
  get minOffset() {
    return -this.offsetLeft - (document.dir === "rtl" ? this.clientWidth : 0);
  }
  get maxOffset() {
    return this.offsetParent.clientWidth + this.minOffset;
  }
  _onPointerDown(event) {
    if (event.target === this || this.contains(event.target)) {
      this._initialX = event.clientX - this._offsetX;
      this._dragging = true;
    }
  }
  _onPointerMove(event) {
    if (!this._dragging) {
      return;
    }
    this._currentX = Math.min(Math.max(event.clientX - this._initialX, this.minOffset), this.maxOffset);
    this._offsetX = this._currentX;
    this._parentSection.style.setProperty("--clip-path-offset", `${this._currentX.toFixed(1)}px`);
  }
  _onPointerUp() {
    this._dragging = false;
  }
  _recalculateOffset() {
    this._parentSection.style.setProperty("--clip-path-offset", `${Math.min(Math.max(this.minOffset, this._currentX.toFixed(1)), this.maxOffset)}px`);
  }
  _onKeyboardNavigation(event) {
    if (!event.target.classList.contains("before-after__cursor") || !this.hasAttribute("vertical") && event.code !== "ArrowLeft" && event.code !== "ArrowRight" || this.hasAttribute("vertical") && event.code !== "ArrowUp" && event.code !== "ArrowDown") {
      return;
    }
    event.preventDefault();
    let currentPosition = parseInt(this._parentSection.style.getPropertyValue("--clip-path-offset"));
    let newPosition;
    if (this.hasAttribute("vertical")) {
      newPosition = event.code === "ArrowUp" ? currentPosition - 1 : currentPosition + 1;
    } else {
      newPosition = event.code === "ArrowLeft" ? currentPosition - 1 : currentPosition + 1;
    }
    this._currentX = Math.min(Math.max(newPosition, this.minOffset), this.maxOffset);
    this._offsetX = this._currentX;
    this._parentSection.style.setProperty("--clip-path-offset", `${this._currentX.toFixed(1)}px`);
  }
};
if (!window.customElements.get("split-cursor")) {
  window.customElements.define("split-cursor", SplitCursor);
}

// js/sections/collection-list.js
import { timeline as timeline8, inView as inView6 } from "vendor";
var CollectionList = class extends HTMLElement {
  constructor() {
    super();
    this._codexMotionStateListener = null;
  }
  connectedCallback() {
    this._codexMotionStateListener = this._handleCodexMotionStateChange.bind(this);
    document.addEventListener("codex:motion-state-change", this._codexMotionStateListener);
    if (this._motionPaused()) {
      this._renderStaticState();
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      inView6(this, this._reveal.bind(this), { margin: "-100px" });
    }
  }
  disconnectedCallback() {
    if (this._codexMotionStateListener) {
      document.removeEventListener("codex:motion-state-change", this._codexMotionStateListener);
      this._codexMotionStateListener = null;
    }
  }
  _motionPaused() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches || document.documentElement.getAttribute("data-codex-motion-paused") === "true" || window.__OOLMotionPaused === true;
  }
  _handleCodexMotionStateChange() {
    if (this._motionPaused()) {
      this._renderStaticState();
    }
  }
  _renderStaticState() {
    const toReveal = Array.from(this.querySelectorAll("[reveal-js]"));
    this.style.opacity = "1";
    this.style.visibility = "visible";
    this.style.transform = "none";
    toReveal.forEach((item) => {
      item.style.opacity = "1";
      item.style.visibility = "visible";
      item.style.transform = "none";
      Array.from(item.querySelectorAll("img, svg, .collection-card__content-wrapper")).forEach((element) => {
        element.style.opacity = "1";
        element.style.visibility = "visible";
        element.style.transform = "none";
      });
    });
  }
  _reveal() {
    const toReveal = Array.from(this.querySelectorAll("[reveal-js]"));
    timeline8([
      [toReveal, { opacity: 1 }, { duration: 0 }],
      [toReveal.map((item) => item.querySelector("img, svg")), { opacity: [0, 1], transform: ["scale(1.03)", "auto"] }, { duration: 0.2 }],
      [toReveal.map((item) => item.querySelector(".collection-card__content-wrapper")), { opacity: [0, 1] }, { duration: 0.2 }]
    ]);
  }
};
if (!window.customElements.get("collection-list")) {
  window.customElements.define("collection-list", CollectionList);
}

// js/sections/customer-login.js
import { Delegate as Delegate5 } from "vendor";
var AccountLogin = class extends HTMLElement {
  connectedCallback() {
    this.recoverForm = this.querySelector("#recover");
    this.loginForm = this.querySelector("#login");
    this.recoverTrigger = null;
    if (window.location.hash === "#recover") {
      this._switchForms();
      this._focusPanel(this.recoverForm);
    }
    new Delegate5(this).on("click", '[href="#recover"], [href="#login"]', this._switchForms.bind(this));
  }
  _switchForms(event) {
    if (event) {
      event.preventDefault();
    }
    const targetId = event?.currentTarget?.getAttribute("href") || (this.recoverForm.hidden ? "#recover" : "#login");
    if (targetId === "#recover") {
      this.recoverTrigger = event?.currentTarget || this.recoverTrigger;
      this.recoverForm.hidden = false;
      this.loginForm.hidden = true;
      this._focusPanel(this.recoverForm);
    } else {
      this.recoverForm.hidden = true;
      this.loginForm.hidden = false;
      this._focusPanel(this.loginForm, this.recoverTrigger);
      this.recoverTrigger = null;
    }
  }
  _focusPanel(panel, preferredTarget = null) {
    if (!(panel instanceof HTMLElement)) {
      return;
    }
    requestAnimationFrame(() => {
      const target = preferredTarget?.isConnected ? preferredTarget : panel.querySelector("h1, h2, [role=heading]");
      if (!(target instanceof HTMLElement)) {
        return;
      }
      if (!target.hasAttribute("tabindex")) {
        target.setAttribute("tabindex", "-1");
      }
      target.focus({ preventScroll: true });
    });
  }
};
if (!window.customElements.get("account-login")) {
  window.customElements.define("account-login", AccountLogin);
}

// js/sections/header.js
import { animate as animate11, timeline as timeline9, stagger as stagger5, Delegate as Delegate6 } from "vendor";
var reduceMenuAnimation = window.themeVariables.settings.reduceMenuAnimation;
var StoreHeader = class extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute("hide-on-scroll") && window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      this._lastScrollTop = 0;
      this._accumulatedScroll = 0;
      this._isVisible = true;
      this._hasSwitchedToSticky = false;
      window.addEventListener("scroll", throttle(this._onScroll.bind(this)));
    }
    this.addEventListener("toggle", this._checkTransparency.bind(this), { capture: true });
    this._setupTransparentHeader();
    if (this.hasAttribute("sticky")) {
      window.addEventListener("pageshow", (event) => {
        if (event.persisted) {
          this.classList.remove("is-filled");
          this._setupTransparentHeader();
        }
      });
      if (Shopify.designMode) {
        document.addEventListener("shopify:section:load", this._setupTransparentHeader.bind(this));
        document.addEventListener("shopify:section:unload", this._setupTransparentHeader.bind(this));
        document.addEventListener("shopify:section:reorder", this._setupTransparentHeader.bind(this));
      }
    }
  }
  async hide() {
    if (this._isVisible) {
      this._isVisible = false;
      document.documentElement.style.setProperty("--header-is-visible", "0");
      await animate11(this, { transform: ["translateY(0)", "translateY(-100%)"] }, { duration: 0.2, easing: "ease" }).finished;
      if (this._isVisible === false) {
        this.closest(".shopify-section").style.visibility = "hidden";
      }
    }
  }
  show() {
    if (!this._isVisible) {
      this.closest(".shopify-section").style.visibility = "visible";
      document.documentElement.style.setProperty("--header-is-visible", "1");
      animate11(this, { transform: ["translateY(-100%)", "translateY(0)"] }, { duration: 0.2, easing: "ease" });
      this._accumulatedScroll = 0;
      this._isVisible = true;
    }
  }
  _onScroll() {
    if (window.scrollY < 0) {
      return;
    }
    this._accumulatedScroll = Math.max(0, this._accumulatedScroll + (window.scrollY - this._lastScrollTop));
    if (window.scrollY < this._lastScrollTop) {
      this.show();
    } else if (this._accumulatedScroll > parseInt(this.getAttribute("hide-on-scroll")) && this.querySelectorAll("[open]").length === 0) {
      this.hide();
    }
    this._lastScrollTop = window.scrollY;
  }
  _checkTransparency() {
    let hasFallbackScrollDetection = false;
    if (CSS.supports("selector(:has(> *))") && this.hasAttribute("sticky") && !this.hasAttribute("hide-on-scroll") && this.hasAttribute("allow-transparency")) {
      const offsetBeforeChanging = 500;
      if (this.querySelectorAll("[open]").length > 0) {
        this.classList.add("is-filled");
      } else if (window.scrollY >= offsetBeforeChanging && !this._hasSwitchedToSticky) {
        this._hasSwitchedToSticky = true;
        this.classList.add("is-filled");
        animate11(this, { transform: ["translateY(-100%)", "translateY(0)"] }, { duration: 0.15, easing: "ease" });
      } else if (window.scrollY < offsetBeforeChanging) {
        if (this._hasSwitchedToSticky) {
          this._hasSwitchedToSticky = false;
          animate11(this, { transform: ["translateY(0)", "translateY(-100%)"] }, { duration: 0.15, easing: "ease" }).finished.then(() => {
            this.style.transform = null;
            this.classList.remove("is-filled");
          });
        } else if (this.getAnimations().length === 0) {
          this.classList.remove("is-filled");
        }
      }
    } else {
      hasFallbackScrollDetection = this.hasAttribute("sticky") && window.scrollY > 20;
      this.classList.toggle("is-filled", !this.hasAttribute("allow-transparency") || this.querySelectorAll("[open]").length > 0 || hasFallbackScrollDetection);
    }
  }
  _setupTransparentHeader() {
    if (document.querySelector(".shopify-section:first-child [allow-transparent-header]")) {
      this.setAttribute("allow-transparency", "");
      this.addEventListener("mouseenter", this._checkTransparency.bind(this));
      this.addEventListener("mouseleave", this._checkTransparency.bind(this));
      if (this.hasAttribute("sticky")) {
        window.addEventListener("scroll", throttle(this._checkTransparency.bind(this)));
      }
      this._checkTransparency();
    } else {
      this.removeAttribute("allow-transparency");
    }
  }
};
var DropdownDisclosure = class _DropdownDisclosure extends AnimatedDetails {
  constructor() {
    super();
    this._detectClickOutsideListener = this._detectClickOutside.bind(this);
    this._detectEscKeyboardListener = this._detectEscKeyboard.bind(this);
    this._detectFocusOutListener = this._detectFocusOut.bind(this);
    this._detectHoverListener = this._detectHover.bind(this);
    this._detectSummaryKeyboardListener = this._detectSummaryKeyboard.bind(this);
    this._detectMenuKeyboardListener = this._detectMenuKeyboard.bind(this);
    this._hoverTimer = null;
    this._transitionToken = 0;
    const externalButton = this.hasAttribute("data-header-split")
      ? this.parentElement?.querySelector(":scope > button[data-header-menu-toggle]")
      : null;
    this._splitHeaderButton = externalButton instanceof HTMLButtonElement ? externalButton : null;
    if (this._splitHeaderButton) {
      this.summaryElement = this._splitHeaderButton;
      this.summaryElement.addEventListener("click", this._onSummaryClicked.bind(this));
    }
    this._hoverArea = this._splitHeaderButton ? this.parentElement : this;
    this._hoverArea.addEventListener("mouseover", this._detectHoverListener);
    this._hoverArea.addEventListener("mouseout", this._detectHoverListener);
    this.summaryElement.addEventListener("keydown", this._detectSummaryKeyboardListener);
    this.addEventListener("keydown", this._detectMenuKeyboardListener);
    this._syncSummaryAccessibilityState();
  }
  get trigger() {
    return !window.matchMedia("screen and (pointer: fine)").matches ? "click" : this.getAttribute("trigger");
  }
  get mouseOverDelayTolerance() {
    return 250;
  }
  /**
   * Split top-level header buttons always toggle the dropdown. Other summaries
   * retain the theme's hover-mode destination shortcut and click-mode toggle.
   */
  _onSummaryClicked(event) {
    if (this._splitHeaderButton) {
      event.preventDefault();
      this.open = !this.open;
      return;
    }
    if (this.trigger === "hover") {
      event.preventDefault();
      if (event.detail === 0) {
        const shouldOpen = !this.open;
        this.open = shouldOpen;
        if (shouldOpen) {
          // Give keyboard and screen-reader users immediate feedback from the
          // first submenu link; pointer/hover activation keeps its usual focus.
          this._focusFirstKeyboardItem();
        }
      } else if (event.currentTarget.hasAttribute("data-url")) {
        window.location.href = event.currentTarget.getAttribute("data-url");
      }
    } else {
      super._onSummaryClicked(event);
      if (event.detail === 0 && this.open) {
        // Keep keyboard opening consistent for click-triggered disclosures
        // (including touch/mobile layouts) without changing pointer focus.
        this._focusFirstKeyboardItem();
      }
    }
  }
  _focusFirstKeyboardItem() {
    requestAnimationFrame(() => {
      this._getKeyboardFocusableElements()[0]?.focus();
    });
  }
  _isTopLevelHeaderDisclosure() {
    const list = this.parentElement?.parentElement;
    return list instanceof HTMLElement
      && list.matches("ul.contents")
      && list.parentElement?.matches("nav.header__link-list")
      && this.closest("nav.header__link-list") === list.parentElement;
  }
  _getTopLevelHeaderControls() {
    if (!this._isTopLevelHeaderDisclosure()) {
      return [];
    }
    return getAllTopLevelHeaderNavigationControls();
  }
  _syncSummaryAccessibilityState() {
    if (!(this.summaryElement instanceof HTMLElement)) {
      return;
    }
    if (!(this.summaryElement instanceof HTMLButtonElement)) {
      this.summaryElement.setAttribute("role", "button");
    }
    this.summaryElement.setAttribute("aria-expanded", this.open ? "true" : "false");
    if (this.contentElement instanceof HTMLElement && this.contentElement.id) {
      this.summaryElement.setAttribute("aria-controls", this.contentElement.id);
    }
  }
  _getKeyboardFocusableElements() {
    if (!(this.contentElement instanceof HTMLElement)) {
      return [];
    }
    return Array.from(this.contentElement.querySelectorAll('a[href]:not([tabindex="-1"]), summary, button:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((element) => {
      return element.closest("details") === this
        && !element.hasAttribute("hidden")
        && element.getAttribute("aria-hidden") !== "true"
        && element.getClientRects().length > 0;
    });
  }
  _closeAndFocusSummary() {
    this.open = false;
    const focusWhenClosed = () => {
      if (this.open || !this.summaryElement?.isConnected) {
        return;
      }
      if (this.hasAttribute("open")) {
        requestAnimationFrame(focusWhenClosed);
        return;
      }
      if (this.summaryElement instanceof HTMLElement) {
        this.summaryElement.focus({ preventScroll: true });
      }
    };
    requestAnimationFrame(focusWhenClosed);
  }
  /**
   * Hover-driven menus must still work as disclosures from the keyboard. The split
   * top-level header uses a native button, whose click always toggles regardless of
   * assistive-technology click detail. Other summaries retain their existing path.
   * The top-level header
   * uses directional keys between sibling controls while closed; once open, vertical
   * arrows enter the submenu and horizontal arrows move to a neighboring top-level
   * control. Nested disclosures retain their existing ArrowUp/ArrowDown entry path.
   */
  _detectSummaryKeyboard(event) {
    if (!["ArrowDown", "ArrowUp", "ArrowRight", "ArrowLeft"].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    if (this._isTopLevelHeaderDisclosure()) {
      const controls = this._getTopLevelHeaderControls(), currentIndex = controls.indexOf(this.summaryElement);
      if (currentIndex === -1) {
        return;
      }
      event.preventDefault();
      if (this.open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
        const focusableElements = this._getKeyboardFocusableElements();
        const elementToFocus = event.key === "ArrowUp" ? focusableElements[focusableElements.length - 1] : focusableElements[0];
        elementToFocus?.focus({ preventScroll: true });
        return;
      }
      const nextIndex = ["ArrowRight", "ArrowDown"].includes(event.key) ? currentIndex + 1 : currentIndex - 1;
      const nextControl = controls[nextIndex];
      const iconControls = getHeaderIconNavigationControls();
      const isForward = ["ArrowRight", "ArrowDown"].includes(event.key);
      const boundaryControl = isForward && currentIndex === controls.length - 1
        ? iconControls[0]
        : !isForward && currentIndex === 0
          ? iconControls[iconControls.length - 1]
          : null;
      if (this.open && (nextControl || boundaryControl)) {
        this.open = false;
      }
      if (nextControl) {
        nextControl.focus({ preventScroll: true });
      } else if (boundaryControl) {
        focusHeaderNavigationControl(boundaryControl);
      }
      return;
    }
    if (this.trigger !== "hover" || !["ArrowDown", "ArrowUp"].includes(event.key)) {
      return;
    }
    event.preventDefault();
    this.open = true;
    requestAnimationFrame(() => {
      const focusableElements = this._getKeyboardFocusableElements();
      const elementToFocus = event.key === "ArrowUp" ? focusableElements[focusableElements.length - 1] : focusableElements[0];
      elementToFocus?.focus();
    });
  }
  _detectMenuKeyboard(event) {
    if (!this.open || !["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    const focusedElement = event.target instanceof Element
      ? event.target.closest('a[href], summary, button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      : null;
    if (!(focusedElement instanceof HTMLElement) || focusedElement === this.summaryElement || focusedElement.closest("details") !== this) {
      return;
    }
    const focusableElements = this._getKeyboardFocusableElements();
    const currentIndex = focusableElements.indexOf(focusedElement);
    if (currentIndex === -1) {
      return;
    }
    event.preventDefault();
    if (event.key === "Home" || event.key === "End") {
      focusableElements[event.key === "Home" ? 0 : focusableElements.length - 1]?.focus({ preventScroll: true });
      return;
    }
    if (event.key === "ArrowUp" && currentIndex === 0 && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
      this._closeAndFocusSummary();
      return;
    }
    const nextIndex = event.key === "ArrowDown" ? currentIndex + 1 : currentIndex - 1;
    focusableElements[nextIndex]?.focus();
  }
  async _transition(value) {
    const transitionToken = ++this._transitionToken;
    this._syncSummaryAccessibilityState();
    if (value) {
      this.setAttribute("open", "");
      document.addEventListener("click", this._detectClickOutsideListener);
      document.addEventListener("keydown", this._detectEscKeyboardListener);
      document.addEventListener("focusout", this._detectFocusOutListener);
      const siblingContainer = this.closest("ul");
      const openSiblings = siblingContainer ? Array.from(siblingContainer.querySelectorAll("[open]")).filter((item) => item !== this) : [];
      openSiblings.forEach((sibling) => sibling.open = false);
      await this._transitionIn(openSiblings.length > 0);
      if (transitionToken !== this._transitionToken || !this._open) {
        this.contentElement.style.opacity = this._open ? 1 : 0;
        if (this._open) {
          this.setAttribute("open", "");
        }
        return;
      }
      this.contentElement.style.opacity = 1;
    } else {
      document.removeEventListener("click", this._detectClickOutsideListener);
      document.removeEventListener("keydown", this._detectEscKeyboardListener);
      document.removeEventListener("focusout", this._detectFocusOutListener);
      await this._transitionOut();
      if (transitionToken !== this._transitionToken || this._open) {
        this.contentElement.style.opacity = this._open ? 1 : 0;
        if (this._open) {
          this.setAttribute("open", "");
        }
        return;
      }
      this.contentElement.style.opacity = 0;
      this.removeAttribute("open");
    }
  }
  _transitionIn(hasOpenSiblings = false) {
    const timelineSequence = [[this.contentElement, { opacity: 1 }, { duration: 0.2 }]];
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches && !reduceMenuAnimation) {
      timelineSequence.push([this.contentElement.querySelectorAll(":scope > ul > li"), { opacity: [0, 1], transform: ["translateY(-10px)", "translateY(0)"] }, { delay: stagger5(0.025), at: "-0.1", duration: 0.2 }]);
    }
    return timeline9(timelineSequence, { delay: hasOpenSiblings > 0 ? 0.1 : 0 }).finished;
  }
  _transitionOut() {
    return timeline9([[this.contentElement, { opacity: 0 }, { duration: 0.2 }]]).finished;
  }
  /**
   * When dropdown menu is configured to open on click, we add a listener to detect click outside and automatically
   * close the navigation.
   */
  _detectClickOutside(event) {
    if (this.trigger !== "click") {
      return;
    }
    if (!this._hoverArea.contains(event.target) && !(event.target.closest("details") instanceof _DropdownDisclosure)) {
      this.open = false;
    }
  }
  /**
   * On desktop device, if the mode is set to hover, we open/close the dropdown on hover
   */
  _detectHover(event) {
    if (this.trigger !== "hover") {
      return;
    }
    if (event.relatedTarget instanceof Node && this._hoverArea.contains(event.relatedTarget)) {
      return;
    }
    if (event.type === "mouseover") {
      if (this._hoverTimer) {
        clearTimeout(this._hoverTimer);
        this._hoverTimer = null;
      }
      this.open = true;
    } else {
      if (this._hoverTimer) {
        clearTimeout(this._hoverTimer);
      }
      this._hoverTimer = setTimeout(() => {
        this._hoverTimer = null;
        this.open = false;
      }, this.mouseOverDelayTolerance);
    }
  }
  /**
   * Detect if we hit the "Escape" key to automatically close the dropdown
   */
  _detectEscKeyboard(event) {
    if (event.code === "Escape") {
      if (this._splitHeaderButton && event.target === this._splitHeaderButton && this.open) {
        event.preventDefault();
        this._closeAndFocusSummary();
        return;
      }
      const targetMenu = event.target.closest("details[open]");
      if (targetMenu) {
        event.preventDefault();
        if (targetMenu instanceof _DropdownDisclosure) {
          targetMenu._closeAndFocusSummary();
        } else {
          const targetSummary = targetMenu.firstElementChild;
          targetMenu.open = false;
          const focusWhenClosed = () => {
            if (targetMenu.open || !targetSummary?.isConnected) {
              return;
            }
            if (targetMenu.hasAttribute("open")) {
              requestAnimationFrame(focusWhenClosed);
              return;
            }
            if (targetSummary instanceof HTMLElement) {
              targetSummary.focus({ preventScroll: true });
            }
          };
          requestAnimationFrame(focusWhenClosed);
        }
      }
    }
  }
  /**
   * Close the dropdown automatically when the dropdown is focused out
   */
  _detectFocusOut(event) {
    if (event.relatedTarget && !this._hoverArea.contains(event.relatedTarget)) {
      this.open = false;
    }
  }
};
function getTopLevelHeaderNavigationControls(control) {
  const list = control.parentElement?.parentElement;
  if (!(list instanceof HTMLElement) || !list.matches("ul.contents") || !list.parentElement?.matches("nav.header__link-list")) {
    return [];
  }
  return Array.from(list.querySelectorAll(":scope > li > a[href], :scope > li > details > summary, :scope > li > button:not([disabled])")).filter((element) => {
    return !element.hasAttribute("hidden")
      && element.getAttribute("aria-hidden") !== "true"
      && element.getClientRects().length > 0;
  });
}
function getAllTopLevelHeaderNavigationControls() {
  return Array.from(document.querySelectorAll("nav.header__link-list > ul.contents")).flatMap((list) => {
    return Array.from(list.querySelectorAll(":scope > li > a[href], :scope > li > details > summary, :scope > li > button:not([disabled])")).filter(isVisibleHeaderNavigationControl);
  });
}
function isVisibleHeaderNavigationControl(element) {
  return element instanceof HTMLElement
    && !element.hasAttribute("hidden")
    && element.getAttribute("aria-hidden") !== "true"
    && element.getClientRects().length > 0;
}
function getHeaderIconNavigationControl(listItem) {
  if (!(listItem instanceof HTMLElement)) {
    return null;
  }
  const accountHost = listItem.querySelector(":scope > shopify-account");
  if (isVisibleHeaderNavigationControl(accountHost)) {
    return accountHost;
  }
  return Array.from(listItem.querySelectorAll("a[href], button:not([disabled]), summary, [tabindex]:not([tabindex=\"-1\"])")).find((element) => {
    return element.closest("li") === listItem && isVisibleHeaderNavigationControl(element);
  }) || null;
}
function getHeaderIconNavigationControls() {
  return Array.from(document.querySelectorAll("ul.header__icon-list")).flatMap((list) => {
    if (!isVisibleHeaderNavigationControl(list)) {
      return [];
    }
    return Array.from(list.querySelectorAll(":scope > li")).map(getHeaderIconNavigationControl).filter(Boolean);
  });
}
function focusHeaderNavigationControl(control) {
  if (!(control instanceof HTMLElement)) {
    return;
  }
  if (control.matches("shopify-account")) {
    control.shadowRoot?.querySelector("a[href], button:not([disabled]), [tabindex]:not([tabindex=\"-1\"])")?.focus({ preventScroll: true });
    return;
  }
  control.focus({ preventScroll: true });
}
function getHeaderNavigationControlFromEvent(event) {
  const path = typeof event.composedPath === "function" ? event.composedPath() : [];
  const listItem = path.find((element) => element instanceof HTMLElement && element.matches("ul.header__icon-list > li"));
  return listItem ? getHeaderIconNavigationControl(listItem) : null;
}
function onTopLevelHeaderNavigationKeydown(event) {
  if (!["ArrowDown", "ArrowUp", "ArrowRight", "ArrowLeft"].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
    return;
  }
  if (event.defaultPrevented) {
    return;
  }
  const control = event.target instanceof Element ? event.target.closest("nav.header__link-list > ul.contents > li > a[href], nav.header__link-list > ul.contents > li > button:not([disabled])") : null;
  const iconControl = getHeaderNavigationControlFromEvent(event);
  const controls = getAllTopLevelHeaderNavigationControls();
  const iconControls = getHeaderIconNavigationControls();
  const currentIndex = controls.indexOf(control), iconIndex = iconControls.indexOf(iconControl);
  if (currentIndex === -1 && iconIndex === -1) {
    return;
  }
  event.preventDefault();
  const isForward = ["ArrowRight", "ArrowDown"].includes(event.key);
  if (currentIndex !== -1) {
    const nextIndex = isForward ? currentIndex + 1 : currentIndex - 1;
    if (controls[nextIndex]) {
      controls[nextIndex].focus({ preventScroll: true });
    } else if (isForward && currentIndex === controls.length - 1 && iconControls[0]) {
      focusHeaderNavigationControl(iconControls[0]);
    } else if (!isForward && currentIndex === 0 && iconControls[iconControls.length - 1]) {
      focusHeaderNavigationControl(iconControls[iconControls.length - 1]);
    }
    return;
  }
  const nextIndex = isForward ? iconIndex + 1 : iconIndex - 1;
  if (iconControls[nextIndex]) {
    focusHeaderNavigationControl(iconControls[nextIndex]);
  } else if (!isForward && iconIndex === 0 && controls[controls.length - 1]) {
    controls[controls.length - 1].focus({ preventScroll: true });
  }
}
if (!window.__oolTopLevelHeaderNavigationKeyboardHandlerInstalled) {
  document.addEventListener("keydown", onTopLevelHeaderNavigationKeydown);
  window.__oolTopLevelHeaderNavigationKeyboardHandlerInstalled = true;
}
var MegaMenuDisclosure = class extends DropdownDisclosure {
  constructor() {
    super();
    this.addEventListener("pointerover", this._preloadImages.bind(this));
  }
  get mouseOverDelayTolerance() {
    return 500;
  }
  _transitionIn(hasOpenSiblings) {
    const timelineSequence = [[this.contentElement, { opacity: 1 }, { duration: 0.2 }], "mega-menu"], contentDelay = hasOpenSiblings ? 0.1 : 0;
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches && !reduceMenuAnimation) {
      this.contentElement.querySelectorAll(".mega-menu__promo").forEach((promo) => {
        timelineSequence.push([promo, { opacity: [0, 1] }, { duration: 0.25, delay: contentDelay, at: "mega-menu" }], "mega-menu-promo");
      });
      this.contentElement.querySelectorAll(".mega-menu__nav > li").forEach((column) => {
        timelineSequence.push([column.querySelectorAll(":scope > :first-child, :scope li"), { opacity: [0, 1], transform: ["translateY(-10px)", "translateY(0)"] }, { easing: "ease", delay: stagger5(0.025, { start: contentDelay }), at: "mega-menu", duration: 0.2 }]);
      });
    }
    return timeline9(timelineSequence).finished;
  }
  /**
   * When the toggle is hovered we preload the mega-menu images to improve perceived performance
   */
  _preloadImages() {
    Array.from(this.querySelectorAll('img[loading="lazy"]')).forEach((image) => image.setAttribute("loading", "eager"));
  }
};
var MegaMenuPromoCarousel = class extends EffectCarousel {
  connectedCallback() {
    super.connectedCallback();
    if (this.nextElementSibling) {
      this.addEventListener("carousel:select", (event) => this._updateControlsColor(event.detail.slide));
    }
    this._updateControlsColor(this.items[this.selectedIndex]);
  }
  _updateControlsColor(slide) {
    const extractFrom = slide.classList.contains("content-over-media") ? slide : slide.firstElementChild;
    this.nextElementSibling.style.setProperty("--text-color", extractFrom.style.getPropertyValue("--text-color"));
  }
};
var NavigationDrawer = class extends Drawer {
  constructor() {
    super();
    const delegate = new Delegate6(this);
    delegate.on("click", "button[data-panel]", this._onPanelButtonClick.bind(this));
    this._isTransitioning = false;
    this._panelTriggerStack = [];
    this.addEventListener("dialog:after-show", () => {
      if (this.id === "header-sidebar-menu" && this._openedFromKeyboard) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._focusHeaderDrawerTarget();
          });
        });
      }
    });
    this.addEventListener("dialog:after-hide", () => {
      this.reinitializeDrawer();
    });
  }
  get focusTrap() {
    return this._focusTrap = this._focusTrap || new FocusTrap.createFocusTrap(this, {
      onDeactivate: () => {
        if (!this._ignoreFocusTrapDeactivate && this.shouldHideOnFocusTrapDeactivate) {
          this.hide();
        }
      },
      allowOutsideClick: this._allowOutsideClick.bind(this),
      initialFocus: window.matchMedia("screen and (pointer: fine)").matches || this._openedFromKeyboard ? this._getInitialFocusTarget() : void 0,
      fallbackFocus: this,
      tabbableOptions: {
        getShadowRoot: true
      },
      preventScroll: this.preventScrollWhenTrapped
    });
  }
  _onToggleClicked(event) {
    this._openedFromKeyboard = event?.detail === 0;
    if (!this.open) {
      this._focusTrap = null;
    }
    return super._onToggleClicked(event);
  }
  get openFrom() {
    return window.matchMedia("(max-width: 699px)").matches ? this.getAttribute("mobile-opening") : super.openFrom;
  }
  _isVisibleElement(element) {
    return !!element && element.getClientRects().length > 0 && window.getComputedStyle(element).visibility !== "hidden";
  }
  _getInitialFocusTarget() {
    const closeButton = this.querySelector("[data-navigation-drawer-close]");
    if (this._isVisibleElement(closeButton)) {
      return closeButton;
    }
    const focusableSelector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([type='hidden']):not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])"
    ].join(",");
    return Array.from(this.querySelectorAll(focusableSelector)).find((element) => this._isVisibleElement(element)) || this;
  }
  _focusHeaderDrawerTarget() {
    const target = this._getInitialFocusTarget();
    if (target && target !== this && this._isVisibleElement(target)) {
      target.focus({ preventScroll: true });
    }
  }
  // Used for navigation mobile and navigation desktop set to drawer
  switchToPanel(panelIndex, linkListIndex = null) {
    const panels = this.querySelectorAll(".panel");
    let panelToHideTransform, panelToShowTransform, panelToHide = linkListIndex !== null ? panels[parseInt(panelIndex) - 1] : panels[parseInt(panelIndex) + 1], panelToShow = panels[panelIndex], linkLists = panelToShow.querySelectorAll(".panel__wrapper"), timelineSequence = [];
    if (document.dir === "rtl") {
      panelToHideTransform = linkListIndex !== null ? ["translateX(0%)", "translateX(100%)"] : ["translateX(0%)", "translateX(-100%)"];
      panelToShowTransform = linkListIndex !== null ? ["translateX(-100%)", "translateX(0%)"] : ["translateX(100%)", "translateX(0%)"];
    } else {
      panelToHideTransform = linkListIndex !== null ? ["translateX(0%)", "translateX(-100%)"] : ["translateX(0%)", "translateX(100%)"];
      panelToShowTransform = linkListIndex !== null ? ["translateX(100%)", "translateX(0%)"] : ["translateX(-100%)", "translateX(0%)"];
    }
    timelineSequence.push(
      [panelToHide, { transform: panelToHideTransform, opacity: [1, 0], visibility: ["visible", "hidden"] }, { duration: 0.3, opacity: { at: "+0.3" }, transform: { at: "+0.3" } }],
      "panelHidden",
      [panelToShow, { opacity: [0, 1], visibility: ["hidden", "visible"], transform: panelToShowTransform }, { at: "<", transform: { duration: 0.3 } }]
    );
    if (linkListIndex !== null) {
      timelineSequence.push(this.switchLinkList(linkLists, linkListIndex));
    }
    return timeline9(timelineSequence).finished;
  }
  // Used when mega menu is set to drawer
  showPanel(panelIndex, linkListIndex = null) {
    const panels = this.querySelectorAll(".panel");
    let timelineSequence = [], panelToShow = panels[panelIndex], linkLists = panelToShow.querySelectorAll(".panel__wrapper");
    if (!panelToShow.hasAttribute("open") && !this._isTransitioning) {
      this._isTransitioning = true;
      timelineSequence.push(
        [this, { width: [this.offsetWidth + "px", (this.offsetWidth - parseInt(window.getComputedStyle(this).getPropertyValue("padding"))) * 2 + "px"] }, { duration: 0.2 }],
        [panelToShow, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { at: "<" }]
      );
      timelineSequence.push(this.switchLinkList(linkLists, linkListIndex));
    }
    if (this.previousLinkIndex && this.previousLinkIndex !== linkListIndex) {
      timelineSequence.push([linkLists[this.previousLinkIndex], { opacity: [1, 0], visibility: ["visible", "hidden"] }, { duration: 0.2 }]);
    }
    timeline9(timelineSequence).finished.then(() => {
      if (this.previousLinkIndex && this.previousLinkIndex !== linkListIndex) {
        linkLists[this.previousLinkIndex].removeAttribute("style");
      }
      if (panelToShow.hasAttribute("open") && this.previousLinkIndex !== linkListIndex) {
        timeline9([this.switchLinkList(linkLists, linkListIndex)]);
      }
      this.previousLinkIndex = linkListIndex;
      panelToShow.setAttribute("open", "");
      this._isTransitioning = false;
    });
  }
  switchLinkList(linkLists, linkListIndex) {
    Array.from(linkLists).forEach((item) => {
      item.setAttribute("hidden", "");
    });
    linkLists[linkListIndex].removeAttribute("hidden");
    return [linkLists[linkListIndex].querySelectorAll("li"), { opacity: [0, 1], visibility: ["hidden", "visible"], transform: ["translateY(-10px)", "translateY(0)"] }, { easing: "ease", duration: 0.2, at: "-0.15", delay: stagger5(0.025, { start: 0.1 }) }];
  }
  // Set navigation drawer to initial state when drawer closed
  reinitializeDrawer() {
    this._panelTriggerStack = [];
    this.setExpanded();
    if (this.hasAttribute("mega-menu") && window.matchMedia("(min-width:1150px)").matches) {
      this.style.removeProperty("width");
    } else {
      const firstPanel = this.querySelector(".panel:first-child");
      firstPanel.style.opacity = "1";
      firstPanel.style.visibility = "visible";
      firstPanel.style.transform = "translateX(0%)";
    }
    Array.from(this.querySelectorAll(".panel:not(:first-child)")).forEach((item) => {
      if (this.hasAttribute("mega-menu")) {
        item.removeAttribute("open");
      }
      item.style.opacity = "0";
      item.style.visibility = "hidden";
      Array.from(item.querySelectorAll(".panel__wrapper")).forEach((list) => list.setAttribute("hidden", ""));
    });
  }
  setExpanded(target) {
    Array.from(this.querySelectorAll("button[data-panel]")).forEach((item) => {
      item.setAttribute("aria-expanded", "false");
    });
    if (target) {
      target.setAttribute("aria-expanded", "true");
    }
  }
  _focusPanel(panelIndex) {
    const panel = this.querySelectorAll(".panel")[parseInt(panelIndex)];
    if (!(panel instanceof HTMLElement)) {
      return;
    }
    const visibleWrapper = Array.from(panel.querySelectorAll(".panel__wrapper")).find((wrapper) => !wrapper.hasAttribute("hidden"));
    const focusContainer = visibleWrapper || panel;
    focusContainer.querySelector('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')?.focus();
  }
  _onPanelButtonClick(event, target) {
    if (this.hasAttribute("mega-menu") && window.matchMedia("(min-width:1150px)").matches) {
      this.setExpanded(target);
      this.showPanel(...target.getAttribute("data-panel").split("-"));
    } else {
      const panelCoordinates = target.getAttribute("data-panel").split("-");
      const isForwardNavigation = panelCoordinates.length > 1;
      const shouldMoveFocus = event.detail === 0;
      let triggerToRestore = null;
      if (isForwardNavigation) {
        target.setAttribute("aria-expanded", "true");
        this._panelTriggerStack.push(target);
      } else {
        triggerToRestore = this._panelTriggerStack.pop() || null;
        triggerToRestore?.setAttribute("aria-expanded", "false");
      }
      this.switchToPanel(...panelCoordinates).then(() => {
        if (!shouldMoveFocus) {
          return;
        }
        if (isForwardNavigation) {
          this._focusPanel(panelCoordinates[0]);
        } else if (triggerToRestore?.isConnected) {
          triggerToRestore.focus();
        }
      });
    }
  }
};
if (!window.customElements.get("store-header")) {
  window.customElements.define("store-header", StoreHeader);
}
if (!window.customElements.get("dropdown-disclosure")) {
  window.customElements.define("dropdown-disclosure", DropdownDisclosure, { extends: "details" });
}
if (!window.customElements.get("mega-menu-disclosure")) {
  window.customElements.define("mega-menu-disclosure", MegaMenuDisclosure, { extends: "details" });
}
if (!window.customElements.get("mega-menu-promo-carousel")) {
  window.customElements.define("mega-menu-promo-carousel", MegaMenuPromoCarousel);
}
if (!window.customElements.get("navigation-drawer")) {
  window.customElements.define("navigation-drawer", NavigationDrawer);
}

// js/sections/feature-chart.js
import { animate as motionAnimate3, scroll } from "vendor";
var FeatureChart = class extends HTMLElement {
  connectedCallback() {
    this.viewButtonElement = this.querySelector('[data-action="toggle-rows"]');
    this.featureChartTable = this.querySelector(".feature-chart__table");
    this.featureChartRows = Array.from(this.featureChartTable.childNodes);
    this.featureProductRow = this.querySelector(".feature-chart__table-row--product");
    this.featureChartSticky = this.querySelector(".feature-chart__table-row--sticky");
    if (this.viewButtonElement) {
      this.viewButtonElement.addEventListener("click", this._toggleRows.bind(this));
    }
    if (this.featureChartSticky) {
      this.featureChartSticky.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]').forEach((element) => {
        element.setAttribute("tabindex", "-1");
      });
      this.featureChartSticky.style.width = `${this.featureChartTable.scrollWidth}px`;
      this.featureChartTable.addEventListener("scroll", (event) => {
        this.featureChartSticky.style.marginLeft = -1 * event.target.scrollLeft + "px";
      });
      new ResizeObserver((entries) => {
        this.featureChartSticky.style.width = `${entries[0].contentRect.width}px`;
      }).observe(this.featureChartTable);
      const offset = getComputedStyle(this).scrollPaddingTop;
      scroll(({ y }) => {
        if (y.current >= y.targetOffset + this.featureProductRow.clientHeight / 2 && y.progress < 0.85) {
          this.featureChartSticky.classList.add("is-visible");
        } else {
          this.featureChartSticky.classList.remove("is-visible");
        }
      }, {
        target: this.featureChartTable,
        offset: [`${offset} start`, `end ${offset}`]
      });
    }
  }
  _toggleRows() {
    if (this.classList.contains("is-expanded")) {
      this._hideRows();
    } else {
      this._showRows();
    }
  }
  async _showRows() {
    const fromHeight = this.featureChartTable.clientHeight;
    this.featureChartRows.forEach((row) => {
      row.hidden = false;
    });
    this.viewButtonElement.querySelector(".feature-chart__toggle-text").innerText = this.viewButtonElement.getAttribute("data-view-less");
    this.classList.add("is-expanded");
    await motionAnimate3(this.featureChartTable, { height: [`${fromHeight}px`, `${this.featureChartTable.clientHeight}px`] }).finished;
    this.featureChartTable.style.height = "auto";
  }
  async _hideRows() {
    let fromHeight = this.featureChartTable.clientHeight, toHeight = 0, maxRows = parseInt(this.getAttribute("max-rows"));
    this.featureChartRows.slice(0, maxRows).forEach((row) => {
      toHeight += row.clientHeight;
    });
    this.viewButtonElement.querySelector(".feature-chart__toggle-text").innerText = this.viewButtonElement.getAttribute("data-view-more");
    this.classList.remove("is-expanded");
    await motionAnimate3(this.featureChartTable, { height: [`${fromHeight}px`, `${toHeight}px`] }).finished;
    this.featureChartRows.slice(maxRows).forEach((row) => row.hidden = true);
    this.featureChartTable.style.height = "auto";
  }
};
if (!window.customElements.get("feature-chart")) {
  window.customElements.define("feature-chart", FeatureChart);
}

// js/sections/image-banner.js
import { scroll as scroll2, timeline as timeline10, animate as animate12, inView as inView7 } from "vendor";
var ImageBanner = class extends HTMLElement {
  constructor() {
    super();
    this._codexMotionStateListener = null;
  }
  connectedCallback() {
    this._codexMotionStateListener = this._handleCodexMotionStateChange.bind(this);
    document.addEventListener("codex:motion-state-change", this._codexMotionStateListener);
    if (this._motionPaused()) {
      this._renderStaticState();
      return;
    }
    if (this.hasAttribute("disable-on-mobile") && window.matchMedia("(max-width: 699px)").matches) {
      this.style.opacity = "1";
      this.querySelectorAll('[reveal-on-scroll="true"]').forEach((element) => element.style.opacity = "1");
      return;
    }
    if (this.parallax && window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      this._setupParallax();
    }
    inView7(this, async () => {
      await imageLoaded(Array.from(this.querySelectorAll(":scope > :is(img, video, iframe, svg, video-media)")));
      const headings = Array.from(this.querySelectorAll('[reveal-on-scroll="true"]'));
      timeline10([
        [this, { opacity: [0, 1] }, { duration: 0.25 }],
        ...headings.map((heading) => [...getHeadingKeyframe(heading)])
      ]);
    });
  }
  disconnectedCallback() {
    if (this._codexMotionStateListener) {
      document.removeEventListener("codex:motion-state-change", this._codexMotionStateListener);
      this._codexMotionStateListener = null;
    }
  }
  _motionPaused() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches || document.documentElement.getAttribute("data-codex-motion-paused") === "true" || window.__OOLMotionPaused === true;
  }
  _handleCodexMotionStateChange() {
    if (this._motionPaused()) {
      this._renderStaticState();
    }
  }
  _renderStaticState() {
    this.style.opacity = "1";
    this.style.visibility = "visible";
    this.style.transform = "none";
    Array.from(this.querySelectorAll(":scope > :is(img, video, iframe, svg, video-media, picture)")).forEach((media) => {
      media.style.opacity = "1";
      media.style.visibility = "visible";
      media.style.transform = "none";
    });
    this.querySelectorAll('[reveal-on-scroll="true"]').forEach((element) => {
      element.style.opacity = "1";
      element.style.visibility = "visible";
      element.style.transform = "none";
    });
  }
  get parallax() {
    return this.hasAttribute("parallax") ? parseFloat(this.getAttribute("parallax")) : false;
  }
  _setupParallax() {
    const media = Array.from(this.querySelectorAll(":scope > :is(img, video, iframe, svg, video-media, picture)")), [scale, translate] = [1 + this.parallax, this.parallax * 100 / (1 + this.parallax)];
    scroll2(
      animate12(media, { transform: [`scale(${scale}) translateY(-${translate}%)`, `scale(${scale}) translateY(0)`] }, { easing: "linear" }),
      {
        target: this,
        offset: ["start end", "end start"]
      }
    );
  }
};
if (!window.customElements.get("image-banner")) {
  window.customElements.define("image-banner", ImageBanner);
}

// js/sections/image-link-blocks.js
var ImageLinkBlocks = class extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.children);
    new ScrollArea(this);
    this.addEventListener("control:prev", this._prev);
    this.addEventListener("control:next", this._next);
  }
  _prev() {
    this.scrollBy({ left: (document.dir === "rtl" ? 1 : -1) * this.items[0].clientWidth, behavior: "smooth" });
  }
  _next() {
    this.scrollBy({ left: (document.dir === "rtl" ? -1 : 1) * this.items[0].clientWidth, behavior: "smooth" });
  }
};
if (!window.customElements.get("image-link-blocks")) {
  window.customElements.define("image-link-blocks", ImageLinkBlocks);
}

// js/sections/images-with-text-scrolling.js
import { animate as animate13, timeline as timeline11, inView as inView8 } from "vendor";
var ImagesWithTextScrolling = class extends HTMLElement {
  connectedCallback() {
    inView8(this, this._reveal.bind(this));
    if (this.hasAttribute("scrolling-experience")) {
      this._imageToTransitionItems = Array.from(this.querySelectorAll(".images-scrolling-desktop__media-wrapper > :not(:first-child)"));
      this._mainImage = this.querySelector(".images-scrolling-desktop__media-wrapper > :first-child");
      this._contentItems = Array.from(this.querySelectorAll(".images-scrolling__content"));
      if (this._imageToTransitionItems.length > 0) {
        window.addEventListener("scroll", throttle(this._onScroll.bind(this)));
      }
    }
  }
  _reveal() {
    Array.from(this.querySelectorAll('[reveal-on-scroll="true"]')).forEach((heading) => {
      animate13(...getHeadingKeyframe(heading));
    });
  }
  _onScroll() {
    const imageRect = this._mainImage.getBoundingClientRect(), imageBottom = imageRect.bottom, imageEffect = this.getAttribute("scrolling-experience");
    for (const [index, contentItem] of this._contentItems.entries()) {
      const contentItemRect = contentItem.getBoundingClientRect(), image = this._imageToTransitionItems[index - 1], content = this._contentItems[index];
      if (contentItemRect.top < imageBottom - imageRect.height * 0.1 && contentItemRect.bottom > imageBottom) {
        if (image && !image.classList.contains("is-visible")) {
          image.classList.add("is-visible");
          if (imageEffect === "fade") {
            timeline11([
              [image, { opacity: [null, 1] }, { duration: 0.2 }],
              [content, { opacity: [null, 1] }, { duration: 0.45, at: "<" }]
            ]);
          } else {
            timeline11([
              [image, { opacity: [null, 1], clipPath: ["inset(100% 0 0 0)", "inset(0 0 0 0)"] }, { duration: 0.35, easing: [0.99, 0.01, 0.5, 0.94], opacity: { duration: 0 } }],
              [content, { opacity: [null, 1] }, { duration: 0.45, at: "<" }]
            ]);
          }
        }
        break;
      }
      if (contentItemRect.top > imageBottom - imageRect.height * 0.1) {
        if (image && image.classList.contains("is-visible")) {
          image.classList.remove("is-visible");
          if (imageEffect === "fade") {
            timeline11([
              [image, { opacity: [null, 0] }, { duration: 0.2 }],
              [content, { opacity: [null, 0] }, { duration: 0.2, at: "<" }]
            ]);
          } else {
            timeline11([
              [image, { opacity: [null, 1], clipPath: ["inset(0 0 0 0)", "inset(100% 0 0 0)"] }, { duration: 0.35, easing: [0.99, 0.01, 0.5, 0.94], opacity: { duration: 0 } }],
              [content, { opacity: [null, 0] }, { duration: 0.2, at: "<" }]
            ]);
          }
        }
        break;
      }
    }
  }
};
if (!window.customElements.get("images-with-text-scrolling")) {
  window.customElements.define("images-with-text-scrolling", ImagesWithTextScrolling);
}

// js/sections/impact-text.js
import { animate as animate14, inView as inView9 } from "vendor";
var ImpactText = class extends HTMLElement {
  constructor() {
    super();
    this._codexMotionStateListener = null;
    this._codexRevealAnimation = null;
    this._codexCountUpAnimation = null;
  }
  connectedCallback() {
    this._codexMotionStateListener = this._handleCodexMotionStateChange.bind(this);
    document.addEventListener("codex:motion-state-change", this._codexMotionStateListener);
    if (this._motionPaused()) {
      this._renderStaticState();
      return;
    }
    if (!window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      return;
    }
    inView9(this, ({ target }) => this._onBecameVisible(target), { margin: "-100px" });
  }
  disconnectedCallback() {
    if (this._codexMotionStateListener) {
      document.removeEventListener("codex:motion-state-change", this._codexMotionStateListener);
      this._codexMotionStateListener = null;
    }
  }
  _motionPaused() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches || document.documentElement.getAttribute("data-codex-motion-paused") === "true" || window.__OOLMotionPaused === true;
  }
  _handleCodexMotionStateChange() {
    if (this._motionPaused()) {
      this._stopAnimationsAndReveal();
    }
  }
  _stopAnimationsAndReveal() {
    this._codexRevealAnimation?.finish?.();
    this._codexCountUpAnimation?.finish?.();
    this._codexRevealAnimation = null;
    this._codexCountUpAnimation = null;
    this._renderStaticState();
  }
  _renderStaticState(target = this) {
    target.style.opacity = "1";
    target.style.transform = "none";
    target.style.visibility = "visible";
    if (!this.hasAttribute("count-up")) {
      return;
    }
    const itemToSearch = this.childElementCount === 0 ? this : this.firstElementChild, matches = itemToSearch.textContent.trim().match(/\d+(?:[,. ]\d+)*/);
    if (!matches) {
      return;
    }
    itemToSearch.innerHTML = itemToSearch.textContent.replace(/\d+(?:[,. ]\d+)*/, `<span>${matches[0]}</span>`);
    const numberSpan = itemToSearch.querySelector("span");
    numberSpan.style.textAlign = matches[0] === matches["input"] ? null : "end";
    if (!itemToSearch.classList.contains("text-gradient")) {
      numberSpan.style.display = "inline-block";
      numberSpan.style.minWidth = `${numberSpan.clientWidth}px`;
    }
    numberSpan.textContent = matches[0];
    numberSpan.style.minWidth = null;
  }
  async _onBecameVisible(target) {
    if (this._motionPaused()) {
      this._renderStaticState(target);
      return;
    }
    this._codexRevealAnimation = animate14(target, { opacity: 1, transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.3 });
    if (this.hasAttribute("count-up")) {
      const itemToSearch = this.childElementCount === 0 ? this : this.firstElementChild, matches = itemToSearch.textContent.trim().match(/\d+(?:[,. ]\d+)*/);
      if (!matches) {
        return;
      }
      itemToSearch.innerHTML = itemToSearch.textContent.replace(/\d+(?:[,. ]\d+)*/, `<span>${matches[0]}</span>`);
      const numberSpan = itemToSearch.querySelector("span");
      numberSpan.style.textAlign = matches[0] === matches["input"] ? null : "end";
      if (!itemToSearch.classList.contains("text-gradient")) {
        numberSpan.style.display = "inline-block";
        numberSpan.style.minWidth = `${numberSpan.clientWidth}px`;
      }
      const toReplace = matches[0].replace(/[,\. ]+/, ""), charactersMatches = [...matches[0].matchAll(/[,\. ]+/g)];
      const countUpAnimation = animate14((progress) => {
        let formattedString = Math.round(progress * parseInt(toReplace)).toString();
        charactersMatches.forEach((character) => {
          if (formattedString.length >= matches[0].length - character.index) {
            formattedString = formattedString.slice(0, character.index) + character[0] + formattedString.slice(character.index);
          }
        });
        numberSpan.textContent = progress === 1 ? matches[0] : formattedString;
      }, { duration: parseFloat(this.getAttribute("count-up")), easing: [0.16, 1, 0.3, 1] });
      this._codexCountUpAnimation = countUpAnimation;
      await countUpAnimation.finished;
      this._codexCountUpAnimation = null;
      numberSpan.style.minWidth = null;
    }
    this._codexRevealAnimation = null;
  }
};
if (!window.customElements.get("impact-text")) {
  window.customElements.define("impact-text", ImpactText);
}

// js/sections/main-search.js
var SearchResultPanel = class extends HTMLElement {
  async connectedCallback() {
    if (!this.hasAttribute("load-from-url")) {
      return;
    }
    const textResponse = await (await fetch(encodeURI(`${this.getAttribute("load-from-url")}&section_id=${extractSectionId(this)}`))).text();
    const temporaryContent = new DOMParser().parseFromString(textResponse, "text/html");
    const searchResultsPanel = temporaryContent.querySelector(`#${this.getAttribute("id")}`);
    if (searchResultsPanel) {
      this.replaceChildren(...searchResultsPanel.children);
    } else {
      document.querySelector(`[controlled-panel="${this.id}"]`).remove();
      this.remove();
    }
  }
};
if (!window.customElements.get("search-result-panel")) {
  window.customElements.define("search-result-panel", SearchResultPanel);
}

// js/sections/media-grid.js
import { timeline as timeline12, inView as inView10 } from "vendor";
var MediaGrid = class extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.children);
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      inView10(this, this._onBecameVisible.bind(this));
    }
  }
  _onBecameVisible() {
    const contentElements = this.querySelectorAll(".content-over-media > :not(img, video, iframe, svg, video-media, native-video, external-video)");
    timeline12([
      [this.items, { opacity: 1, transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.3 }],
      [contentElements, { opacity: [0, 1] }, { duration: 0.2, at: "+0.1" }]
    ]);
  }
};
if (!window.customElements.get("media-grid")) {
  window.customElements.define("media-grid", MediaGrid);
}

// js/sections/media-with-text.js
import { animate as animate15, timeline as timeline13, inView as inView11 } from "vendor";
var reduceMotion = window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
var MediaWithText = class extends HTMLElement {
  connectedCallback() {
    this._codexMotionStateListener = this._handleCodexMotionStateChange?.bind(this) || null;
    document.addEventListener("codex:motion-state-change", this._codexMotionStateListener);
    if (this._motionPaused()) {
      this._renderStaticState();
      return;
    }
    if (reduceMotion) {
      Array.from(this.querySelectorAll(".media-with-text__item")).forEach((item) => {
        inView11(item, (observer) => this.reveal(observer.target));
      });
    }
  }
  disconnectedCallback() {
    if (this._codexMotionStateListener) {
      document.removeEventListener("codex:motion-state-change", this._codexMotionStateListener);
      this._codexMotionStateListener = null;
    }
  }
  _motionPaused() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches || document.documentElement.getAttribute("data-codex-motion-paused") === "true" || window.__OOLMotionPaused === true;
  }
  _handleCodexMotionStateChange() {
    if (this._motionPaused()) {
      this._renderStaticState();
    }
  }
  _renderStaticState() {
    this.style.opacity = "1";
    this.style.visibility = "visible";
    this.style.transform = "none";
    Array.from(this.querySelectorAll(".media-with-text__item")).forEach((item) => {
      item.style.opacity = "1";
      item.style.visibility = "visible";
      item.style.transform = "none";
      const media = item.querySelector(".media-with-text__media");
      if (media) {
        media.style.opacity = "1";
        media.style.visibility = "visible";
        media.style.transform = "none";
        Array.from(media.querySelectorAll("img, video-media")).forEach((element) => {
          element.style.opacity = "1";
          element.style.visibility = "visible";
          element.style.transform = "none";
        });
      }
      const prose = item.querySelector(".media-with-text__content > .prose");
      if (prose) {
        prose.style.opacity = "1";
        prose.style.visibility = "visible";
        prose.style.transform = "none";
      }
    });
  }
  reveal(item) {
    const media = item.querySelector(".media-with-text__media");
    if (media.hasAttribute("data-hide-image-mobile") && window.matchMedia("(max-width: 699px)").matches) {
      media.style.opacity = "1";
      animate15(item.querySelector(".media-with-text__content > .prose"), { opacity: [0, 1] }, { duration: 0.2 });
      return;
    }
    imageLoaded(media.querySelector("img")).then(() => {
      timeline13([
        [media, { opacity: [0, 1] }, { duration: 0.3 }],
        [media.querySelector("img, video-media"), { transform: ["scale(1.05)", "scale(1)"] }, { duration: 0.3, at: "<" }]
      ]);
    });
    animate15(item.querySelector(".media-with-text__content > .prose"), { opacity: [0, 1] }, { duration: 0.2, delay: 0.3 });
  }
};
if (!window.customElements.get("media-with-text")) {
  window.customElements.define("media-with-text", MediaWithText);
}

// js/sections/multiple-images-with-text.js
import { timeline as timeline14, animate as animate16, stagger as stagger6, inView as inView12 } from "vendor";
var MultipleImagesWithText = class extends HTMLElement {
  constructor() {
    super();
    this._imageCarousel = this.querySelector("multiple-images-with-text-image-list");
    this._contentCarousel = this.querySelector("multiple-images-with-text-content-list");
    this.addEventListener("control:prev", () => {
      this._imageCarousel.previous();
      this._contentCarousel.previous();
    });
    this.addEventListener("control:next", () => {
      this._imageCarousel.next();
      this._contentCarousel.next();
    });
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => {
        if (event.target.hasAttribute("image-id")) {
          this._imageCarousel.select(this._imageCarousel.items.findIndex((item) => item.id === event.target.getAttribute("image-id")));
        }
      });
    }
  }
};
var MultipleImagesWithTextImageList = class extends EffectCarousel {
  constructor() {
    super();
    inView12(this, this._reveal.bind(this));
  }
  async _reveal() {
    await imageLoaded(this.querySelectorAll("img"));
    if (this.getAttribute("layout") === "stacked") {
      timeline14([
        [this.lastElementChild, { transform: "rotate(0deg)" }],
        [this.lastElementChild?.previousElementSibling, { transform: "rotate(2deg)" }, { at: "<" }],
        [this.lastElementChild?.previousElementSibling?.previousElementSibling, { transform: "rotate(-2deg)" }, { at: "<" }]
      ], {
        defaultOptions: { duration: 0.15, easing: [0.26, 0.02, 0.27, 0.97] }
      });
    } else if (this.getAttribute("layout") === "collage") {
      timeline14([
        [this.children, { opacity: 1, transform: ["translateY(15px)", "translateY(0)"] }, { duration: 0.3, delay: stagger6(0.1) }]
      ]);
    } else {
      timeline14([
        [this.children, { opacity: 1, transform: "rotate(var(--image-rotation, 0deg))" }, { duration: 0.3, delay: stagger6(0.1) }]
      ]);
    }
  }
  _transitionTo(fromSlide, toSlide, { direction, animate: animate19 }) {
    if (this.getAttribute("layout") !== "stacked") {
      return;
    }
    const transitionSpeed = 0.2;
    if (direction === "next") {
      const fromSlideTransform = getComputedStyle(fromSlide).getPropertyValue("transform");
      return timeline14([
        [fromSlide, { opacity: 0, transform: "rotate(5deg) translate(40px, 10px)" }, { duration: transitionSpeed }],
        [toSlide, { zIndex: 1 }, { duration: transitionSpeed, zIndex: { easing: "step-start" } }],
        [fromSlide, { opacity: 1, transform: fromSlideTransform, zIndex: -1 }, { duration: transitionSpeed, at: "<", zIndex: { easing: "step-start" } }],
        [toSlide.previousElementSibling, { zIndex: 0 }, { at: "<", easing: "step-start" }]
      ], { defaultOptions: { easing: [0.26, 0.02, 0.27, 0.97] } }).finished;
    } else {
      const toSlideTransform = getComputedStyle(toSlide).getPropertyValue("transform");
      return timeline14([
        [toSlide, { opacity: 0, transform: "rotate(-5deg) translate(-40px, -10px)" }, { duration: transitionSpeed }],
        this.items.length >= 3 && [fromSlide.previousElementSibling || this.lastElementChild, { zIndex: -1 }, { easing: "step-start" }],
        [toSlide, { opacity: 1, transform: toSlideTransform, zIndex: 1 }, { duration: transitionSpeed, at: this.items.length >= 3 ? "<" : "+0", zIndex: { easing: "step-start" } }],
        [fromSlide, { zIndex: 0 }, { duration: transitionSpeed, at: "<", zIndex: { easing: "step-start" } }]
      ].filter(Boolean), { defaultOptions: { easing: [0.26, 0.02, 0.27, 0.97] } }).finished;
    }
  }
};
var MultipleImagesWithTextContentList = class extends EffectCarousel {
  constructor() {
    super();
    inView12(this, this._reveal.bind(this));
  }
  _reveal() {
    animate16(...getHeadingKeyframe(this.querySelector('[reveal-on-scroll="true"]')));
  }
  _transitionTo(fromSlide, toSlide, { direction = "next", animate: animate19 = true } = {}) {
    fromSlide.classList.remove("is-selected");
    toSlide.classList.add("is-selected");
    return timeline14([
      [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"] }],
      [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"] }],
      [...getHeadingKeyframe(toSlide.querySelector('[reveal-on-scroll="true"]'), { at: "<" })]
    ], { duration: animate19 ? parseFloat(this.getAttribute("fade-speed") || 0.5) : 0 }).finished;
  }
};
if (!window.customElements.get("multiple-images-with-text")) {
  window.customElements.define("multiple-images-with-text", MultipleImagesWithText);
}
if (!window.customElements.get("multiple-images-with-text-image-list")) {
  window.customElements.define("multiple-images-with-text-image-list", MultipleImagesWithTextImageList);
}
if (!window.customElements.get("multiple-images-with-text-content-list")) {
  window.customElements.define("multiple-images-with-text-content-list", MultipleImagesWithTextContentList);
}

// js/sections/newsletter-popup.js
var NewsletterPopup = class extends Drawer {
  connectedCallback() {
    super.connectedCallback();
    if (this.shouldAppearAutomatically) {
      setTimeout(() => this.show(), this.apparitionDelay);
    }
  }
  get initialFocus() {
    return false;
  }
  get shouldAppendToBody() {
    return false;
  }
  get apparitionDelay() {
    return parseInt(this.getAttribute("apparition-delay") || 0) * 1e3;
  }
  get shouldAppearAutomatically() {
    return !(localStorage.getItem("theme:popup-filled") === "true" || this.hasAttribute("only-once") && localStorage.getItem("theme:popup-appeared") === "true");
  }
  _setInitialPosition() {
    this.style.top = null;
    this.style.bottom = "0px";
    this.style.left = document.dir === "ltr" ? null : "0px";
    this.style.right = document.dir === "rtl" ? "auto" : "0px";
  }
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === "open" && this.open) {
      localStorage.setItem("theme:popup-appeared", "true");
    }
  }
};
if (!window.customElements.get("newsletter-popup")) {
  window.customElements.define("newsletter-popup", NewsletterPopup);
}

// js/sections/press.js
import { timeline as timeline15, animate as animate17, inView as inView13 } from "vendor";
var reduceMotion2 = window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
var PressCarousel = class extends EffectCarousel {
  constructor() {
    super();
    this._codexMotionStateListener = null;
  }
  connectedCallback() {
    super.connectedCallback();
    this._codexMotionStateListener = this._handleCodexMotionStateChange.bind(this);
    document.addEventListener("codex:motion-state-change", this._codexMotionStateListener);
    if (this._motionPaused()) {
      this._renderStaticState();
      return;
    }
    if (reduceMotion2) {
      inView13(this, this._reveal.bind(this));
    }
  }
  disconnectedCallback() {
    if (this._codexMotionStateListener) {
      document.removeEventListener("codex:motion-state-change", this._codexMotionStateListener);
      this._codexMotionStateListener = null;
    }
    super.disconnectedCallback?.();
  }
  _motionPaused() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches || document.documentElement.getAttribute("data-codex-motion-paused") === "true" || window.__OOLMotionPaused === true;
  }
  _handleCodexMotionStateChange() {
    if (this._motionPaused()) {
      this._renderStaticState();
    }
  }
  _reveal() {
    animate17(this.selectedSlide.querySelector(".blockquote"), { opacity: 1, transform: ["translateY(15px)", "translateY(0)"] }, { duration: 0.2 });
  }
  _renderStaticState() {
    const selectedSlide = this.selectedSlide;
    if (!(selectedSlide instanceof HTMLElement)) {
      return;
    }
    selectedSlide.style.opacity = "1";
    selectedSlide.style.visibility = "visible";
    selectedSlide.style.transform = "none";
    selectedSlide.querySelectorAll(".press__logo, .press__author, .blockquote").forEach((element) => {
      element.style.opacity = "1";
      element.style.visibility = "visible";
      element.style.transform = "none";
    });
  }
  async _transitionTo(fromSlide, toSlide, options = {}) {
    await timeline15([
      [fromSlide.querySelectorAll(".press__logo, .press__author"), { opacity: [null, 0] }, { duration: 0.2 }],
      [fromSlide.querySelector(".blockquote"), { opacity: [null, 0], ...reduceMotion2 && { transform: [null, "translateY(-10px)"] } }, { duration: 0.2, at: "<" }]
    ]).finished;
    fromSlide.classList.remove("is-selected");
    toSlide.classList.add("is-selected");
    await timeline15([
      [toSlide.querySelectorAll(".press__logo, .press__author"), { opacity: [0, 1] }, { duration: 0.2 }],
      [toSlide.querySelector(".blockquote"), { opacity: [0, 1], ...reduceMotion2 && { transform: ["translateY(10px)", "translateY(0px)"] } }, { duration: 0.2, at: "<" }]
    ]).finished;
  }
};
if (!window.customElements.get("press-carousel")) {
  window.customElements.define("press-carousel", PressCarousel);
}

// js/sections/product-recommendations.js
var ProductRecommendations = class extends HTMLElement {
  constructor() {
    super();
    this._isLoaded = false;
  }
  connectedCallback() {
    this._loadRecommendations();
  }
  async _loadRecommendations() {
    if (this._isLoaded) {
      return;
    }
    this._isLoaded = true;
    const section = this.closest(".shopify-section"), intent = this.getAttribute("intent") || "related", url = `${Shopify.routes.root}recommendations/products?product_id=${this.getAttribute("product")}&limit=${this.getAttribute("limit") || 4}&section_id=${section.id.replace("shopify-section-", "")}&intent=${intent}`, response = await fetch(url, { priority: "low" });
    const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), productRecommendationsElement = tempDiv.querySelector("product-recommendations");
    if (productRecommendationsElement.childElementCount > 0) {
      this.replaceChildren(...document.importNode(productRecommendationsElement, true).childNodes);
      this.setAttribute("aria-busy", "false");
    } else {
      if (intent === "related") {
        section.remove();
      } else {
        this.remove();
      }
    }
  }
};
if (!window.customElements.get("product-recommendations")) {
  window.customElements.define("product-recommendations", ProductRecommendations);
}

// js/sections/recently-viewed-products.js
var _isLoaded, _RecentlyViewedProducts_instances, searchQueryString_get, loadProducts_fn;
var RecentlyViewedProducts = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _RecentlyViewedProducts_instances);
    __privateAdd(this, _isLoaded, false);
  }
  connectedCallback() {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(__privateMethod(this, _RecentlyViewedProducts_instances, loadProducts_fn).bind(this), { timeout: 1500 });
    } else {
      __privateMethod(this, _RecentlyViewedProducts_instances, loadProducts_fn).call(this);
    }
  }
};
_isLoaded = new WeakMap();
_RecentlyViewedProducts_instances = new WeakSet();
searchQueryString_get = function() {
  const items = new Set(JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]"));
  if (this.hasAttribute("exclude-id")) {
    items.delete(parseInt(this.getAttribute("exclude-id")));
  }
  return Array.from(items.values(), (item) => `id:${item}`).slice(0, parseInt(this.getAttribute("products-count"))).join(" OR ");
};
loadProducts_fn = async function() {
  if (__privateGet(this, _isLoaded)) {
    return;
  }
  __privateSet(this, _isLoaded, true);
  const section = this.closest(".shopify-section"), url = `${Shopify.routes.root}search?type=product&q=${__privateGet(this, _RecentlyViewedProducts_instances, searchQueryString_get)}&section_id=${extractSectionId(section)}`, response = await fetch(url, { priority: "low" });
  const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), recentlyViewedProductsElement = tempDiv.querySelector("recently-viewed-products");
  if (recentlyViewedProductsElement.childElementCount > 0) {
    this.replaceChildren(...document.importNode(recentlyViewedProductsElement, true).childNodes);
  } else {
    section.remove();
  }
};
if (!window.customElements.get("recently-viewed-products")) {
  window.customElements.define("recently-viewed-products", RecentlyViewedProducts);
}

// js/sections/revealed-image-on-scroll.js
import { scroll as scroll3, timeline as timeline16, ScrollOffset, inView as inView14 } from "vendor";
var RevealedImage = class extends HTMLElement {
  connectedCallback() {
    const scrollTracker = this.querySelector(".revealed-image__scroll-tracker"), scroller = this.querySelector(".revealed-image__scroller");
    scrollTracker.style.height = `${scroller.clientHeight}px`;
    new ResizeObserver((entries) => scrollTracker.style.height = `${entries[0].contentRect.height}px`).observe(scroller);
    scroll3(timeline16([
      [this.querySelectorAll(".revealed-image__image-clipper, .revealed-image__content--inside"), { clipPath: ["inset(37% 37% 41% 37%)", "inset(calc(var(--sticky-area-height) / 2) 0)"] }, { easing: "ease-in" }],
      [this.querySelectorAll("img, svg"), { transform: ["translate(-10%, -1.5%) scale(1.4)", "translate(0, 0) scale(1)"] }, { easing: "ease-in", at: "<" }],
      [this.querySelectorAll(".revealed-image__content"), { opacity: [0, 1, 1] }, { offset: [0, 0.25, 1], at: "<" }]
    ]), {
      target: scrollTracker,
      offset: ScrollOffset.All
    });
    inView14(this, () => {
      this.style.visibility = "visible";
      return () => {
        this.style.visibility = "hidden";
      };
    }, { margin: "200px" });
  }
};
if (!window.customElements.get("revealed-image")) {
  window.customElements.define("revealed-image", RevealedImage);
}

// js/sections/scrolling-text.js
import { animate as animate18, scroll as scroll4 } from "vendor";
var ScrollingText = class extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      if (window.ScrollTimeline) {
        const timeline18 = new ViewTimeline({
          subject: this,
          axis: "block"
        });
        this.animate(
          {
            transform: ["translateX(calc(var(--transform-logical-flip) * 50vw))", "translateX(calc(var(--transform-logical-flip) * 50vw - 10%))"]
          },
          {
            timeline: timeline18,
            rangeStart: "cover 0%",
            rangeEnd: "cover 100%"
          }
        );
      } else {
        scroll4(
          animate18(
            this,
            {
              transform: ["translateX(calc(var(--transform-logical-flip) * 50vw))", "translateX(calc(var(--transform-logical-flip) * 50vw - 10%))"]
            }
          ),
          {
            target: this,
            offset: ["start end", "end start"]
          }
        );
      }
    }
  }
};
if (!window.customElements.get("scrolling-text")) {
  window.customElements.define("scrolling-text", ScrollingText);
}

// js/sections/shop-the-look.js
var ShopTheLookDots = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    Array.from(this.children).forEach((dots) => {
      document.getElementById(dots.getAttribute("aria-controls")).addEventListener("carousel:change", (event) => this._onDotClicked(event), { signal: this._abortController.signal });
    });
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  _onDotClicked(event) {
    Array.from(this.querySelectorAll(`button:nth-child(${event.detail.index + 1})`)).forEach((button) => button.click());
  }
};
if (!window.customElements.get("shop-the-look-dots")) {
  window.customElements.define("shop-the-look-dots", ShopTheLookDots);
}

// js/sections/slideshow.js
import { animate as motionAnimate4, timeline as timeline17, inView as inView15 } from "vendor";
var Slideshow = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("carousel:select", this._onSlideSelected);
  }
  async _onSlideSelected(event) {
    const slideStyles = getComputedStyle(event.detail.slide);
    this.style.setProperty("--slideshow-controls-background", slideStyles.getPropertyValue("--slideshow-slide-controls-background"));
    this.style.setProperty("--slideshow-controls-color", slideStyles.getPropertyValue("--slideshow-slide-controls-color"));
    if (!this.classList.contains("slideshow--boxed")) {
      return;
    }
    const backgroundElement = this.querySelector(".slideshow__slide-background");
    const background = slideStyles.getPropertyValue("--slideshow-slide-background");
    backgroundElement.style.background = background;
    await motionAnimate4(backgroundElement, { opacity: [0, 1] }, { duration: 0.2 }).finished;
    this.style.setProperty("--slideshow-background", background);
    motionAnimate4(backgroundElement, { opacity: 0 }, { duration: 0 });
  }
};
var SlideshowCarousel = class extends EffectCarousel {
  _pendingAnimationControls = [];
  constructor() {
    super();
    this.addEventListener("carousel:select", this._onSlideSelected);
    if (this.hasAttribute("disable-on-mobile") && window.matchMedia("(max-width: 699px)").matches) {
      this.style.opacity = "1";
    } else if (this.hasAttribute("reveal-on-scroll")) {
      inView15(this, this._reveal.bind(this));
    }
    if (this.querySelector(".slideshow__cursor")) {
      this.addEventListener("tap", this._onSlideshowTap);
    }
  }
  connectedCallback() {
    super.connectedCallback();
    if (this._player && this.hasAttribute("autoplay")) {
      this._player.addEventListener("player:start", (event) => {
        const cursorRing = this.querySelector(".slideshow__cursor-ring circle");
        if (cursorRing) {
          const cursorRingAnimationControl = motionAnimate4(cursorRing, { strokeDasharray: [`0px, ${cursorRing.getTotalLength()}px`, `${cursorRing.getTotalLength()}px, ${cursorRing.getTotalLength()}px`] }, {
            duration: event.detail.duration,
            easing: "linear"
          });
          this._pendingAnimationControls.push(cursorRingAnimationControl);
        }
        const circles = Array.from(this.querySelectorAll(".numbered-dots__item"));
        circles.forEach((item) => {
          const circle = item.querySelector("circle:last-child");
          let circleAnimationControl = null;
          if (item.getAttribute("aria-current") === "true") {
            circleAnimationControl = motionAnimate4(circle, { strokeDasharray: [`0px, ${circle.getTotalLength()}px`, `${circle.getTotalLength()}px, ${circle.getTotalLength()}px`] }, { duration: event.detail.duration, easing: "linear" });
          } else {
            circleAnimationControl = motionAnimate4(circle, { strokeDasharray: `${circle.getTotalLength()}px, ${circle.getTotalLength()}px` }, { duration: 0, easing: "linear" });
          }
          this._pendingAnimationControls.push(circleAnimationControl);
        });
      });
      this._player.addEventListener("player:pause", () => {
        this._pendingAnimationControls.forEach((control) => control.pause());
      });
      this._player.addEventListener("player:resume", () => {
        this._pendingAnimationControls.forEach((control) => control.play());
      });
    }
  }
  get transitionType() {
    if (this.hasAttribute("disable-on-mobile") && window.matchMedia("(max-width: 699px)").matches) {
      return "fade";
    }
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "fade" : this.getAttribute("transition");
  }
  async _reveal() {
    const selectedSlide = this.selectedSlide;
    await imageLoaded(selectedSlide.querySelectorAll("img"));
    this.style.opacity = "1";
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || this.hasAttribute("disable-on-mobile") && window.matchMedia("(max-width: 699px)").matches) {
      return;
    }
    if (!this._reloaded) {
      timeline17([
        [selectedSlide, { zIndex: 1 }, { duration: 0 }],
        [selectedSlide.querySelectorAll("img, video-media"), { opacity: [0, 1], transform: ["scale(1.05)", "scale(1)"] }, { duration: 0.3 }],
        "content",
        [selectedSlide.querySelectorAll('[data-sequence="subheading"], .button'), { opacity: [0, 1] }, { duration: 0.3, at: "content" }],
        [...getHeadingKeyframe(selectedSlide.querySelector('[data-sequence="heading"]'), { duration: 0.3, at: "content" })],
        [selectedSlide.querySelector(".button"), { opacity: [0, 1] }, { duration: 0.3, at: "content" }],
        [this.querySelector(".slideshow__controls"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.3 }]
      ]);
    }
  }
  _onSlideshowTap(event) {
    if (event.target.matches("button, a[href], button :scope, a[href] :scope") || !window.matchMedia("screen and (pointer: fine)").matches) {
      return;
    }
    event.detail.originalEvent.clientX > window.innerWidth / 2 ? this.next() : this.previous();
  }
  /**
   * Perform a simple fade animation. For more complex animations, you should implement your own custom elements
   * that extends the EffectCarousel, and implement your own transition. You should make sure to return a promise
   * that resolves when the animation is finished
   */
  async _transitionTo(fromSlide, toSlide, { direction, animate: animate19 = true } = {}) {
    fromSlide.classList.remove("is-selected");
    toSlide.classList.add("is-selected");
    let timelineControls = null;
    switch (this.transitionType) {
      case "fade":
        timelineControls = this._fade(fromSlide, toSlide, { animate: animate19 });
        break;
      case "fade_with_text":
        timelineControls = this._fadeWithText(fromSlide, toSlide, { animate: animate19 });
        break;
    }
    if (!animate19) {
      timelineControls.finish();
    }
    return timelineControls.finished;
  }
  /**
   * Perform a simple fade transition
   */
  _fade(fromSlide, toSlide) {
    return timeline17([
      [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"], zIndex: 0 }, { duration: 0.3, easing: "ease-in", zIndex: { easing: "step-end" } }],
      [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"], zIndex: 1 }, { duration: 0.3, at: "<", easing: "ease-out", zIndex: { easing: "step-end" } }]
    ]);
  }
  /**
   * Perform a transition with fade images and image transition
   */
  async _fadeWithText(fromSlide, toSlide) {
    motionAnimate4(fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"], zIndex: 0 }, { duration: 0.3, easing: "ease-in", zIndex: { easing: "step-end" } });
    await imageLoaded(toSlide.querySelectorAll("img"));
    motionAnimate4(toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"], zIndex: 1 }, { duration: 0, zIndex: { easing: "step-end" } });
    return timeline17([
      [toSlide.querySelectorAll("img, video-media"), { opacity: [0, 1], transform: ["scale(1.05)", "scale(1)"] }, { duration: 0.3, easing: "ease-out" }],
      "content",
      [toSlide.querySelectorAll('[data-sequence="subheading"], .button'), { opacity: [0, 1] }, { duration: 0.3, at: "content" }],
      [...getHeadingKeyframe(toSlide.querySelector('[data-sequence="heading"]'), { duration: 0.3, at: "content" })]
    ]);
  }
  /**
   * Perform the synchronization with video
   */
  async _onSlideSelected(event) {
    Array.from(this.querySelectorAll("video-media")).forEach((video) => video.pause());
    if (event.detail.slide.getAttribute("data-slide-type") === "video") {
      const visibleVideo = Array.from(event.detail.slide.querySelectorAll("video-media")).filter((video) => video.offsetParent !== null).pop();
      visibleVideo.play({ restart: true });
      if (this.player) {
        const video = visibleVideo.querySelector("video");
        if (isNaN(video.duration)) {
          await new Promise((resolve) => {
            video.onloadedmetadata = () => resolve();
          });
        }
        this._player.setDuration(video.duration);
        this._player.resume({ restart: true });
      }
    } else {
      this._player?.setDuration(parseInt(this.getAttribute("autoplay")));
    }
  }
};
if (!window.customElements.get("x-slideshow")) {
  window.customElements.define("x-slideshow", Slideshow);
}
if (!window.customElements.get("slideshow-carousel")) {
  window.customElements.define("slideshow-carousel", SlideshowCarousel);
}

// js/theme.js
import { Delegate as Delegate7 } from "vendor";
(() => {
  const delegateDocument = new Delegate7(document.documentElement);
  delegateDocument.on("click", 'a[href="#product-grid"]', (event, target) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || target.pathname !== window.location.pathname || target.search !== window.location.search) {
      return;
    }
    const anchorElement = document.getElementById("product-grid");
    if (!anchorElement || !anchorElement.classList.contains("collection__results")) {
      return;
    }
    event.preventDefault();
    if (window.location.hash !== "#product-grid") {
      window.history.pushState(window.history.state, "", "#product-grid");
    }
    anchorElement.focus({ preventScroll: true });
    const motionPaused = window.__OOLMotionPaused || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    anchorElement.scrollIntoView({ block: "start", behavior: motionPaused ? "auto" : "smooth" });
  });
  if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
    delegateDocument.on("click", 'a[href*="#"]', (event, target) => {
      if (event.defaultPrevented || target.getAttribute("href") === "#product-grid" || target.pathname !== window.location.pathname || target.search !== window.location.search) {
        return;
      }
      const url = new URL(target.href);
      if (url.hash === "") {
        return;
      }
      const anchorElement = document.querySelector(url.hash);
      if (anchorElement) {
        event.preventDefault();
        anchorElement.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    });
  }
  const setScrollbarWidth = () => {
    const scrollbarWidth = window.innerWidth - document.body.clientWidth;
    if (scrollbarWidth > 0) {
      document.documentElement.style.setProperty("--scrollbar-width", `${scrollbarWidth}px`);
    }
  };
  setScrollbarWidth();
  window.addEventListener("resize", throttle(setScrollbarWidth));
})();
export {
  AccordionDisclosure,
  AccountLogin,
  AnimatedDetails,
  AnnouncementBar,
  BuyButtons,
  CartCount,
  CartDiscountBanner,
  CartDiscountField,
  CartDiscountRemoveButton,
  CartDrawer,
  CartNote,
  CartNotificationDrawer,
  CollectionList,
  ConfirmButton,
  CopyButton,
  CountrySelector,
  CustomButton,
  CustomCursor,
  DialogElement,
  Drawer,
  DropdownDisclosure,
  EffectCarousel,
  FacetApplyButton,
  FacetDialog,
  FacetDrawer,
  FacetFloatingFilter,
  FacetForm,
  FacetLink,
  FacetSortBy,
  FeatureChart,
  FreeShippingBar,
  GestureArea,
  GiftCardRecipient,
  HeightObserver,
  ImageBanner,
  ImageLinkBlocks,
  ImagesWithTextScrolling,
  ImpactText,
  LineItem,
  LineItemQuantity,
  Listbox,
  MarqueeText,
  MediaCarousel,
  MediaGrid,
  MediaWithText,
  MegaMenuDisclosure,
  MegaMenuPromoCarousel,
  ModelMedia,
  MultipleImagesWithText,
  MultipleImagesWithTextContentList,
  MultipleImagesWithTextImageList,
  NavigationDrawer,
  NewsletterPopup,
  NextButton,
  PageDots,
  PillLoader,
  Player,
  Popover,
  PredictiveSearch,
  PressCarousel,
  PrevButton,
  PriceRange,
  PrivacyBar,
  ProductCard,
  ProductForm,
  ProductGallery,
  ProductLoader,
  ProductQuickAdd,
  ProductRecommendations,
  ProductRerender,
  ProductZoomButton,
  ProgressBar,
  QuantitySelector,
  QuickBuyDrawer,
  RecentlyViewedProducts,
  ResizableTextarea,
  RevealItems,
  RevealedImage,
  SafeSticky,
  ScrollArea,
  ScrollCarousel,
  ScrollProgress,
  ScrollShadow,
  ScrollingText,
  SearchDrawer,
  SearchResultPanel,
  SectionHeader,
  ShareButton,
  ShippingEstimator,
  ShopTheLookDots,
  Slideshow,
  SlideshowCarousel,
  SplitCursor,
  SplitLines,
  StoreHeader,
  Tabs,
  VariantPicker,
  VideoMedia,
  announceError,
  announceStatus,
  cachedFetch,
  createMediaImg,
  debounce,
  deepQuerySelector,
  extractSectionId,
  fetchCart,
  generateSrcset,
  getHeadingKeyframe,
  imageLoaded,
  throttle,
  waitForEvent
};
